/* Trailblazer — quantity tier selector.
   Sets the product form's quantity input when a pack option is chosen. */
if (!customElements.get('tb-quantity-tiers')) {
  customElements.define(
    'tb-quantity-tiers',
    class TbQuantityTiers extends HTMLElement {
      connectedCallback() {
        this.inputs = Array.from(this.querySelectorAll('input[type="radio"]'));
        this.inputs.forEach((input) => {
          input.addEventListener('change', () => this.onChange(input));
        });
        const checked = this.inputs.find((i) => i.checked);
        if (checked) this.setQuantity(checked.value);
      }

      onChange(input) {
        this.querySelectorAll('.tb-tier').forEach((el) => el.classList.remove('tb-tier--active'));
        const label = input.closest('.tb-tier');
        if (label) label.classList.add('tb-tier--active');
        this.setQuantity(input.value);
      }

      setQuantity(value) {
        const id = this.dataset.quantityInput;
        const field =
          (id && document.getElementById(id)) ||
          (this.closest('.product') || document).querySelector('input[name="quantity"]');
        if (!field) return;
        field.value = value;
        field.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  );
}

/* Trailblazer — cart add-ons (upsell rows) */
if (!customElements.get('tb-cart-upsell')) {
  customElements.define(
    'tb-cart-upsell',
    class TbCartUpsell extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-tb-add]');
          if (btn) this.add(btn);
        });
      }

      async add(btn) {
        const id = btn.dataset.tbAdd;
        const original = btn.textContent;
        btn.disabled = true;
        btn.textContent = 'Adding…';
        try {
          const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
            body: JSON.stringify({
              items: [{ id: Number(id), quantity: 1 }],
              sections: 'cart-drawer,cart-icon-bubble,main-cart-items,main-cart-footer',
              sections_url: window.location.pathname,
            }),
          });
          const data = await res.json();
          const drawer = document.querySelector('cart-drawer');
          if (drawer && data.sections && typeof drawer.renderContents === 'function') {
            drawer.renderContents(data);
          } else {
            window.location.reload();
          }
        } catch (err) {
          btn.disabled = false;
          btn.textContent = original;
        }
      }
    }
  );
}

/* Trailblazer — sticky add-to-bag on mobile */
if (!customElements.get('tb-sticky-atc')) {
  customElements.define(
    'tb-sticky-atc',
    class TbStickyAtc extends HTMLElement {
      connectedCallback() {
        this.bar = this.querySelector('.tb-sticky');
        this.target = document.querySelector('.product-form__submit');
        if (!this.bar || !this.target) return;

        this.querySelector('[data-tb-sticky-add]')?.addEventListener('click', () => {
          this.target.click();
        });

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            ([entry]) => {
              const show = !entry.isIntersecting;
              this.bar.classList.toggle('tb-sticky--in', show);
              // Tells the page to reserve the bar's height, so nothing ends up
              // permanently trapped underneath it.
              document.body.classList.toggle('tb-sticky-on', show);
            },
            { rootMargin: '0px 0px -60px 0px' }
          );
          this.observer.observe(this.target);
        } else {
          this.bar.classList.add('tb-sticky--in');
        }

        // The price was printed once by Liquid and then never moved, so the bar
        // said $26.99 while the page said $68.82. Rather than recompute the
        // maths a second time and risk the two disagreeing, it mirrors whatever
        // the selected pack card is showing — one source, always in step.
        this.priceEl = this.querySelector('.tb-sticky__price');
        this.syncPrice = () => {
          if (!this.priceEl) return;
          const chosen = document.querySelector('.tb-tier--active .tb-tier__price')
            || document.querySelector('.price__container .price-item--sale')
            || document.querySelector('.price__container .price-item--regular');
          const text = chosen ? chosen.textContent.trim() : '';
          if (text) this.priceEl.textContent = text;
        };
        this.onChange = () => requestAnimationFrame(this.syncPrice);
        document.addEventListener('change', this.onChange);
        document.addEventListener('click', this.onChange);
        this.syncPrice();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        document.body.classList.remove('tb-sticky-on');
        document.removeEventListener('change', this.onChange);
        document.removeEventListener('click', this.onChange);
      }
    }
  );
}

/* Trailblazer — delivery estimate.
   Computed in the shopper's browser from THEIR clock every time the page loads,
   so it re-dates itself daily and can never show a stale window. Handling runs
   Mon–Fri; USPS Ground Advantage delivers Mon–Sat, so transit skips Sundays and
   US federal holidays only. */
if (!customElements.get('tb-delivery')) {
  const TB_HOLIDAYS = new Set([
    // US federal holidays on which neither the warehouse nor USPS moves.
    '2026-10-12', '2026-11-11', '2026-11-26', '2026-12-25',
    '2027-01-01', '2027-01-18', '2027-02-15', '2027-05-31', '2027-06-18',
    '2027-07-05', '2027-09-06', '2027-10-11', '2027-11-11', '2027-11-25', '2027-12-24',
  ]);

  const tbKey = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  customElements.define(
    'tb-delivery',
    class TbDelivery extends HTMLElement {
      connectedCallback() {
        this.render();
        // A tab left open overnight would otherwise keep yesterday's dates.
        this.timer = setInterval(() => this.render(), 30 * 60 * 1000);
        document.addEventListener('visibilitychange', this.onShow = () => {
          if (!document.hidden) this.render();
        });
      }

      disconnectedCallback() {
        clearInterval(this.timer);
        document.removeEventListener('visibilitychange', this.onShow);
      }

      closed(date, saturdayCounts) {
        const day = date.getDay();
        if (day === 0) return true;                    // Sunday: nobody moves
        if (day === 6 && !saturdayCounts) return true; // Saturday: carrier only
        return TB_HOLIDAYS.has(tbKey(date));
      }

      advance(from, days, saturdayCounts) {
        const date = new Date(from.getTime());
        let left = days;
        while (left > 0) {
          date.setDate(date.getDate() + 1);
          if (!this.closed(date, saturdayCounts)) left -= 1;
        }
        return date;
      }

      render() {
        const handling = Number(this.dataset.handlingDays);
        const fast = Number(this.dataset.minTransit);
        const slow = Number(this.dataset.maxTransit);
        if (!Number.isFinite(handling) || !Number.isFinite(fast) || !Number.isFinite(slow)) return;

        const now = new Date();
        const cutoff = Number(this.dataset.cutoffHour);
        // Past the cutoff, today no longer counts as a packing day.
        let base = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        if (Number.isFinite(cutoff) && now.getHours() >= cutoff) {
          base = this.advance(base, 1, false);
        }
        const ships = this.advance(base, handling, false);
        const earliest = this.advance(ships, fast, true);
        const latest = this.advance(ships, slow, true);

        const short = { weekday: 'short', month: 'short', day: 'numeric' };
        const noDay = { month: 'short', day: 'numeric' };
        const one = (d, opts) => d.toLocaleDateString(undefined, opts);
        const sameMonth = earliest.getMonth() === latest.getMonth();
        const window = `${one(earliest, short)} – ${one(latest, sameMonth ? { day: 'numeric' } : noDay)}`;

        const out = this.querySelector('[data-tb-delivery-text]') || this;
        out.innerHTML =
          `<span class="tb-deliv__lead">${this.dataset.label || 'Get it'}</span> `
          + `<strong>${window}</strong>`;
        this.setAttribute('data-ready', 'true');
      }
    }
  );
}

/* Trailblazer — small helpers the builder leans on. */

// Browser storage throws outright in some privacy modes, so every touch is
// wrapped and every failure is silent. Nothing here is load-bearing.
//
// `var`, not `const`, and the distinction is not cosmetic. Several sections
// ask the theme for this file, so a product page can end up with six copies
// of the same script tag. A second `const` of the same name at the top level
// is a SYNTAX error, which the browser raises while it is still reading the
// file — before a single line of it runs, so no guard inside the file can
// catch it, and the whole copy is thrown away with a red error in the
// console. A repeated `var` is simply allowed. The double-run guard below
// (TB_BOOTED) is what actually stops the work being done twice; this is only
// what lets the file get far enough to reach it.
var TB_STORE = {
  read(key) {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      return null;
    }
  },
  write(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      /* full, blocked, or private mode — carry on without it */
    }
  },
  drop(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (err) {
      /* nothing to do */
    }
  },
};

// One place to shout from, so the builder does not care who is listening.
// Anything present gets told; anything absent is skipped without error.
function tbTrack(name, payload) {
  const detail = Object.assign({ event: `tb_${name}` }, payload || {});
  try {
    document.dispatchEvent(new CustomEvent(`tb:${name}`, { detail, bubbles: true }));
  } catch (err) {
    /* ancient browser */
  }
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(detail);
  } catch (err) {
    /* no GTM */
  }
  try {
    if (typeof window.gtag === 'function') window.gtag('event', `tb_${name}`, payload || {});
  } catch (err) {
    /* no GA4 */
  }
}

/* Trailblazer — build your pack */
var TB_BUILD = '2026-09-19a';

// Stamped onto every cart line the builder adds, and onto the cart itself.
// Two jobs: a second checkout knows which lines were its own and replaces
// them, and the finished order carries a mark that Flow can tag on.
var TB_PACK_MARK = 'Build Your Pack';

// This file is pulled in by several sections AND, so the cart and every other
// page get it too, by the theme layout. A page carrying both would run it
// twice and double every listener. The custom elements were already guarded;
// this guards the one-time page wiring below.
var TB_BOOTED = window.__TB_BOOTED === true;
window.__TB_BOOTED = true;

if (!customElements.get('tb-bundle-builder')) {
  customElements.define(
    'tb-bundle-builder',
    class TbBundleBuilder extends HTMLElement {
      connectedCallback() {
        this.currency = this.dataset.currency || 'USD';
        this.tiers = this.parse(this.dataset.tiers, []).sort((a, b) => a.at - b.at);
        this.max = Number(this.dataset.max) || 1;
        this.mode = this.dataset.mode === 'value' ? 'value' : 'packs';
        this.eligible = new Set(this.parse(this.dataset.eligible, []).map(Number));
        // No list at all (or a collection that turned out empty) means every
        // product counts. It must never mean that nothing does.
        this.eligibleAll = this.eligible.size === 0;
        this.freeCents = Number(this.dataset.freeCents) || 0;
        this.inBag = 0;
        this.inBagValue = 0;
        this.lastPct = 0;
        this.lastFree = false;

        // lines: Map keyed by variant id -> { id, qty, price, title, detail, productId }
        this.lines = new Map();
        // Two levels, deliberately. The master switch says "I want to
        // subscribe" and opens the question; each line then answers it for
        // itself, with its own quantity. Off at either level means no
        // subscription line for that item.
        this.subOff = new Set();   // lines switched off inside an on pack
        this.repeatQty = new Map(); // how many of that line keep coming
        this.tiles = Array.from(this.querySelectorAll('[data-tb-tile]'));

        // plan id -> "Every 3 months". Each product has its own ids for the
        // same wording, so the cadence is what the two sides agree on.
        const cadRaw = this.querySelector('[data-tb-cadences]');
        this.cadences = cadRaw ? this.parse(cadRaw.textContent, {}) : {};
        const nameRaw = this.querySelector('[data-tb-cadence-names]');
        this.everyNames = nameRaw ? this.parse(nameRaw.textContent, []) : [];
        if (!this.everyNames.length) {
          // The list is the reliable source because it is ordered. If it is
          // missing, the map's values still name every cadence on offer — worse
          // order, but far better than the page deciding nothing can repeat.
          const seen = [];
          Object.keys(this.cadences).forEach((id) => {
            const nm = this.cadences[id];
            if (nm && seen.indexOf(nm) === -1) seen.push(nm);
          });
          this.everyNames = seen;
        }
        this.defaultEvery = this.everyNames[0] || '';
        this.repeatEvery = new Map();

        this.tiles.forEach((tile) => {
          const raw = tile.querySelector('[data-tb-variants]');
          tile.variants = raw ? this.parse(raw.textContent, []) : [];
          const addButton = tile.querySelector('[data-tb-add]');
          if (addButton) {
            addButton.dataset.label = addButton.textContent.trim();
            addButton.addEventListener('click', () => this.add(tile));
            // Once something is in the pack the button becomes a quantity
            // control. Pressing "Add to pack" a second time works, but nobody
            // should have to guess that — the stepper says it out loud.
            const stepper = document.createElement('div');
            stepper.className = 'tb-bundle__qty';
            stepper.hidden = true;
            stepper.innerHTML =
              '<button type="button" data-tb-tile-step="-1" aria-label="Remove one">&minus;</button>'
              + '<b><span data-tb-tile-qty>0</span> <i>in pack</i></b>'
              + '<button type="button" data-tb-tile-step="1" aria-label="Add one">+</button>';
            addButton.insertAdjacentElement('afterend', stepper);
            tile.stepper = stepper;
            stepper.addEventListener('click', (event) => {
              const button = event.target.closest('[data-tb-tile-step]');
              if (!button) return;
              const variant = this.currentVariant(tile);
              if (variant) this.step(variant.id, Number(button.dataset.tbTileStep));
            });
          }
          // One handler on the tile for the sold-out alternatives, because the
          // chips are rebuilt every time the choice changes.
          tile.addEventListener('click', (event) => {
            const chip = event.target.closest('[data-tb-swap]');
            if (!chip) return;
            this.swapTo(tile, chip.dataset.tbSwap.split('|'));
          });
          tile.querySelectorAll('[data-tb-option]').forEach((sel) => {
            sel.addEventListener('change', () => {
              this.sizeTouched(tile, sel);
              this.onOptionChange(tile);
            });
          });
        });

        this.buildStops();

        this.list = this.querySelector('[data-tb-list]');
        this.list?.addEventListener('click', (event) => {
          const sub = event.target.closest('[data-tb-line-sub]');
          if (sub) {
            const key = String(sub.dataset.tbLineSub);
            if (!this.repeating()) {
              // Coming in through one item: that item, and nothing else. The
              // master switch follows, because there IS a subscription now —
              // but it does not drag the rest of the pack in with it.
              this.subOff.clear();
              this.lines.forEach((other) => {
                if (String(other.id) !== key) this.subOff.add(String(other.id));
              });
              const yes = this.repeatBox
                && this.repeatBox.querySelector('[data-tb-repeat-mode][value="repeat"]');
              if (yes) yes.checked = true;
              this.subAll = false;
              tbTrack('resupply_mode', { on: true, from: 'line' });
            } else if (this.subOff.has(key)) {
              this.subOff.delete(key);
            } else {
              this.subOff.add(key);
              // Switching off the last one means nothing is repeating, so the
              // master should say so rather than claiming a subscription that
              // has no items in it.
              let any = false;
              this.lines.forEach((other) => {
                if (this.hasAnyPlan(other) && !this.subOff.has(String(other.id))) any = true;
              });
              if (!any) {
                const no = this.repeatBox
                  && this.repeatBox.querySelector('[data-tb-repeat-mode][value="once"]');
                if (no) no.checked = true;
              }
            }
            tbTrack('resupply_line', { on: !this.subOff.has(key) });
            this.render();
            return;
          }
          const rep = event.target.closest('[data-tb-rep-step]');
          if (rep) {
            const key = String(rep.dataset.variant);
            const line = this.lines.get(key);
            if (!line) return;
            let next = this.repeatQtyFor(line) + Number(rep.dataset.tbRepStep);
            if (next < 1) next = 1;
            if (next > 10) next = 10;
            this.repeatQty.set(key, next);
            // What repeats ships today too, so raising the subscription raises
            // today's quantity rather than being capped by it.
            if (next > line.qty) this.step(key, next - line.qty);
            else this.render();
            return;
          }
          const button = event.target.closest('[data-tb-step]');
          if (!button) return;
          this.step(button.dataset.variant, Number(button.dataset.tbStep));
        });
        this.list?.addEventListener('change', (event) => {
          const sel = event.target.closest('[data-tb-line-every]');
          if (!sel) return;
          this.repeatEvery.set(String(sel.dataset.tbLineEvery), sel.value);
          this.render();
        });

        this.addButton = this.querySelector('[data-tb-bundle-add]');
        this.addButton?.addEventListener('click', () => this.addAll());
        this.wireRepeat();
        this.makeStickyBar();
        this.wireQuickView();

        // Emptying the pack used to leave the last "$X more for..." line on
        // screen, which reads as a promise about a bag that no longer exists.
        const startMsg = this.querySelector('[data-tb-meter-msg]');
        this.startNote = startMsg ? startMsg.innerHTML : '';

        this.packKey = 'tb-pack-v1';
        this.sizeKey = 'tb-size-v1';
        this.restoreSizes();
        this.seedFromLink();

        this.setupFocus();
        this.makeDebug();
        this.render();
        // Always, now. The setting below governs whether OTHER people's lines
        // count toward the meter; reading the cart is how the pack learns what
        // it already put there, and the mirror cannot start without it.
        this.readBag();
        // And if that first read never landed — a slow network, a request that
        // went out before the page was ready — the mirror would stay switched
        // off for the whole visit and every choice would go nowhere. One quiet
        // second chance, which is the difference between a hiccup and a
        // builder that does not write to the cart at all.
        setTimeout(() => { if (!this.bagReady) this.readBag(); }, 2500);

        // The cart is not only written from here. Somebody can open the bag,
        // delete a line and come back — and until now the builder carried on
        // showing a pack that no longer existed, because it read the cart once
        // on arrival and never looked again. Every way back to this page is a
        // reason to look: a tab regaining focus, a drawer closing, a cached
        // page being restored.
        const relook = () => this.recheckBag();
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') relook();
        });
        window.addEventListener('focus', relook);
        window.addEventListener('pageshow', relook);
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof MutationObserver === 'function') {
          const watch = new MutationObserver(() => relook());
          watch.observe(drawer, { childList: true, subtree: true });
        }
      }

      // On a phone the pack summary sits below every tile, so someone who has
      // chosen three things has to scroll back past the whole grid to buy. This
      // rides along at the bottom, and gets out of the way the moment the real
      // summary button is on screen.
      // The bar's height is whatever its contents come to, so the room left
      // for it at the bottom of the page is measured rather than guessed. A
      // guess that is too small hides the last line of the page behind it.
      padForDock(bar) {
        const set = () => {
          const tall = bar && !bar.hidden ? bar.offsetHeight : 0;
          document.documentElement.style.setProperty(
            '--tb-dock-h', (tall ? tall + 16 : 0) + 'px'
          );
        };
        set();
        window.addEventListener('resize', set);
        if (typeof ResizeObserver === 'function') new ResizeObserver(set).observe(bar);
        return set;
      }

      makeStickyBar() {
        if (!this.addButton) return;
        const bar = document.createElement('div');
        bar.className = 'tb-dock';
        bar.hidden = true;
        // "Subscribe to socks" means going to the socks and pressing the thing
        // that says subscribe. Watching somebody build a whole pack, get told
        // to subscribe to one item, ignore every switch and the bar, scroll
        // back up and press this — which was a label — settled what it should
        // be. A black filled box in this theme is a button everywhere else.
        //
        // It puts the item in the pack if it is not there, subscribes that one
        // line and nothing else, and takes them to the pack to see it. The
        // quantity and frequency still live there; this is the way in.
        this.addEventListener('click', (event) => {
          const chip = event.target.closest('[data-tb-subchip]');
          if (!chip) return;
          event.preventDefault();
          event.stopPropagation();
          const tile = chip.closest('[data-tb-tile]');
          if (!tile) return;
          const variant = this.currentVariant(tile);
          if (!variant || !variant.available) return;
          const key = String(variant.id);
          if (!this.lines.has(key)) this.add(tile);

          if (!this.repeating()) {
            // Same narrow door as a line's own switch: this item, not the pack.
            this.subOff.clear();
            this.lines.forEach((other) => {
              if (String(other.id) !== key) this.subOff.add(String(other.id));
            });
            const yes = this.repeatBox
              && this.repeatBox.querySelector('[data-tb-repeat-mode][value="repeat"]');
            if (yes) yes.checked = true;
          } else {
            this.subOff.delete(key);
          }
          tbTrack('resupply_mode', { on: true, from: 'chip' });
          this.render();
          this.goToPack();
        });

        // A strip above the total, carried the whole way down the grid. The
        // pack summary is off screen for most of the build, which is exactly
        // the stretch where subscribing has to stay visible. It does not
        // subscribe anybody — it takes them to the choice, which is one place
        // and stays one place.
        // One job: a way to finish, and a way to look. The running commentary
        // that used to sit under the total — "$23.01 to 10% off" — was the
        // meter's line, repeated a second time two inches below the meter.
        bar.innerHTML =
          '<div class="tb-dock__row">'
          + '<div class="tb-dock__facts">'
          + '<b data-tb-dock-total></b>'
          + '</div>'
          + '</div>'
          + '<div class="tb-dock__acts">'
          + '<button type="button" class="tb-dock__sub-btn" data-tb-dock-sub hidden>'
          + '<span data-tb-dock-sub-text></span>'
          + '</button>'
          + '<button type="button" class="tb-dock__go"></button>'
          + '</div>';
        // Its OWN label, and its own job. Copying the pack button's text is why
        // renaming that to "Check out" renamed this too — and it does not check
        // anybody out, it takes them to the pack.
        this.dockGo = bar.querySelector('.tb-dock__go');
        // Fixed, not a setting. It WAS a setting, and changing the setting's
        // default could never reach a shop that had already saved a value
        // against it — which is why this still said "View my cart" after the
        // word had been taken out of everywhere else. Their collection is
        // called the pack on every screen a customer sees; "cart" is the
        // shop's own plumbing and stays out of the words.
        this.dockGo.textContent = 'I\u2019m done building';
        this.dockGo.addEventListener('click', () => this.goToPack());
        // Turns subscribing on and takes them to the pack in one press. The
        // per-item switches are the point of the feature and they live down
        // there, so a version of this that flipped a switch and left them at
        // the top of the grid would hide the thing it just turned on.
        bar.querySelector('[data-tb-dock-sub]').addEventListener('click', () => {
          this.setRepeat(true);
          this.goToPack();
        });


        document.body.appendChild(bar);
        this.measureDock = this.padForDock(bar);
        this.dock = bar;
        this.summaryEl = this.querySelector('.tb-bundle__summary');

        if ('IntersectionObserver' in window) {
          // Fine steps, because the test is "how much of the pack can I see",
          // not "can I see any of it" — and a coarse threshold list would let
          // the bar flip somewhere other than where the rule says.
          const steps = [];
          for (let i = 0; i <= 50; i += 1) steps.push(i / 50);

          this.dockWatch = new IntersectionObserver(
            ([entry]) => {
              // The bar goes when the pack has taken over the screen — half of
              // the usable height, or half the pack itself when the pack is the
              // shorter of the two. A sliver of it appearing at the bottom of
              // the grid is not arriving at it, and that is when the bar is
              // still doing its job.
              const shown = entry.intersectionRect.height;
              const box = entry.boundingClientRect.height || 1;
              const reach = Math.min(window.innerHeight - this.dockHeight(), box);
              this.summaryOnScreen = reach > 0 && shown / reach >= 0.5;
              this.paintDock();
            },
            // Watching the pack itself, not the button at its foot: the bar
            // exists to get you to the pack, so the moment the pack is on
            // screen it has nothing left to offer. Scroll back above it and
            // it returns.
            //
            // The bottom margin is the bar's own height — otherwise the pack
            // peeking out from underneath it counts as visible.
            { rootMargin: `0px 0px -${this.dockHeight()}px 0px`, threshold: steps }
          );
          this.dockWatch.observe(this.summaryEl || this.addButton);
        }
      }

      dockHeight() {
        return (this.dock && this.dock.offsetHeight) || 110;
      }

      // Are the pack's own buttons — Keep building, Check out, Restart pack —
      // in view ABOVE the bar? Measured from where they are right now rather
      // than watched: a watcher reports a hidden, zero-height element as
      // "visible", which is how the bar ended up hidden on screens that never
      // showed those buttons at all.
      actsUp() {
        if (!this.packActs) return false;
        const box = this.packActs.getBoundingClientRect();
        if (!box.height) return false;
        return box.bottom > 0 && box.top < window.innerHeight - this.dockHeight();
      }

      paintDock() {
        if (!this.dock) return;
        // The bar exists to get somebody TO those buttons. The moment they are
        // on screen it has nothing left to offer — and worse, it is sitting on
        // top of them. Scrolling down to reach Restart pack used to bring the
        // bar back over it, so the first press hit the bar, the page jumped,
        // and the second press landed.
        const show = this.lines.size > 0 && !this.summaryOnScreen && !this.actsUp();
        this.dock.hidden = !show;
        document.body.classList.toggle('tb-dock-on', show);
        if (this.measureDock) this.measureDock();

        const strip = this.dock.querySelector('[data-tb-dock-sub]');
        if (!strip) return;
        strip.hidden = !this.repeatBox;
        const on = this.repeating();
        strip.classList.toggle('is-on', on);
        // One button, the full width of the phone. Two buttons made somebody
        // choose between finishing and looking, which is a choice nobody asked
        // for and the wrong one is easy to press — and "Check out" next to
        // "View pack" quietly implies that checking out skips the looking.
        // It does not: this goes to their pack, where the total, the
        // subscription switches and the order button all are.
        if (this.flow) {
          strip.hidden = true;
          strip.classList.remove('is-on');
          return;
        }
        const text = strip.querySelector('[data-tb-dock-sub-text]');
        if (text) {
          const off = this.subOffPct || 0;
          text.textContent = on
            ? 'Set up subscription'
            : 'Subscribe to get ' + (off || 10) + '% off';
        }
      }

      // The bar ends at the pack, because that is where the decisions are and
      // where the order is placed.
      // Moving between screens is a pause in the tapping, so it is a free
      // moment to send whatever is outstanding — and it means the cart is
      // already right by the time anyone reaches the checkout button.
      flushBag() {
        if (this.bagTimer) { clearTimeout(this.bagTimer); this.bagTimer = 0; this.syncBag(); }
      }

      goToPack() {
        this.flushBag();
        // The TOP of the pack, not the button at its foot. Somebody asking to
        // see their cart wants to read it from the start — the choice, then
        // the items — not to be dropped at the bottom of it.
        const anchor = this.summaryEl || this.addButton || this.repeatBox;
        if (!anchor) return;

        // The meter pins itself to the top, so aligning the pack with the top
        // of the window slides the pack underneath it. Room is left for the
        // bar AND for whatever the shop's header is doing above it, both
        // measured rather than assumed — the bar's height changes with the
        // message in it, and the header comes and goes as the page moves.
        //
        // This used to test for position:sticky, which the meter stopped being
        // when it was changed to a fixed bar. The test quietly failed, the
        // offset came out as zero, and the pack landed behind the meter.
        const meter = this.querySelector('[data-tb-meter]');
        let offset = 0;
        if (meter && !meter.hidden) {
          const stick = parseFloat(
            getComputedStyle(this).getPropertyValue('--tb-stick')
          ) || 0;
          offset = stick + meter.offsetHeight;
        }
        const top = Math.max(0, window.scrollY + anchor.getBoundingClientRect().top - offset - 12);
        window.scrollTo({ top, behavior: 'smooth' });
        // Same as every other landing: once it has settled, the shop's header
        // is brought back, so the pack is read under a header rather than
        // under a gap where one is about to appear.
        clearTimeout(this.packPeek);
        this.packPeek = setTimeout(() => {
          if (Math.abs(window.pageYOffset - top) > 40) return;
          this.headPeek(window.pageYOffset);
        }, 700);

        const flash = this.repeatBox || anchor;
        flash.classList.add('tb-repeat--flash');
        clearTimeout(this.flashTimer);
        this.flashTimer = setTimeout(() => flash.classList.remove('tb-repeat--flash'), 1400);

        // Belt and braces: a smooth scroll that gets interrupted, or a browser
        // that fires the observer late, would otherwise strand the bar on
        // screen. Scrolling back up brings it straight back.
        clearTimeout(this.dockHideTimer);
        this.dockHideTimer = setTimeout(() => {
          if (!this.dock || this.dock.hidden) return;
          const box = anchor.getBoundingClientRect();
          const room = window.innerHeight - this.dockHeight();
          const shown = Math.min(box.bottom, room) - Math.max(box.top, 0);
          const reach = Math.min(room, box.height || 1);
          if (reach > 0 && shown / reach >= 0.5) {
            this.summaryOnScreen = true;
            this.paintDock();
          }
        }, 700);
      }

      clearSubPrefs() {
        this.subOff.clear();
        this.repeatQty.clear();
        this.repeatEvery.clear();
        const no = this.repeatBox
          && this.repeatBox.querySelector('[data-tb-repeat-mode][value="once"]');
        if (no) no.checked = true;
        // And the products themselves. Clearing the pack used to leave every
        // product page still set to Subscribe, with its frequency picker open
        // and its button quoting the plan price — a decision nobody had made,
        // sitting on top of a pack that had just been emptied on purpose.
        this.subAll = false;
        this.tiles.forEach((tile) => {
          if (!tile.dressed) return;
          tile.subWant = false;
          tile.subQty = 1;
          tile.subEvery = this.defaultEvery;
          this.syncSub(tile);
          this.paintBuy(tile);
        });
      }

      setRepeat(on) {
        const want = on ? 'repeat' : 'once';
        const el = this.repeatBox
          && this.repeatBox.querySelector(`[data-tb-repeat-mode][value="${want}"]`);
        if (!el) return;

        // Turning it on is a fresh start, whether or not it was already on.
        // Pressing Subscribe and watching nothing happen because the state was
        // technically already "subscribed" is the worst kind of dead button.
        if (on) { this.subOff.clear(); this.subAll = true; }
        const changed = !el.checked;
        el.checked = true;
        if (!changed && !on) return;
        tbTrack('resupply_mode', { on: on, from: 'dock' });
        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener('keydown', this.onQuickKey);
        document.body.classList.remove('tb-quick-on');
        this.quick?.remove();
        this.dockWatch?.disconnect();
        this.dock?.remove();
        document.body.classList.remove('tb-dock-on');
      }

      // ---- quick view: get informed without leaving the page -----------
      // A cold visitor should not have to choose between buying blind and
      // navigating away. The drawer body is fetched from its own section the
      // first time it is asked for, then kept.
      wireQuickView() {
        this.quick = this.querySelector('[data-tb-quick]');
        if (!this.quick) return;
        // position:fixed resolves against the nearest ancestor carrying a
        // transform, not the viewport. Dawn's scroll-in animation puts a
        // transform on the section while it slides into view, which trapped the
        // drawer inside the section's box and cropped it. Hanging it off <body>
        // means no ancestor can ever do that again.
        document.body.appendChild(this.quick);
        this.quickSlot = this.quick.querySelector('[data-tb-quick-slot]');
        this.quickFoot = this.quick.querySelector('[data-tb-quick-foot]');
        this.quickAdd = this.quick.querySelector('[data-tb-quick-add]');
        this.quickCache = new Map();

        this.addEventListener('click', (event) => {
          const opener = event.target.closest('[data-tb-open]');
          if (!opener) return;
          const tile = opener.closest('[data-tb-tile]');
          if (tile) this.openQuick(tile);
        });

        this.quick.querySelectorAll('[data-tb-quick-close]').forEach((el) => {
          el.addEventListener('click', () => this.closeQuick());
        });
        // Escape, the scrim and a swipe down all close it. Trapping someone in a
        // panel they cannot dismiss reads as a trick and costs more than it saves.
        this.onQuickKey = (event) => {
          if (event.key === 'Escape' && !this.quick.hidden) this.closeQuick();
        };
        document.addEventListener('keydown', this.onQuickKey);

        // The panel moves with the thumb so it is obvious a hard pull dismisses it
        // and a small one does not. The old version closed on a 90px drag with no
        // feedback, which meant a stray scroll threw the whole drawer away.
        const panel = this.quick.querySelector('.tb-quick__panel');
        this.quickPanel = panel;
        const scrim = this.quick.querySelector('.tb-quick__scrim');
        let startY = null;
        let startT = 0;
        let dy = 0;

        const setDrag = (px) => {
          panel.style.transform = px ? `translateY(${px}px)` : '';
          if (scrim) scrim.style.opacity = px ? String(Math.max(0.25, 1 - px / 420)) : '';
        };

        panel?.addEventListener('touchstart', (event) => {
          if (event.touches.length !== 1) return;
          // Only when the content is already scrolled to the top, so a normal
          // read never turns into a dismiss.
          if (this.quickSlot && this.quickSlot.scrollTop > 0) { startY = null; return; }
          startY = event.touches[0].clientY;
          startT = Date.now();
          dy = 0;
          panel.style.transition = 'none';
        }, { passive: true });

        panel?.addEventListener('touchmove', (event) => {
          if (startY === null) return;
          const raw = event.touches[0].clientY - startY;
          if (raw <= 0) { dy = 0; setDrag(0); return; }
          // One to one with the thumb. Damping made the panel lag the finger,
          // which reads as sluggish and hides how far you actually have to pull.
          dy = raw;
          setDrag(dy);
        }, { passive: true });

        const settle = () => {
          if (startY === null) return;
          // About a quarter of the panel's own height, so the distance scales
          // with the sheet instead of being a number that suits one phone.
          const needed = Math.max(120, panel.getBoundingClientRect().height * 0.24);
          const flick = dy > 70 && Date.now() - startT < 240;
          panel.style.transition = 'transform .2s ease-out';
          if (dy > needed || flick) {
            setDrag(panel.getBoundingClientRect().height);
            setTimeout(() => this.closeQuick(), 160);
          } else {
            setDrag(0);
          }
          startY = null;
        };
        panel?.addEventListener('touchend', settle);
        panel?.addEventListener('touchcancel', settle);

        this.quickAdd?.addEventListener('click', () => this.addFromQuick());

        // Opening should feel instant. Warm the body on the first sign of
        // intent — a finger landing on the tile, or a cursor arriving — which
        // buys 100-300ms, and quietly fetch the rest once the page is idle.
        const warm = (event) => {
          const tile = event.target.closest?.('[data-tb-tile]');
          if (tile) this.warmQuick(tile.dataset.handle);
        };
        this.addEventListener('pointerenter', warm, true);
        this.addEventListener('touchstart', warm, { passive: true, capture: true });

        const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
        idle(() => {
          this.tiles.forEach((tile, i) => {
            setTimeout(() => this.warmQuick(tile.dataset.handle), i * 250);
          });
        });
      }

      warmQuick(handle) {
        if (!handle || this.quickCache.has(handle)) return;
        // Mark it first so two events cannot start the same request twice.
        this.quickCache.set(handle, null);
        fetch(`${window.Shopify.routes.root}products/${handle}?section_id=tb-quick`)
          .then((res) => res.text())
          .then((html) => { this.quickCache.set(handle, html); })
          .catch(() => { this.quickCache.delete(handle); });
      }

      async openQuick(tile) {
        const handle = tile.dataset.handle;
        if (!handle) return;
        this.quickTile = tile;
        this.quickReturn = tile.querySelector('[data-tb-open]');
        this.quick.hidden = false;
        document.body.classList.add('tb-quick-on');
        this.quickFoot.hidden = true;
        tbTrack('quick_view_opened', { product_title: tile.dataset.title || '', handle });

        let html = this.quickCache.get(handle);
        if (html == null) {
          this.quickSlot.innerHTML = '<p class="tb-quick__loading">Loading&hellip;</p>';
          try {
            const res = await fetch(`${window.Shopify.routes.root}products/${handle}?section_id=tb-quick`);
            html = await res.text();
            this.quickCache.set(handle, html);
          } catch (err) {
            // Never strand him: offer the real page rather than an empty panel.
            this.quickSlot.innerHTML =
              `<p class="tb-quick__loading">Couldn't load the details. `
              + `<a href="${window.Shopify.routes.root}products/${handle}" target="_blank" rel="noopener">Open the product page</a>.</p>`;
            return;
          }
        }
        if (this.quick.hidden) return;
        this.quickSlot.innerHTML = html;
        this.quickSlot.scrollTop = 0;

        const raw = this.quickSlot.querySelector('[data-tb-quick-variants]');
        this.quickVariants = raw ? this.parse(raw.textContent, []) : [];
        // Start the drawer on whatever he already had chosen on the tile.
        const chosen = Array.from(tile.querySelectorAll('[data-tb-option]')).map((sel) => sel.value);
        this.quickSlot.querySelectorAll('[data-tb-quick-option]').forEach((sel, i) => {
          if (chosen[i] != null && Array.from(sel.options).some((o) => o.value === chosen[i])) {
            sel.value = chosen[i];
          }
          sel.addEventListener('change', () => this.paintQuick());
        });
        this.quickFoot.hidden = !this.quickSlot.querySelector('[data-tb-quick-buy]');
        this.wakeReviews();
        this.paintQuick();
        this.quick.querySelector('.tb-quick__x')?.focus();
      }

      // Judge.me renders its widget from its own script. Dropped into the DOM
      // after load it needs a poke, and the poke is not the same call in every
      // version — so try what exists, then check whether anything actually
      // appeared. An empty reviews box is worse than admitting there isn't one.
      wakeReviews() {
        const box = this.quickSlot.querySelector('[data-tb-quick-reviews]');
        const slot = box?.querySelector('.jdgm-widget');
        if (!box || !slot) return;
        const jdgm = window.jdgm || {};
        [jdgm.batchRenderBadges, jdgm.customizeBadges, jdgm.initWidgets,
         window.jdgmSetupWidgets, window.jdgmLoadCss]
          .forEach((fn) => { try { if (typeof fn === 'function') fn.call(jdgm); } catch (err) { /* not this one */ } });

        clearTimeout(this.reviewTimer);
        this.reviewTimer = setTimeout(() => {
          if (slot.children.length || slot.textContent.trim()) return;
          const fallback = box.querySelector('[data-tb-quick-reviews-fallback]');
          slot.replaceWith(fallback ? fallback.content.cloneNode(true) : document.createTextNode(''));
        }, 1800);
      }

      quickVariant() {
        const chosen = Array.from(this.quickSlot.querySelectorAll('[data-tb-quick-option]')).map((s) => s.value);
        return (this.quickVariants || []).find((v) => v.options.every((o, i) => o === chosen[i]));
      }

      paintQuick() {
        if (!this.quickAdd) return;
        const variant = this.quickVariant();
        const out = !variant || !variant.available;
        this.quickAdd.disabled = out;
        const line = variant ? this.lines.get(String(variant.id)) : null;
        const mine = line ? line.qty : 0;
        this.quickAdd.textContent = out
          ? 'Sold out'
          : mine > 0 ? `Add another (${mine} in pack)` : 'Add to pack';
        // A cue that this is one stop on a bigger job, not the end of it.
        const meterEl0 = this.quick.querySelector('[data-tb-quick-meter]');
        if (meterEl0) meterEl0.classList.toggle('is-live', this.lines.size > 0);

        // The meter is behind the drawer, so bring its state along.
        const meterEl = this.quick.querySelector('[data-tb-quick-meter]');
        const msg = this.querySelector('[data-tb-meter-msg]');
        if (meterEl && msg) meterEl.textContent = msg.textContent.trim();
      }

      addFromQuick() {
        const variant = this.quickVariant();
        if (!variant || !variant.available || !this.quickTile) return;
        // Keep the tile in step with what he chose in the drawer.
        const chosen = Array.from(this.quickSlot.querySelectorAll('[data-tb-quick-option]')).map((s) => s.value);
        const tile = this.quickTile;
        tile.querySelectorAll('[data-tb-option]').forEach((sel, i) => {
          if (chosen[i] != null && Array.from(sel.options).some((o) => o.value === chosen[i])) {
            sel.value = chosen[i];
          }
        });
        this.add(tile);
        // Adding closes the drawer. Leaving it open is how someone ends up
        // reading about tees and forgetting there are three other things — the
        // way out has to be the same gesture as the way forward.
        this.closeQuick();
        // Land him back on the grid looking at the tile he just filled, with the
        // meter in view, so the reward for adding is the first thing he sees.
        const meter = this.querySelector('[data-tb-meter]');
        if (meter && !this.prefersStill()) {
          meter.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        this.celebrate(this.querySelector('[data-tb-meter-badge]'));
      }

      prefersStill() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      closeQuick() {
        if (!this.quick || this.quick.hidden) return;
        if (this.quickPanel) {
          this.quickPanel.style.transition = '';
          this.quickPanel.style.transform = '';
        }
        const scrim = this.quick.querySelector('.tb-quick__scrim');
        if (scrim) scrim.style.opacity = '';
        this.quick.hidden = true;
        document.body.classList.remove('tb-quick-on');
        this.quickReturn?.focus();
        this.quickTile = null;
      }

      // ---- a breadcrumb, not a pack -------------------------------------
      //
      // This used to hold the pack itself, which made it a THIRD copy of the
      // same thing — browser, page, cart — and the one most likely to be
      // wrong, because it kept whatever it was told until something cleared
      // it. The pack lives in the cart now and nowhere else. All that is kept
      // here is where the builder is, so the cart page can send somebody back
      // to it. Nothing is ever restored from this.
      savePack() {
        if (!this.lines.size) {
          TB_STORE.drop(this.packKey);
          return;
        }
        let units = 0;
        this.lines.forEach((line) => { units += line.qty; });
        TB_STORE.write(this.packKey, {
          at: Date.now(),
          url: window.location.pathname,
          units,
        });
      }

      // Arriving from a product page. The link carries that product's handle,
      // so the builder opens with it already in the pack and the meter already
      // moving. Landing in an empty builder after clicking "Build a pack" reads
      // as being sent back to the start, and people leave.
      //
      // The handle is only ever matched against tiles this page already
      // rendered, so a link cannot introduce a product, a price or a quantity —
      // the worst a bad handle can do is nothing at all.
      seedFromLink() {
        let wanted = [];
        try {
          const raw = new URLSearchParams(window.location.search).get('add');
          if (!raw) return;
          wanted = raw.split(',').map((h) => h.trim().toLowerCase()).filter(Boolean).slice(0, 6);
        } catch (e) {
          return;
        }
        if (!wanted.length) return;

        let seeded = 0;
        wanted.forEach((entry) => {
          // "handle" or "handle:3" — the quantity the product page was showing.
          const bits = entry.split(':');
          const handle = bits[0];
          let qty = parseInt(bits[1], 10);
          if (!Number.isFinite(qty) || qty < 1) qty = 1;
          if (qty > 10) qty = 10;
          const tile = this.tiles.find((t) => (t.dataset.handle || '').toLowerCase() === handle);
          if (!tile) return;
          for (let i = 0; i < qty; i += 1) this.add(tile);
          seeded += qty;
        });

        // Take the parameter back out of the address bar, so a refresh or a
        // shared link does not quietly add the same pack a second time.
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('add');
          window.history.replaceState({}, '', url.pathname + url.search + url.hash);
        } catch (e) {
          /* an old browser keeps the parameter; nothing breaks */
        }

        if (!seeded) return;
        tbTrack('pack_seeded_from_link', { seeded: seeded, lines: this.lines.size });
        // They came here to build, not to read the hero again.
        requestAnimationFrame(() => {
          try {
            this.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } catch (e) {
            this.scrollIntoView();
          }
        });
      }

      // ---- he is a Large on every tile, not just the first one ----------
      rememberSize(tile) {
        const map = this.sizes || {};
        tile.querySelectorAll('[data-tb-option]').forEach((sel) => {
          const name = this.optionName(sel);
          if (name) map[name] = sel.value;
        });
        this.sizes = map;
        TB_STORE.write(this.sizeKey, map);
      }

      restoreSizes() {
        const map = TB_STORE.read(this.sizeKey);
        if (!map || typeof map !== 'object') return;
        this.sizes = map;
        this.tiles.forEach((tile) => this.applySizes(tile));
      }

      applySizes(tile) {
        if (!this.sizes) return;
        const pickers = Array.from(tile.querySelectorAll('[data-tb-option]'));
        const before = pickers.map((sel) => sel.value);
        let changed = false;
        pickers.forEach((sel) => {
          const want = this.sizes[this.optionName(sel)];
          if (!want) return;
          // Only if this product actually offers it — never invent a choice.
          const has = Array.from(sel.options).some((o) => o.value === want);
          if (has && sel.value !== want) {
            sel.value = want;
            changed = true;
          }
        });
        if (!changed) return;
        // Choosing Large on the tees answers the question for the tanks too.
        this.sizeTouched(tile, this.sizePicker(tile));
        // Remembering his size must never make a tile read "Sold out" before he
        // has touched it. If the combination we just built cannot be bought,
        // put the tile back exactly as it was.
        const variant = this.currentVariant(tile);
        if (!variant || !variant.available) {
          pickers.forEach((sel, i) => { sel.value = before[i]; });
          return;
        }
        this.onOptionChange(tile, true);
      }

      optionName(sel) {
        return (sel.closest('[data-tb-option-name]')?.dataset.tbOptionName
          || sel.previousElementSibling?.textContent
          || '').trim().toLowerCase();
      }

      pctAt(gross) {
        let hit = 0;
        this.tiers.forEach((t) => {
          if (gross >= t.at) hit = t.pct;
        });
        return hit;
      }

      // The gross subtotal at which shipping actually becomes free, given that a
      // discount is taken off before Shopify looks at the shipping threshold.
      grossForFreeShipping() {
        let gross = this.freeCents;
        for (let i = 0; i < 8; i += 1) {
          const pct = this.pctAt(gross);
          const need = Math.ceil(this.freeCents / (1 - pct / 100));
          if (need === gross) break;
          gross = need;
        }
        return gross;
      }

      // How much more product actually gets shipping free.
      //
      // Shopify takes the pack discount off BEFORE it looks at the shipping
      // threshold, so "sixty dollars minus what you are paying now" is not
      // the answer — adding an item can trip the next rung and push what you
      // pay DOWN, which moves the finish line away after the money is already
      // spent. Two tanks read "$10.02 away"; adding $14.99 of socks left it
      // reading "$1.53 away". That is a shop that looks like it is playing
      // games with somebody who did exactly what it asked.
      //
      // The meter already places the free-shipping rung at the gross subtotal
      // that genuinely clears it. This answers from the same place, so adding
      // what the line asks for always works.
      freeToGo(counted, eff) {
        const gap = this.grossForFreeShipping() - counted;
        if (gap > 0) return gap;
        // Enough counted, still paying under the line: something in the pack
        // does not count toward the discount, or a subscription moved the
        // total. Fall back to the plain shortfall rather than promising zero.
        return Math.max(0, this.freeCents - eff);
      }

      fillPercent(counted) {
        const stops = this.stops || [];
        if (!stops.length) return 0;
        let previous = 0;
        for (let i = 0; i < stops.length; i += 1) {
          const stop = stops[i];
          if (counted < stop.at) {
            const span = stop.at - previous;
            const through = span > 0 ? (counted - previous) / span : 1;
            return (i + Math.max(0, Math.min(1, through))) * this.segment;
          }
          previous = stop.at;
        }
        return 100;
      }

      buildStops() {
        this.stops = this.tiers.map((t) => ({
          // "off" rides along even on the narrow label. A bare "10%" beside a
          // dollar figure is a number without a verb — it could as easily be
          // read as a share of the pack as a discount on it.
          at: t.at, label: `${t.pct}% off`, short: `${t.pct}% off`, kind: 'pct', pct: t.pct,
        }));
        if (this.mode === 'value' && this.freeCents) {
          this.stops.push({
            at: this.grossForFreeShipping(), label: 'Free shipping', short: 'Free shipping', kind: 'ship',
          });
        }
        this.stops.sort((a, b) => a.at - b.at);
        this.max = this.stops.length ? this.stops[this.stops.length - 1].at : 1;
        // Every checkpoint gets an equal share of the bar, so each step feels like
        // the same distance however far apart the dollar amounts really are.
        this.segment = this.stops.length ? 100 / this.stops.length : 100;

        const host = this.querySelector('[data-tb-stops]');
        if (!host) return;
        host.innerHTML = this.stops
          .map((stop, i) => {
            const left = (i + 1) * this.segment;
            const detail = this.mode === 'value' ? this.money(stop.at) : `${stop.at} packs`;
            return `<span class="tb-meter__stop tb-meter__stop--${stop.kind}" data-tb-stop="${i}" style="left:${left}%">
              <i></i><em><b class="tb-meter__lg">${stop.label}</b><b class="tb-meter__sm">${stop.short}</b><br><small>${detail}</small></em>
            </span>`;
          })
          .join('');
      }

      // Replacing markup that has not changed is how a tap gets eaten.
      //
      // Every live block on this page — the four tabs, the four choosing
      // cards, the pack rows, the "in your pack" chips — is rebuilt from
      // scratch on every render. And a render happens whenever the cart
      // answers: a few hundred milliseconds after any tap, on coming back to
      // the tab, whenever the bag changes anywhere. If a finger is down on a
      // button at that moment, the button it was pressing has been thrown away
      // and replaced by an identical one by the time the finger lifts — and a
      // press that lands on an element no longer in the page does nothing at
      // all. Press again and it works. That is the whole of it.
      //
      // So nothing is replaced unless it is genuinely different.
      fresh(el, html) {
        if (!el) return false;
        if (el.tbHTML === html) return false;
        el.tbHTML = html;
        el.innerHTML = html;
        return true;
      }

      parse(value, fallback) {
        try {
          return JSON.parse(value);
        } catch (err) {
          return fallback;
        }
      }

      // A second look at the cart, from outside. Never while a write of our
      // own is in the air, and never twice in the same second — a tab being
      // clicked fires focus and visibilitychange together.
      recheckBag() {
        if (!this.bagReady || this.bagBusy) return;
        if (this.bagTimer) return;
        const now = Date.now();
        if (this.lastLook && now - this.lastLook < 900) return;
        this.lastLook = now;
        this.readBag(true);
      }

      // quiet: asked FOR a redraw rather than on arrival — do not move the
      // shopper to another screen and do not reach into the size and colour
      // pickers they may be using right now. Only the first read of the visit
      // gets to do those.
      async readBag(quiet, guard, had) {
        try {
          // change.js and update.js answer with the WHOLE cart, freshly
          // computed by the shop a moment ago. Asking for it again is a
          // second round trip for the same answer — on a phone that is a
          // few hundred milliseconds of the pack not matching the bag icon,
          // on every single tap. add.js does not answer with the cart, so
          // after an add this still goes and asks.
          let cart = had || null;
          if (!cart) {
            const res = await fetch(`${window.Shopify.routes.root}cart.js`);
            cart = await res.json();
          }
          // The pack's own lines are NOT "already in the bag" — they are the
          // pack, counted once by the pack. Counting them here as well was the
          // meter congratulating itself twice for the same tee.
          const mine = this.dataset.countCart === 'true'
            ? (cart.items || []).filter(
              (item) => !(item.properties || {})._pack
                && (this.eligibleAll || this.eligible.has(Number(item.product_id)))
            )
            : [];
          this.inBag = mine.reduce((sum, item) => sum + item.quantity, 0);
          this.inBagValue = mine.reduce((sum, item) => sum + item.line_price, 0);
          // Reading the cart and ADOPTING it are two different risks. If
          // adopting trips over something, the mirror must still come up —
          // the alternative is a builder that silently never writes to the
          // cart again for the rest of the visit, which is the worst failure
          // this page has.
          try {
            // Did the pack move on while this answer was coming back? Then
            // the answer is already out of date: keep what is on screen, take
            // only the cart's line keys from it, and send the newer thing.
            const stale = guard && this.bagSent != null
              && JSON.stringify(Array.from(this.bagWants().entries())) !== this.bagSent;
            this.adoptBag(cart, quiet, stale);
          } catch (err) {
            this.bagNote = 'adopt failed: ' + (err && err.message ? err.message : err);
            this.bagMap = this.bagMap || new Map();
            this.bagReady = true;
          }
        } catch (err) {
          this.inBag = 0;
          this.inBagValue = 0;
          this.bagNote = 'cart unreadable: ' + (err && err.message ? err.message : err);
          // Still on. A cart we could not read is not a reason to stop trying
          // to write to it; the next write reconciles from whatever comes back.
          this.bagMap = this.bagMap || new Map();
          this.bagReady = true;
        }
        this.render();
        // The header count is the shopper's proof this went somewhere real.
        if (typeof window.tbSyncBag === 'function') window.tbSyncBag();
        // Somebody arriving with a pack already built — back from the cart,
        // back from checkout, back tomorrow — is not starting over. They land
        // on the screen that shows what they have and what is missing, not on
        // a product page as though none of it happened. An ad that named a
        // product still wins, because that is what was clicked.
        if (!quiet && this.flow && this.lines.size && !this.hadStart) this.showNext(true);
      }

      makeDebug() {
        // Explicit flag only. Showing bar widths and gradient strings to
        // somebody previewing their own shop reads as the page being broken.
        if (window.location.search.indexOf('tbdebug=1') === -1) return;
        this.debug = document.createElement('div');
        this.debug.className = 'tb-debug';
        const meter = this.querySelector('[data-tb-meter]');
        if (meter && meter.parentNode) {
          meter.parentNode.insertBefore(this.debug, meter.nextSibling);
        } else {
          this.appendChild(this.debug);
        }
      }

      writeDebug(counted, span) {
        if (!this.debug) return;
        const rows = [
          ['build', TB_BUILD],
          ['meter mode', this.mode],
          ['eligible ids', this.eligible.size + (this.eligibleAll ? '  (empty list → everything counts)' : '')],
          ['checkpoints', this.tiers.length ? this.tiers.map((t) => `${t.at}c=${t.pct}%`).join('  ') : 'NONE — no percent set'],
          ['free shipping', this.freeCents ? this.freeCents + 'c' : 'NOT SET'],
          ['markers on bar', (this.stops || []).map((s2) => s2.at + 'c ' + s2.label).join('  |  ') || 'NONE'],
          ['bar runs 0 →', span + 'c'],
          ['lines in pack', this.lines.size],
          ['counted', counted + 'c'],
          ['bar width', span > 0 ? Math.round((counted / span) * 1000) / 10 + '%' : 'n/a'],
          ['bag mirror', this.bagReady ? 'on' : 'NOT STARTED — cart never read'],
          ['cart lines held', this.bagMap ? this.bagMap.size : 0],
          ['last cart write', this.bagNote || 'nothing sent yet'],
          ['last refusal', this.bagWhy || this.bagWarn || 'none'],
          ['bar painted', this.trackEl ? getComputedStyle(this.trackEl).backgroundSize : 'NO TRACK'],
          ['bar colour', this.trackEl ? getComputedStyle(this.trackEl).backgroundImage.slice(0, 60) : 'n/a'],
        ];
        this.debug.innerHTML =
          '<p class="tb-debug__h">Builder readout — only you can see this, it disappears on the live site</p>' +
          rows.map((r) => `<div><span>${r[0]}</span><b>${r[1]}</b></div>`).join('');
      }

      // Crossing a checkpoint is the one moment on this page worth marking, and
      // it used to pass with a 14% nudge of the badge and a border flash on a
      // bar that has no visible border. Three cues now, all inside a second:
      // the bar catches a light, the dot just passed throws a ring, the badge
      // lands. Restrained, but unmistakably something happened.
      celebrate(el) {
        // No early return on reduced motion any more. The classes always go on
        // and the stylesheet decides what is allowed to play — so somebody who
        // has asked for less movement still gets told, in colour, that
        // something was unlocked, instead of getting nothing at all.
        const restart = (node, cls) => {
          if (!node) return;
          node.classList.remove(cls);
          // Reading the layout forces the removal to take effect, so the same
          // animation can be played twice in a row.
          void node.offsetWidth;
          node.classList.add(cls);
        };
        restart(el, 'tb-pop');
        restart(this.querySelector('.tb-meter__track'), 'tb-sweep');
        restart(this.querySelector('[data-tb-meter]'), 'tb-won');
        const lit = this.querySelectorAll('.tb-meter__stop.is-on');
        restart(lit[lit.length - 1], 'tb-hit');

        // A short double tap of the phone, where the phone allows it. Android
        // does; iOS Safari has no vibration API at all, so roughly half the
        // traffic feels nothing and the visual has to carry the moment on its
        // own. Wrapped because some browsers expose the method and then throw.
        try {
          if (navigator.vibrate) navigator.vibrate([12, 40, 22]);
        } catch (err) {
          /* no haptics here */
        }
      }

      money(cents) {
        const value = Math.round(cents) / 100;
        if (!this.formatter) {
          try {
            this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
              style: 'currency',
              currency: this.currency,
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
          } catch (err) {
            this.formatter = null;
          }
        }
        if (this.formatter) return this.formatter.format(value);
        return `$${value.toFixed(2)}`;
      }

      // A tile's price, in the terms the pack is currently being bought on.
      // Writes to the amount only — setting text on the whole paragraph is what
      // used to erase the Subscribe chip and the Full price tag.
      paintTilePrice(tile) {
        const el = tile.querySelector('[data-tb-price-amount]');
        if (!el) return;
        const variant = this.currentVariant(tile);
        const list = variant ? variant.price : Number(
          tile.querySelector('[data-tb-price]')?.dataset.cents || 0
        );
        // The card shows one price: what this costs. The chip beside it is a
        // standing offer, not a readout of the current pack — a tile that
        // silently reprices itself because of a switch further down the page
        // is a card you cannot trust at a glance.
        el.textContent = this.money(list);

        // It is a control now, so it says what state it put things in. A
        // button that swallows a press without visibly changing gets pressed
        // again, and again.
        const chip = tile.querySelector('[data-tb-subchip]');
        if (chip) {
          const v = this.currentVariant(tile);
          const line = v ? this.lines.get(String(v.id)) : null;
          const on = Boolean(line && this.repeats(line));
          chip.classList.toggle('is-on', on);
          if (!chip.dataset.label) chip.dataset.label = chip.textContent.trim();
          chip.textContent = on ? 'Subscribed' : chip.dataset.label;
        }
      }

      // The grid needs the full title — it is the product page's headline and it
      // is what search reads. A summary line does not: six of those stacked is
      // most of a phone screen spent on words the shopper already knows.
      //
      // Tag a product short:Tees to set this properly. The guess below is a
      // convenience so the cart is readable before any tagging is done, and it
      // never overrides a tag.
      // Shortening the name took "(3 Pack)" out of the summary with it, and
      // people already arrive thinking a pack is assembled from singles. It
      // goes back in beside the colour and size, where it is read as a fact
      // about the line rather than part of a long title.
      lineDetail(tile, options) {
        const opts = options
          || Array.from(tile.querySelectorAll('[data-tb-option]')).map((s) => s.value);
        const bits = /3[\s-]?pack/i.test(tile.dataset.title || '') ? ['3-pack'] : [];
        return bits.concat(opts).join(' · ');
      }

      shortTitle(tile) {
        if (tile.dataset.short) return `TRAILBLAZER ${tile.dataset.short}`;
        const full = tile.dataset.title
          || tile.querySelector('.tb-card__title')?.textContent.trim() || '';
        return tbShortName(full);
      }

      currentVariant(tile) {
        const chosen = Array.from(tile.querySelectorAll('[data-tb-option]')).map((s) => s.value);
        return tile.variants.find((v) => v.options.every((o, i) => o === chosen[i]));
      }

      onOptionChange(tile, quiet) {
        // quiet = we set the dropdown ourselves, so do not echo it back out.
        if (!quiet) {
          this.rememberSize(tile);
          this.tiles.forEach((other) => {
            if (other !== tile) this.applySizes(other);
          });
        }
        this.paintTilePrice(tile);
        this.paintStock(tile);
        this.paintBuy(tile);
        // Picking White has to show the white one. Nobody reads a colour
        // label over a photograph that contradicts it — they believe the
        // photograph.
        this.paintGal(tile);
        // A different size/colour is a different line, so the tile's quantity
        // control has to follow the switch.
        this.render();
      }

      // Which photographs belong to what is currently selected.
      //
      // A product's media can be given to a variant in the product itself —
      // this black pack belongs to Black, this white one to White. That
      // assignment is the only thing used here: a picture claimed by SOME
      // variant but not this one is put away, a picture claimed by nobody is
      // shared by every colour and stays, and the one this variant owns leads.
      //
      // A product where nothing has been assigned is left exactly as it was,
      // so this can never make a shop worse than it is today.
      galList(tile) {
        const all = tile.images || [];
        if (all.length < 2) return all;
        const claimed = new Set();
        (tile.variants || []).forEach((v) => {
          if (v && v.media) claimed.add(String(v.media));
        });
        if (!claimed.size) return all;
        const variant = this.currentVariant(tile);
        const mine = variant && variant.media ? String(variant.media) : '';
        const keep = all.filter((m) => !claimed.has(String(m.id)) || String(m.id) === mine);
        if (!keep.length) return all;
        const lead = keep.findIndex((m) => String(m.id) === mine);
        if (lead > 0) keep.unshift(keep.splice(lead, 1)[0]);
        return keep;
      }

      paintGal(tile) {
        const gal = tile.gal;
        if (!gal) return;
        const shots = this.galList(tile);
        const sig = shots.map((m) => m.id + '|' + m.src).join(',');
        // Nothing to do when the same pictures are already up — rebuilding
        // would jump a half-swiped gallery back to the start for no reason.
        if (tile.galSig === sig) return;
        tile.galSig = sig;
        const rail = gal.querySelector('[data-tb-slides]');
        const dots = gal.querySelector('[data-tb-dots]');
        if (!rail || !dots) return;
        rail.innerHTML = shots.map((m, i) => '<div class="tb-focus__slide">'
          + this.shotTag(m.src, 'slide', i ? ' loading="lazy"' : '') + '</div>').join('');
        dots.innerHTML = shots.length > 1
          ? shots.map((m, i) => '<span class="tb-focus__dot'
            + (i === 0 ? ' is-on' : '') + '"></span>').join('')
          : '';
        rail.scrollLeft = 0;
      }

      // What a sold-out tile offers instead of a dead grey button.
      //
      // A gone size is not a gone sale. Black/L being out says nothing about
      // Black/M, and the shopper is standing right here with a pack half
      // built — so the loud option is the sizes that CAN ship today, one tap
      // each. Anything else is throwing away an order to collect a phone
      // number.
      paintSoldOut(tile, variant) {
        let row = tile.querySelector('[data-tb-oos]');
        const out = !variant || !variant.available;
        if (!out) {
          if (row) row.remove();
          return;
        }
        if (!row) {
          row = document.createElement('div');
          row.className = 'tb-oos';
          row.setAttribute('data-tb-oos', '');
          const button = tile.querySelector('[data-tb-add]');
          if (button && button.parentNode) button.parentNode.insertBefore(row, button.nextSibling);
          else tile.appendChild(row);
        }

        // Every chip spells out the whole combination. Naming only the part
        // that differs is shorter and reads as a riddle: "S", "M", "White"
        // side by side leaves you working out what White means. Nobody is
        // solving that mid-build.
        //
        // Nearest first — same colour, different size — because that is the
        // swap somebody actually makes.
        const chosen = Array.from(tile.querySelectorAll('[data-tb-option]')).map((s2) => s2.value);
        const others = (tile.variants || [])
          .filter((v) => v.available)
          .map((v) => ({
            v: v,
            gap: v.options.reduce((n, o, i) => n + (o === chosen[i] ? 0 : 1), 0),
          }))
          .sort((a, b) => a.gap - b.gap)
          .slice(0, 4);

        const chips = others.map((o) => `<button type="button" class="tb-oos__chip" `
          + `data-tb-swap="${o.v.options.join('|')}">${o.v.options.join(' · ')}</button>`).join('');

        this.fresh(row, others.length
          ? `<p class="tb-oos__lead">${chosen.join(' · ')} is out. In stock now:</p>`
            + `<div class="tb-oos__chips">${chips}</div>`
          : '<p class="tb-oos__lead">Out of stock in every size right now.</p>');
      }

      // Switching to one of those alternatives. The selects are the source of
      // truth for everything else on the tile, so setting them and replaying
      // the change keeps price, plans and the add button in step.
      swapTo(tile, options) {
        const selects = Array.from(tile.querySelectorAll('[data-tb-option]'));
        options.forEach((value, i) => {
          const sel = selects[i];
          if (sel && Array.from(sel.options).some((o) => o.value === value)) sel.value = value;
        });
        this.onOptionChange(tile);
      }

      // ---------------------------------------------------------------------
      // The guided flow.
      //
      // The grid asked a cold visitor to understand a system before it showed
      // them anything to want: four products, a meter at $0.00, three price
      // checkpoints and a counter reading 0 of 4, all above the fold. 1,100
      // visitors produced five add-to-carts.
      //
      // So the page opens on ONE product, laid out the way every shop lays out
      // a product, and the pack machinery stays out of sight until there is a
      // pack to describe. $0.00 and "0 of 4" say nothing on arrival; after the
      // first add the same two elements are progress, which is the opposite
      // feeling from the same pixels.
      // ---------------------------------------------------------------------
      setupFocus() {
        if (this.dataset.flow !== 'focus') return;
        this.grid = this.querySelector('.tb-bundle__grid');
        if (!this.grid || !this.tiles.length) return;
        this.flow = true;
        this.classList.add('tb-flow-on');
        // The master Subscribe / Just this once card asked the question the
        // product page now asks, at a point in the page where nobody has a
        // product in mind. Two places asking the same thing is where "halfway
        // down the page gets confusing" comes from.
        // Hidden, not removed: its radio is still how the page knows whether
        // anything in the pack repeats. Taking it out of the DOM took the
        // state with it and quietly unsubscribed everything.
        const master = this.querySelector('[data-tb-repeat]');
        if (master) master.classList.add('tb-repeat--silent');

        // The bar is fixed to the body, so it is outside this element and a
        // descendant rule can never reach it. Its second button is taken out
        // here: the bar has one job, and one button the width of the phone is
        // harder to miss with a thumb than either of two.
        // The pack screen is the end of the line, so it carries every move
        // that is left: back to adding, on to paying, or start again. Built
        // here rather than in the theme file so the whole of it — including
        // the confirm step — lives in one place.
        // Hung off the checkout button itself rather than off a wrapper with
        // a particular class name: a theme that renames or re-nests that
        // wrapper would otherwise silently lose all three of these.
        if (this.addButton && this.addButton.parentNode && !this.packActs) {
          const acts = document.createElement('div');
          acts.className = 'tb-pack__acts';
          const keep = document.createElement('button');
          keep.type = 'button';
          keep.className = 'tb-pack__keep';
          keep.textContent = 'Keep building';
          keep.addEventListener('click', () => this.showNext());
          this.addButton.insertAdjacentElement('beforebegin', acts);
          acts.appendChild(keep);
          acts.appendChild(this.addButton);
          this.packActs = acts;

          // Quiet, and two taps. Quiet because emptying a pack somebody spent
          // five minutes on is not a thing to put a big button next to; two
          // taps because there is no undo — the cart is the pack now, and
          // once those lines are gone they are gone.
          const bar = document.createElement('div');
          bar.className = 'tb-restart';
          bar.hidden = true;
          bar.innerHTML =
            '<button type="button" class="tb-restart__go" data-tb-restart>Restart pack</button>'
            + '<span class="tb-restart__ask" data-tb-restart-ask hidden>'
            + '<b>Clear the whole pack?</b>'
            + '<button type="button" class="tb-restart__yes" data-tb-restart-yes>Yes, start over</button>'
            + '<button type="button" class="tb-restart__no" data-tb-restart-no>Keep it</button>'
            + '</span>';
          acts.insertAdjacentElement('afterend', bar);
          this.restartBar = bar;

          bar.addEventListener('click', (event) => {
            if (event.target.closest('[data-tb-restart]')) { this.askRestart(true); return; }
            if (event.target.closest('[data-tb-restart-no]')) { this.askRestart(false); return; }
            if (event.target.closest('[data-tb-restart-yes]')) {
              this.askRestart(false);
              this.restartPack();
            }
          });
        }

        const dockSub = document.querySelector('[data-tb-dock-sub]');
        if (dockSub) dockSub.hidden = true;
        if (this.dock) this.dock.classList.add('tb-dock--one');
        // Said the way somebody would say it. "View pack" described the
        // mechanism; this names the moment — they have finished adding, and
        // this is the way out of adding. What is on the other side is their
        // pack: everything in it, what it costs, what repeats, and the button
        // that places the order.
        if (this.dockGo) this.dockGo.textContent = 'I\u2019m done building';

        const host = document.createElement('div');
        host.className = 'tb-flow';
        // The title and the line under it become a dark band across the
        // whole screen, and the pale page starts below them. Two reasons: it
        // separates the shop's promise from the shop itself, so the first
        // pale thing a visitor sees is the thing they are meant to do — and
        // on a phone the fold is short enough that a heading sharing a
        // background with the products reads as part of the products.
        //
        // Done from here rather than from the theme file so it only happens
        // on this layout, and so a shop that has cleared its heading is left
        // alone instead of being given an empty black box.
        const sect = this.closest('.tb-section');
        const head = sect && sect.querySelector('.tb-head');
        if (sect && head && head.textContent.trim()) {
          sect.classList.add('tb-flow-sect');
          head.classList.add('tb-head--invert');
        }

        // Four words, and only until one of them has been chosen. The old
        // line — "choose an essential to start with" — made the row look like
        // the first step of a sequence with three more to go. This one says
        // what to do and makes no promise about what comes after it.
        host.innerHTML = '<p class="tb-flow__lead" data-tb-lead>Choose one to start</p>'
          + '<div class="tb-flow__chips" data-tb-chips></div>'
          + '<div class="tb-next" data-tb-next hidden></div>';
        this.grid.parentNode.insertBefore(host, this.grid);
        this.chipRail = host.querySelector('[data-tb-chips]');
        this.leadEl = host.querySelector('[data-tb-lead]');
        this.nextEl = host.querySelector('[data-tb-next]');

        // "Complete your drawer" used to live here — one of each, named, with
        // an Add both button. It has been taken out on purpose. It invented a
        // finish line this shop does not have: the discount is earned by what
        // the pack is WORTH, so three tee 3-packs clears the same rung as one
        // of each, and a panel telling that customer what they are "missing"
        // is telling them they got it wrong. The meter already says what the
        // next rung costs, and it says it without naming a product.

        this.tiles.forEach((tile) => {
          const raw = tile.querySelector('[data-tb-images]');
          // Either shape: a plain list of addresses from an older paste, or
          // the current one that carries each picture's media id too.
          tile.images = (raw ? this.parse(raw.textContent, []) : [])
            .map((item) => (typeof item === 'string' ? { id: 0, src: item } : item))
            .filter((item) => item && item.src);
        });

        this.chipRail.addEventListener('click', (event) => {
          const chip = event.target.closest('[data-tb-chip]');
          if (chip) this.focusOn(chip.dataset.tbChip);
        });
        this.nextEl.addEventListener('click', (event) => {
          const card = event.target.closest('[data-tb-nextadd]');
          if (card) { this.focusOn(card.dataset.tbNextadd); return; }
          if (event.target.closest('[data-tb-viewpack]')) { this.goToPack(); return; }
          if (event.target.closest('[data-tb-nextcheckout]')) this.addAll();
        });

        // The meter pins itself under the header as the page scrolls, so the
        // progress is never more than a glance away. It has to know how tall
        // that header is, and only the page can say.
        // Where the meter should pin itself. Measured as the header's bottom
        // edge RIGHT NOW, not its height once at load: this theme's header
        // slides away as you scroll down and comes back as you scroll up, and
        // a meter pinned to where the header used to be is a white band
        // hanging in the middle of the page with nothing above it. Following
        // the real edge means the two are always flush, header or no header.
        const measureHead = () => {
          const head = document.querySelector('sticky-header, .header-wrapper, .section-header');
          let y = 0;
          if (head) {
            const how = getComputedStyle(head).position;
            if (how === 'fixed' || how === 'sticky') {
              y = Math.max(0, head.getBoundingClientRect().bottom);
            }
          }
          this.style.setProperty('--tb-stick', Math.round(y) + 'px');
        };
        measureHead();
        window.addEventListener('resize', measureHead);
        // The shop's header slides out of the way going down the page and
        // comes back coming up, and it does that on the SAME scroll event this
        // listens to — so whichever handler runs first wins, and ours was
        // reading where the header was a moment ago. When the page then stops,
        // nothing corrects it: the meter pins itself the height of a header
        // that is not there, and floats with a gap above it.
        //
        // The theme says exactly when it moves — it puts a class on and the
        // bar animates — so that is what is listened to, rather than guessing
        // from scroll position.
        const headEl = document.querySelector('sticky-header, .header-wrapper, .section-header');
        if (headEl) {
          headEl.addEventListener('transitionend', measureHead);
          if (typeof MutationObserver === 'function') {
            new MutationObserver(measureHead)
              .observe(headEl, { attributes: true, attributeFilter: ['class', 'style'] });
          }
        }

        // The phone's own back arrow, on a product, means the screen before
        // it — not the page before this one.
        window.addEventListener('popstate', () => {
          if (!this.flow || !this.nextEl) return;
          if (!this.nextEl.hidden || !this.lines.size) return;
          this.histIn = false;
          this.lastAdded = null;
          this.showNext();
        });

        // Once it is pinned the meter becomes a bar across the top of the
        // screen: square edges, full width, flush under the header.
        //
        // It is FIXED rather than sticky while it is up there. Sticky is the
        // tidier mechanism but it is fragile — any ancestor with a hidden
        // overflow quietly kills it, and a meter that silently stops following
        // is worse than one that is a little blunt about how it does it.
        // Fixed also gets the full width for nothing, with no tricks needed to
        // paint past the page's own margins.
        const meterEl = this.querySelector('[data-tb-meter]');
        if (meterEl) {
          // Asking the meter whether it is stuck, when being stuck changes its
          // height, is a loop: it shrinks, which un-sticks it, which grows it,
          // which sticks it again, and the page shivers. So the question is
          // put to a marker sitting just above it instead — nothing the meter
          // does can move that.
          const spy = document.createElement('i');
          spy.className = 'tb-meter__spy';
          spy.setAttribute('aria-hidden', 'true');
          meterEl.parentNode.insertBefore(spy, meterEl);
          // Anything that needs to know where the meter BELONGS, rather than
          // where it is currently pinned, asks the marker.
          meterEl.tbSpy = spy;
          let waiting = false;
          const markStuck = () => {
            waiting = false;
            measureHead();
            const stick = parseFloat(
              getComputedStyle(this).getPropertyValue('--tb-stick')
            ) || 0;
            const on = spy.getBoundingClientRect().top < stick - 1;
            if (on === Boolean(meterEl.tbStuck)) return;
            meterEl.tbStuck = on;
            if (on) {
              // The marker takes over the exact space the meter is about to
              // vacate, so nothing below it jumps when the bar lifts off.
              spy.style.height = meterEl.offsetHeight + 'px';
              meterEl.classList.add('is-stuck');
            } else {
              meterEl.classList.remove('is-stuck');
              spy.style.height = '';
            }
          };
          const soon = () => {
            if (waiting) return;
            waiting = true;
            if (typeof requestAnimationFrame === 'function') requestAnimationFrame(markStuck);
            else markStuck();
          };
          window.addEventListener('scroll', soon, { passive: true });
          const headNow = document.querySelector('sticky-header, .header-wrapper, .section-header');
          if (headNow) {
            headNow.addEventListener('transitionend', soon);
            if (typeof MutationObserver === 'function') {
              new MutationObserver(soon)
                .observe(headNow, { attributes: true, attributeFilter: ['class', 'style'] });
            }
          }
          markStuck();
        }

        this.focusOn(this.startHandle());
      }

      // Which product the page opens on. An ad can name it — ?start=boxers —
      // so a creative about boxers lands on boxers instead of making somebody
      // hunt for what they just watched. Matched loosely on purpose: the
      // handle a shop uses is rarely the word a person would type.
      startHandle() {
        let want = '';
        try {
          want = new URLSearchParams(window.location.search).get('start') || '';
        } catch (err) { want = ''; }
        // Noted, because an ad that names a product wants that product on
        // screen — not the pack they left behind last week.
        this.hadStart = Boolean(want);
        want = String(want || this.dataset.start || '').trim().toLowerCase();
        if (want) {
          const exact = this.tiles.find((t) => (t.dataset.handle || '').toLowerCase() === want);
          if (exact) return exact.dataset.handle;
          const named = this.tiles.find((t) => (t.dataset.short || '').toLowerCase() === want);
          if (named) return named.dataset.handle;
          const loose = this.tiles.find((t) => (t.dataset.handle || '').toLowerCase().indexOf(want) !== -1
            || (t.dataset.short || '').toLowerCase().indexOf(want) !== -1);
          if (loose) return loose.dataset.handle;
        }
        return this.tiles[0].dataset.handle;
      }

      focusOn(handle, quiet) {
        const tile = this.tiles.find((t) => t.dataset.handle === handle) || this.tiles[0];
        if (!tile) return;
        // Leaving the decision screen for a product is a move worth recording.
        // The phone's back arrow then comes back here instead of leaving the
        // shop altogether, which is what it did — there was nothing in the
        // history between arriving at this page and opening a product, so
        // "back" meant the page before Build Your Pack.
        const leavingNext = Boolean(this.nextEl && !this.nextEl.hidden);
        if (!quiet && leavingNext && !this.histIn) {
          try {
            window.history.pushState({ tbScreen: 'product' }, '');
            this.histIn = true;
          } catch (err) { /* a browser that will not have it changes nothing */ }
        }
        this.focusTile = tile;
        this.tiles.forEach((t) => { t.hidden = t !== tile; });
        this.grid.hidden = false;
        this.grid.classList.add('tb-bundle__grid--focus');
        this.dressTile(tile);
        this.syncSub(tile);
        this.paintStock(tile);
        this.paintBuy(tile);
        this.navOff = false;
        if (this.nextEl) this.nextEl.hidden = true;
        this.paintBack(tile);
        this.paintFlow();
      }

      // Once something is in the pack, a product page needs a way back to it.
      // Without one the only route is a scroll past the whole page, and the
      // pack is the screen where the order gets placed.
      paintBack(tile) {
        // A way back at the TOP of the product as well as the bottom. The one
        // under the Add button is a long way down a page somebody may not have
        // scrolled, and "the only way out is at the end" is not a way out.
        let top = tile.querySelector('[data-tb-backtop]');
        if (!this.lines.size) {
          if (top) top.remove();
        } else if (!top) {
          top = document.createElement('button');
          top.type = 'button';
          top.className = 'tb-focus__backtop';
          top.setAttribute('data-tb-backtop', '');
          top.innerHTML = '&larr; Back';
          tile.insertAdjacentElement('afterbegin', top);
          top.addEventListener('click', () => { this.lastAdded = null; this.showNext(); });
        }

        let bar = tile.querySelector('[data-tb-back]');
        if (!this.lines.size) { if (bar) bar.remove(); return; }
        if (bar) {
          // The count rides on the button, so "my pack" is a place with
          // something in it rather than a word somebody has to trust.
          const n = bar.querySelector('[data-tb-packn]');
          if (n) {
            let units = 0;
            this.lines.forEach((line) => { units += line.qty; });
            n.textContent = units;
          }
        }
        if (!bar) {
          bar = document.createElement('div');
          bar.className = 'tb-focus__nav';
          bar.setAttribute('data-tb-back', '');
          // Two ways back, because they are different places. One is the
          // screen that was just on show — what to add next — which until now
          // vanished the moment a product was opened, with nothing to bring it
          // back. The other is the pack itself.
          bar.innerHTML = '<button type="button" class="tb-focus__navb" data-tb-backnext>'
            + '&larr; Keep adding</button>'
            + '<button type="button" class="tb-focus__navb" data-tb-backpack>'
            + 'My pack <b data-tb-packn></b></button>';
          const add = tile.querySelector('[data-tb-add]');
          if (add) add.insertAdjacentElement('afterend', bar);
          bar.addEventListener('click', (event) => {
            if (event.target.closest('[data-tb-backpack]')) { this.goToPack(); return; }
            if (event.target.closest('[data-tb-backnext]')) {
              // Nothing was added on the way here, so the green "added" note
              // would be describing something that happened two screens ago.
              this.lastAdded = null;
              this.showNext();
            }
          });
        }
      }

      // Turns a grid card into something shaped like a product page. Done once
      // per tile, on demand, so three products nobody opens cost nothing.
      dressTile(tile) {
        if (tile.dressed) { this.syncPills(tile); return; }
        tile.dressed = true;
        this.armSizeAsk(tile);

        // Three things on this card were built for a grid of four and make a
        // product page worse.
        //
        // SUBSCRIBE -10% is a button, and tapping it put the shopper on a plan
        // they had not agreed to — a decision made for them. The page asks the
        // question properly a few centimetres lower down now.
        //
        // "Details & reviews" opened a pop-up over a page that IS this
        // product's own page. There is nothing left for it to reveal, and the
        // photo stops being a pop-up trigger for the same reason.
        tile.querySelectorAll('[data-tb-subchip], .tb-bundle__peek').forEach((el) => el.remove());
        tile.querySelectorAll('[data-tb-open]').forEach((el) => el.removeAttribute('data-tb-open'));

        // Name, rating and price go ABOVE the photo. On a phone the photo is
        // most of the first screen either way, so whatever sits above it is
        // the only thing a visitor reads without scrolling — and "what is this
        // and what does it cost" is a better use of that space than a picture
        // they can see the top of anyway.
        const media = tile.querySelector('.tb-bundle__media');
        const price = tile.querySelector('.tb-card__price');
        if (media && price && price.parentNode) {
          price.insertAdjacentElement('afterend', media);
        }
        // The pictures swipe. A row of thumbnails underneath spent a whole
        // band of page height doing what a sideways drag does for free, and a
        // phone shopper already knows to push a photo sideways. Dots say how
        // many there are so nobody has to guess whether it moves.
        if (media && tile.images && tile.images.length) {
          const gal = document.createElement('div');
          gal.className = 'tb-focus__gal';
          gal.innerHTML = '<div class="tb-focus__slides" data-tb-slides></div>'
            + '<div class="tb-focus__dots" data-tb-dots></div>';
          media.insertAdjacentElement('afterend', gal);
          // The count bubble and the "Back soon" flag were children of the
          // photo button. They move onto the gallery so nothing disappears
          // along with it.
          media.querySelectorAll('[data-tb-count], .tb-card__badge').forEach((badge) => {
            gal.insertAdjacentElement('afterbegin', badge);
          });
          media.hidden = true;
          tile.gal = gal;
          const rail = gal.querySelector('[data-tb-slides]');
          // Read fresh each time: the dots are rebuilt whenever the colour
          // changes, so a list captured once would be a list of elements that
          // are no longer on the page.
          rail.addEventListener('scroll', () => {
            const at = Math.round(rail.scrollLeft / (rail.clientWidth || 1));
            gal.querySelectorAll('.tb-focus__dot').forEach((dot, n) => {
              dot.classList.toggle('is-on', n === at);
            });
          }, { passive: true });
          this.paintGal(tile);
        }

        // Dropdowns become pills. A select hides its choices behind a tap and
        // gives no sense of how many there are; on a product page people
        // expect to SEE the sizes.
        tile.querySelectorAll('[data-tb-option]').forEach((sel) => {
          const wrap = sel.closest('.tb-bundle__opt') || sel.parentNode;
          const name = (wrap.dataset && wrap.dataset.tbOptionName) || '';
          const pills = document.createElement('div');
          pills.className = 'tb-pills';
          pills.innerHTML = (name ? '<span class="tb-pills__lab">' + name + '</span>' : '')
            + '<span class="tb-pills__row">'
            + Array.from(sel.options).map((o) => '<button type="button" class="tb-pill" data-tb-pill="'
              + String(o.value).replace(/"/g, '&quot;') + '">' + o.textContent + '</button>').join('')
            + '</span>';
          wrap.insertAdjacentElement('afterend', pills);
          wrap.hidden = true;
          pills.addEventListener('click', (event) => {
            const button = event.target.closest('[data-tb-pill]');
            if (!button) return;
            // Switching size or colour shows a different thing; it does not
            // rewrite what is already in the pack. Moving the line was the
            // wrong call: somebody with black/L in the pack who picks white/L
            // to add a SECOND pack had their black turned into a white, and
            // the builder told them they had two whites. A pack line is only
            // ever changed by the control that belongs to it.
            sel.value = button.dataset.tbPill;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
          });
          sel.tbPills = pills;
        });

        // Subscribe, said the way every shop says it. On the builder this
        // choice lived at the pack summary, where a first-time visitor never
        // reached it. On a product page it is the most familiar control there
        // is — one-time or repeat, next to the price — so it costs nothing to
        // understand and it gets asked while they are deciding.
        const addBtn = tile.querySelector('[data-tb-add]');
        if (addBtn) {
          // Said out loud, on the product, in the one place somebody is
          // looking. The pack list is real but it is below the fold, and a
          // shopper who has to go and find it to learn what they already
          // chose is a shopper being asked to do the page's job.
          const held = document.createElement('p');
          held.className = 'tb-focus__held';
          held.setAttribute('data-tb-held', '');
          held.hidden = true;
          addBtn.insertAdjacentElement('beforebegin', held);

          const qty = document.createElement('div');
          qty.className = 'tb-focus__qty';
          qty.innerHTML = '<span class="tb-pills__lab" data-tb-qtylab>Quantity</span>'
            + '<span class="tb-focus__qtybox">'
            + '<button type="button" data-tb-aq="-1" aria-label="One fewer">&minus;</button>'
            + '<b data-tb-aqn>1</b>'
            + '<button type="button" data-tb-aq="1" aria-label="One more">+</button></span>';
          addBtn.insertAdjacentElement('beforebegin', qty);
          tile.addQty = 1;
          qty.addEventListener('click', (event) => {
            const button = event.target.closest('[data-tb-aq]');
            if (!button) return;
            // Once this exact thing is in the pack, this IS the pack's
            // quantity. Pressing + here and finding the pack still saying 1 is
            // the page disagreeing with itself in two places at once.
            const live = this.packLine(tile);
            if (live) {
              this.step(live.id, Number(button.dataset.tbAq));
              return;
            }
            let n = Number(tile.addQty || 1) + Number(button.dataset.tbAq);
            if (n < 1) n = 1;
            if (n > 10) n = 10;
            tile.addQty = n;
            qty.querySelector('[data-tb-aqn]').textContent = String(n);
            if (Number(tile.subQty || 1) > n) {
              tile.subQty = n;
              const sn = tile.querySelector('[data-tb-sqn]');
              if (sn) sn.textContent = String(n);
            }
            this.paintBuy(tile);
          });
        }

        const add = tile.querySelector('[data-tb-add]');
        if (add && this.tileHasPlans(tile)) {
          const every = this.everyNames.length ? this.everyNames : [''];
          const sub = document.createElement('div');
          sub.className = 'tb-focus__sub';
          sub.innerHTML = '<label class="tb-focus__subopt is-on">'
            + '<input type="radio" name="tb-buy-' + tile.dataset.productId + '" value="once" checked>'
            + '<span><b>One time</b></span></label>'
            + '<label class="tb-focus__subopt">'
            + '<input type="radio" name="tb-buy-' + tile.dataset.productId + '" value="sub">'
            + '<span><b>Subscribe</b><i data-tb-subsave-tile></i></span></label>'
            + '<div class="tb-focus__every" data-tb-every hidden>'
            + '<div class="tb-focus__field">'
            + '<span class="tb-pills__lab">How many</span>'
            + '<span class="tb-focus__qtybox">'
            + '<button type="button" data-tb-sq="-1" aria-label="One fewer">&minus;</button>'
            + '<b data-tb-sqn>1</b>'
            + '<button type="button" data-tb-sq="1" aria-label="One more">+</button></span></div>'
            + '<div class="tb-focus__field tb-focus__field--wide">'
            + '<span class="tb-pills__lab">Choose how often</span>'
            + '<select data-tb-focus-every>'
            + every.map((n) => '<option value="' + n + '">' + n + '</option>').join('')
            + '</select></div></div>';
          add.insertAdjacentElement('beforebegin', sub);
          tile.subBox = sub;
          tile.subWant = false;
          tile.subEvery = this.defaultEvery;
          tile.subQty = 1;
          sub.addEventListener('click', (event) => {
            const step = event.target.closest('[data-tb-sq]');
            if (!step) return;
            // What repeats can never exceed what ships today — the first
            // delivery IS today's order, so raising one raises the other.
            let n = Number(tile.subQty || 1) + Number(step.dataset.tbSq);
            if (n < 1) n = 1;
            if (n > 10) n = 10;
            tile.subQty = n;
            sub.querySelector('[data-tb-sqn]').textContent = String(n);
            if (n > Number(tile.addQty || 1)) {
              tile.addQty = n;
              const an = tile.querySelector('[data-tb-aqn]');
              if (an) an.textContent = String(n);
            }
            this.applySub(tile);
            this.paintBuy(tile);
          });
          sub.addEventListener('change', (event) => {
            const every = sub.querySelector('[data-tb-focus-every]');
            if (event.target.matches('[data-tb-focus-every]')) {
              tile.subEvery = event.target.value;
            } else {
              tile.subWant = sub.querySelector('input[value="sub"]').checked;
              sub.querySelectorAll('.tb-focus__subopt').forEach((l) => {
                l.classList.toggle('is-on', l.contains(l.querySelector('input:checked')));
              });
              sub.querySelector('[data-tb-every]').hidden = !tile.subWant;
              if (every) tile.subEvery = every.value;
            }
            this.applySub(tile);
            this.paintBuy(tile);
          });
        }

        // Size guide sits under the size pills, because that is the second
        // somebody wants it. Everything else folds: open to whoever wants it,
        // one line tall to whoever does not.
        const extras = tile.querySelector('[data-tb-extras]');
        if (extras) {
          const guide = extras.querySelector('[data-tb-sg]');
          const groups = Array.from(tile.querySelectorAll('.tb-pills'));
          const sizes = groups.find((group) => {
            const lab = group.querySelector('.tb-pills__lab');
            return lab && /size/i.test(lab.textContent || '');
          }) || groups[groups.length - 1];
          if (guide && sizes) sizes.insertAdjacentElement('afterend', guide);
          const addEl = tile.querySelector('[data-tb-add]');
          if (addEl) addEl.insertAdjacentElement('afterend', extras);
          extras.hidden = false;
        }

        this.syncPills(tile);
        this.paintBuy(tile);
      }

      // The product page has to open showing what the pack already decided for
      // this item, or somebody comes back to a tile that says "One time" about
      // a line they subscribed a minute ago.
      syncSub(tile) {
        if (!tile || !tile.subBox) return;
        const line = this.packLine(tile);
        const on = Boolean(line && this.repeats(line));
        if (line) {
          tile.subWant = on;
          if (on) {
            tile.subEvery = this.everyFor(line);
            tile.subQty = this.repeatQtyFor(line);
          }
        }
        const pick = tile.subBox.querySelector('input[value="' + (tile.subWant ? 'sub' : 'once') + '"]');
        if (pick) pick.checked = true;
        tile.subBox.querySelectorAll('.tb-focus__subopt').forEach((l) => {
          l.classList.toggle('is-on', Boolean(l.querySelector('input:checked')));
        });
        const every = tile.subBox.querySelector('[data-tb-focus-every]');
        if (every && tile.subEvery) every.value = tile.subEvery;
        const sn = tile.subBox.querySelector('[data-tb-sqn]');
        if (sn) sn.textContent = String(tile.subQty || 1);
        const box = tile.subBox.querySelector('[data-tb-every]');
        if (box) box.hidden = !tile.subWant;
      }

      subscribeLine(key, every, howMany) {
        const id = String(key);
        if (howMany) this.repeatQty.set(id, Math.max(1, Math.min(10, Number(howMany))));
        if (!this.repeating()) {
          this.subOff.clear();
          this.lines.forEach((other) => {
            if (String(other.id) !== id) this.subOff.add(String(other.id));
          });
          const yes = this.repeatBox
            && this.repeatBox.querySelector('[data-tb-repeat-mode][value="repeat"]');
          if (yes) yes.checked = true;
        } else {
          this.subOff.delete(id);
        }
        // Entered through one item, so this pack is selective: later items
        // start off rather than being swept onto a plan nobody asked for.
        this.subAll = false;
        if (every) this.repeatEvery.set(id, every);
        tbTrack('resupply_mode', { on: true, from: 'product' });
      }

      tileHasPlans(tile) {
        return (tile.variants || []).some((v) => v.plans && Object.keys(v.plans).length);
      }

      // The button carries the price, because a button that only says what it
      // does makes you look back up to find out what it costs.
      paintBuy(tile) {
        if (!tile || !tile.dressed) return;
        const add = tile.querySelector('[data-tb-add]');
        const variant = this.currentVariant(tile);
        if (!add || !variant) return;
        if (!variant.available) return;
        const plan = tile.subWant ? this.planOn(variant, tile.subEvery) : null;
        const unit = plan ? plan.price : variant.price;
        const label = add.dataset.label || 'Add to pack';
        // In the pack already: the quantity box is showing the pack's number,
        // not a draft, and the button has nothing left to add.
        const live = this.lines.get(String(variant.id));
        const shown = live ? live.qty : Number(tile.addQty || 1);
        const box = tile.querySelector('[data-tb-aqn]');
        if (box) box.textContent = String(shown);

        // EVERY size and colour of this product that is in the pack, not just
        // the one currently on screen. Somebody holding a black large and a
        // white large was being told they had one white large, because the
        // page was only ever reporting what the pickers happened to be set to.
        //
        // Each one is a button: tapping it switches the page to that size and
        // colour, which is how you get at the quantity control for it. The
        // swap itself rides on the same handler the sold-out suggestions use.
        const held = tile.querySelector('[data-tb-held]');
        if (held) {
          const mine = [];
          this.lines.forEach((line) => {
            if (line.productId === tile.dataset.productId) mine.push(line);
          });
          let total = 0;
          mine.forEach((line) => { total += line.qty; });
          held.hidden = total === 0;
          if (total) {
            const rows = mine.map((line) => {
              const spec = (tile.variants || [])
                .find((v) => String(v.id) === String(line.id));
              const says = String(line.detail || '').replace(/^3-pack\s*·\s*/, '') || 'this one';
              const here = String(line.id) === String(variant.id);
              return '<button type="button" class="tb-held__one' + (here ? ' is-on' : '')
                + '"' + (spec ? ' data-tb-swap="' + spec.options.join('|') + '"' : '')
                + '>' + says + ' &times;' + line.qty + '</button>';
            }).join('');
            this.fresh(held, '<b>&#10003; ' + total + ' in your pack</b>'
              + '<span class="tb-held__rows">' + rows + '</span>');
          }
        }

        // With more than one in there, "how many in your pack" is ambiguous —
        // it has to name which one it is counting.
        const qlab = tile.querySelector('[data-tb-qtylab]');
        if (qlab) {
          let kinds = 0;
          this.lines.forEach((line) => {
            if (line.productId === tile.dataset.productId) kinds += 1;
          });
          if (!live) qlab.textContent = 'Quantity';
          else if (kinds > 1) {
            qlab.textContent = 'How many '
              + (String(live.detail || '').replace(/^3-pack\s*·\s*/, '') || 'of this one');
          } else qlab.textContent = 'How many in your pack';
        }

        add.classList.toggle('tb-bundle__add--in', Boolean(live));
        if (tile.sizeAsk) { /* the button is asking for a size; leave it asking */ }
        else if (live) add.textContent = 'Add another — ' + this.money(unit);
        else add.textContent = label + ' — ' + this.money(unit * shown);
        const tag = tile.querySelector('[data-tb-subsave-tile]');
        if (tag) {
          const off = variant.price > 0
            ? Math.round(((variant.price - (this.planOn(variant, tile.subEvery) || variant).price)
              / variant.price) * 100)
            : 0;
          tag.textContent = off > 0 ? 'Save ' + off + '%' : '';
        }
      }

      // What is and is not buyable right now: the button, the row of
      // alternatives under it, and the pickers.
      //
      // Called when a picker changes AND when a product is opened, because a
      // product whose first size happens to be gone was opening with a live
      // Add button until the shopper touched something.
      paintStock(tile) {
        if (!tile || !tile.dressed) return;
        const variant = this.currentVariant(tile);
        const out = !variant || !variant.available;
        const button = tile.querySelector('[data-tb-add]');
        if (button) {
          const ask = Boolean(tile.sizeAsk) && !this.allGone(tile);
          button.disabled = out || ask;
          if (ask) button.textContent = 'Choose a size';
          else button.textContent = out ? 'Sold out' : button.dataset.label || button.textContent;
        }
        tile.classList.toggle('tb-bundle__tile--unavailable', out);
        this.paintSoldOut(tile, variant);
        this.syncPills(tile);
      }

      // Which values in a picker can actually be bought, given everything
      // else that is currently chosen.
      //
      // Small in white being gone is a fact about small in white. It is not a
      // fact about the tank top, about black, about large, or about the price
      // — so it is said on the S button and nowhere else. Fading the whole
      // product for it tells somebody the range is finished when one square
      // of a grid is.
      // Only the pickers ABOVE this one are held to what is chosen. Colour
      // comes first on the page, so colour asks "is white gone in every size"
      // and size asks "is small gone in white" — which is the one that goes
      // grey when small white is out, and the order a person reads the page
      // in. Holding both ways round marks the colour as well, and a customer
      // reads that as "white is finished" when white is fine in medium.
      pillGone(tile, at, value) {
        const picks = Array.from(tile.querySelectorAll('[data-tb-option]'))
          .map((other) => other.value);
        return !(tile.variants || []).some((v) => {
          if (!v || !v.available) return false;
          const opts = v.options || [];
          if (String(opts[at]) !== String(value)) return false;
          for (let i = 0; i < at; i += 1) {
            if (String(opts[i]) !== String(picks[i])) return false;
          }
          return true;
        });
      }

      /* A size has to be chosen on purpose.

         The picker opens on whatever the shop lists first — Small — and a
         shopper who taps Add without looking gets a size nobody picked.
         That is a return, a refund, two shipping labels and a one-star
         review about sizing, and it is the shop's fault rather than theirs.
         So the size starts unchosen and the button says what it wants.

         Only ever on a first visit. The size this shopper used last time is
         restored before any of this runs, and asking again for something
         already answered is its own kind of rude — which is why the test
         below is "do we know their size", not "have they tapped yet". */
      sizePicker(tile) {
        return Array.from(tile.querySelectorAll('[data-tb-option]'))
          .find((sel) => this.optionName(sel).indexOf('size') !== -1) || null;
      }

      armSizeAsk(tile) {
        tile.sizeAsk = false;
        const sel = this.sizePicker(tile);
        if (!sel || sel.options.length < 2) return;
        const known = this.sizes && this.sizes[this.optionName(sel)];
        if (known) return;
        tile.sizeAsk = true;
      }

      // Anything the shopper changed themselves counts as answering, as long
      // as it was the size they changed. Picking a colour is not picking a
      // size, and must not quietly settle one for them.
      sizeTouched(tile, sel) {
        if (!tile.sizeAsk || !sel) return;
        if (sel !== this.sizePicker(tile)) return;
        tile.sizeAsk = false;
      }

      // Nothing in this product can be bought at all. Then "Sold out" is the
      // truth and asking for a size would be a runaround.
      allGone(tile) {
        return !(tile.variants || []).some((v) => v && v.available);
      }

      syncPills(tile) {
        if (!tile || !tile.dressed) return;
        tile.querySelectorAll('[data-tb-option]').forEach((sel) => {
          if (!sel.tbPills) return;
          const at = Number(sel.dataset.tbOption);
          sel.tbPills.querySelectorAll('[data-tb-pill]').forEach((button) => {
            const asking = Boolean(tile.sizeAsk) && sel === this.sizePicker(tile);
            button.classList.toggle('is-on', !asking && button.dataset.tbPill === sel.value);
            // Still pressable. A gone size that cannot be pressed cannot
            // explain itself, and what sits under the button — the sizes and
            // colours that CAN ship today — is the useful part.
            button.classList.toggle('is-gone', this.pillGone(tile, at, button.dataset.tbPill));
          });
        });
      }

      // The chips are the product navigation: tabs across a shop, not steps
      // through a checklist. Nothing here counts how many of the four have
      // been "covered" — three tee packs is a pack, and a page that keeps
      // saying "1 of 4" is telling that customer they are not finished when
      // they are. The badge on a chip is the QUANTITY in the pack, which is a
      // fact about what they chose rather than a score against a target.
      renderChips() {
        if (!this.chipRail) return;
        this.fresh(this.chipRail, this.tiles.map((tile) => {
          const qty = this.inPack(tile);
          const name = this.bareName(this.shortTitle(tile));
          // Nothing left to sell in any size or colour. This is the ONLY
          // reason a tab is allowed to look faded: a shop that dims the three
          // products you are not currently looking at is telling you three
          // quarters of the range is out of stock.
          const gone = !(tile.variants || []).some((v) => v.available);
          return '<button type="button" class="tb-chip' + (qty ? ' is-in' : '')
            + (gone ? ' is-out' : '')
            + (this.focusTile === tile ? ' is-on' : '') + '" data-tb-chip="' + tile.dataset.handle + '"'
            + (qty ? ' data-n="' + qty + '"' : '') + '>'
            + this.tileShot(tile) + '<span>' + name + '</span></button>';
        }).join(''));

        if (this.chipRail) this.chipRail.hidden = Boolean(this.navOff);
        // Gone the moment it has been answered. Telling somebody how to start
        // after they have started is words in the way.
        if (this.leadEl) this.leadEl.hidden = Boolean(this.navOff) || this.lines.size > 0;
      }

      // A tile that has never been on screen has a lazy image with no
      // currentSrc, which is why these came out as broken-image icons. The
      // gallery JSON is already parsed and always has a real URL in it.
      // A square thumbnail of a 4:5 photo crops from the middle, which on a
      // full-body shot takes the head off. Tag a product crop:25 and its
      // thumbnails frame 25% down instead — same size, better framing, set
      // per product because every photo sits differently.
      /* Ask for a picture at the size it will actually be shown.

         Every address in data-tb-images arrives asking for 900 pixels wide,
         whatever it is for. A chip thumbnail is 75 across on a phone, so
         that is six times the width and about thirty-six times the pixels,
         fetched on the first screen over whatever connection the visitor
         brought. Nothing here changes what is on screen; it changes how much
         is dragged down the wire to put it there.

         The address is handed over at several widths and the browser does
         the choosing, because it knows the screen, the pixel density and
         what the layout settled on, none of which this code does. An
         address the shop cannot resize is left exactly alone, so the worst
         case is what happens today.  */
      shotTag(src, kind, extra) {
        const SLOT = {
          chip:  { widths: [160, 200, 260, 320], sizes: '(min-width: 990px) 130px, 80px', plain: 200 },
          slide: { widths: [400, 600, 700, 900], sizes: '(min-width: 990px) 560px, 88vw', plain: 700 }
        };
        const slot = SLOT[kind];
        const tail = extra || '';
        if (!src || typeof src !== 'string') return '';
        if (!slot || !/[?&]width=\d+/.test(src)) {
          return '<img src="' + src + '" alt="" decoding="async"' + tail + '>';
        }
        const at = (w) => src.replace(/([?&]width=)\d+/, '$1' + w);
        const set = slot.widths.map((w) => at(w) + ' ' + w + 'w').join(', ');
        return '<img src="' + at(slot.plain) + '" srcset="' + set
          + '" sizes="' + slot.sizes + '" alt="" decoding="async"' + tail + '>';
      }

      tileShot(tile) {
        const img = tile.querySelector('.tb-bundle__media img');
        const src = (tile.images && tile.images[0] && tile.images[0].src)
          || (img && (img.currentSrc || img.getAttribute('src'))) || '';
        if (!src) return '';
        const crop = tile.dataset.crop || this.dataset.crop;
        const where = crop ? ' style="object-position:center ' + crop + '%"' : '';
        // Not deferred. These are four thumbnails of products already shown
        // on this page, so they are in hand — and an image that arrives after
        // the page has been scrolled moves everything under it, which lands
        // the screen a few pixels out of where it was aimed.
        return this.shotTag(src, 'chip', where);
      }

      // What this card should say it costs: the chosen size and colour if
      // there is one, otherwise the product's own listed price.
      tilePrice(tile, variant) {
        if (variant && Number.isFinite(variant.price)) return variant.price;
        const tag = tile.querySelector('[data-tb-price]');
        const cents = tag && tag.dataset ? Number(tag.dataset.cents) : 0;
        if (Number.isFinite(cents) && cents > 0) return cents;
        let least = 0;
        (tile.variants || []).forEach((v) => {
          if (Number.isFinite(v.price) && (!least || v.price < least)) least = v.price;
        });
        return least;
      }

      bareName(title) {
        return String(title || '').replace(/^\s*TRAILBLAZER\s+/i, '').trim();
      }

      // How many of this product are already in the pack. Asked in four places
      // now, so it stops being written out four times.
      inPack(tile) {
        let qty = 0;
        this.lines.forEach((line) => {
          if (line.productId === tile.dataset.productId) qty += line.qty;
        });
        return qty;
      }

      // What adding one of this product would do to the discount, said at the
      // moment the choice is being made rather than left for a meter to imply.
      nextUnlock(tile) {
        const variant = this.currentVariant(tile);
        if (!variant || !variant.available) return '';
        const totals = this.counts();
        const counts = this.eligibleAll
          || (tile.dataset.counts !== 'false' && this.eligible.has(Number(tile.dataset.productId)));
        const here = this.mode === 'value'
          ? totals.eligibleSubtotal + this.inBagValue
          : totals.packs + this.inBag;
        const added = this.mode === 'value'
          ? here + (counts ? variant.price : 0)
          : here + (counts ? 1 : 0);
        const now = this.tierFor(here);
        const then = this.tierFor(added);
        if (then && (!now || then.pct > now.pct)) return 'Unlocks ' + then.pct + '% off';
        const next = this.nextTier(added);
        if (!next) return '';
        const gap = next.at - added;
        if (gap <= 0) return '';
        const goal = next.kind === 'ship' || !next.pct ? 'free shipping' : next.pct + '% off';
        return this.mode === 'value'
          ? this.money(gap) + ' to ' + goal
          : gap + ' more to ' + goal;
      }

      paintFlow() {
        if (!this.flow) return;
        const has = this.lines.size > 0;

        // An empty pack has no "what do you want to add next" — the question
        // assumes there is something to add TO. Taking the last item out used
        // to leave that screen on display over nothing: no meter, no pack, and
        // four cards still quoting discounts for a pack that no longer exists.
        // Clearing the pack is somebody starting over, so the product goes
        // back on screen, which is where starting over begins.
        if (!has && this.nextEl && !this.nextEl.hidden && !this.resetting) {
          this.resetting = true;
          this.nextEl.hidden = true;
          this.lastAdded = null;
          this.focusOn((this.focusTile && this.focusTile.dataset.handle) || this.startHandle());
          this.resetting = false;
          return;
        }

        // The machinery arrives when it has something to report. At $0.00 with
        // nothing chosen it is furniture in the way of the product.
        const meter = this.querySelector('[data-tb-meter], .tb-meter');
        const summary = this.querySelector('.tb-bundle__summary');
        if (meter) meter.hidden = !has;
        if (summary) summary.hidden = !has;

        this.renderChips();
        if (!this.nextEl || this.nextEl.hidden) return;

        const just = this.lastAdded;
        const rest = this.tiles.filter((t) => t !== just).concat(just ? [just] : []);
        const cards = rest.map((tile) => {
          const variant = this.currentVariant(tile);
          const name = this.bareName(this.shortTitle(tile));
          const unlock = this.nextUnlock(tile);
          const have = this.inPack(tile);
          // A tile reading "Tees" next to three others reading the same thing
          // says nothing about which of them is already handled. The verb does
          // that work: Add tanks, Add more tees.
          const label = (have ? 'Add more ' : 'Add ') + name;

          // The badge says WHICH, not just how many.
          //
          // "1" on a tee card answers half a question: one of them, yes, but
          // one black medium or one white large? Where there is exactly one
          // kind of this product in the pack it can say so — "1 · M Black" —
          // and that is worth the width.
          //
          // Where there are two kinds it goes back to the plain number, on
          // purpose. Naming one of two would be wrong, and naming both would
          // not fit. A badge that is sometimes precise and sometimes a count
          // is honest; one that is sometimes precise and sometimes misleading
          // is not. The product itself lists every size and colour held, and
          // the pack lists them line by line.
          const mine = [];
          this.lines.forEach((line) => {
            if (line.productId === tile.dataset.productId) mine.push(line);
          });
          const lone = mine.length === 1
            ? String(mine[0].detail || '').replace(/^3-pack\s*·\s*/, '').replace(/\s*·\s*/g, ' ').trim()
            : '';
          const badge = lone
            ? '<span class="tb-next__tick is-wide" aria-hidden="true">'
              + mine[0].qty + ' &middot; ' + lone + '</span>'
            : (have ? '<span class="tb-next__tick" aria-hidden="true">' + have + '</span>' : '');

          // The whole tile is the button, and at two across there is room for
          // the photograph to be worth looking at.
          return '<button type="button" class="tb-next__card' + (have ? ' is-in' : '')
            + '" data-tb-nextadd="' + tile.dataset.handle + '">'
            + '<span class="tb-next__pic">' + this.tileShot(tile) + '</span>'
            + badge
            + '<span class="tb-next__name"><em>' + label
            + '</em><i aria-hidden="true">\u2192</i></span>'
            // A price, always. Where the size remembered from another product
            // is one THIS product does not sell, there is no variant to read a
            // price from — and the card came out as a bare name with nothing
            // under it. The product's own price is on the card already; it is
            // the right answer and it is always there.
            + '<span class="tb-next__meta">' + this.money(this.tilePrice(tile, variant))
            + (have && !lone ? ' &middot; ' + have + ' in pack' : '') + '</span>'
            + (unlock ? '<span class="tb-next__unlock">' + unlock + '</span>' : '')
            + '</button>';
        }).join('');

        const added = just ? this.bareName(this.shortTitle(just)) : '';
        this.fresh(this.nextEl,
          (added ? '<p class="tb-next__said">' + added + ' added to your pack</p>' : '')
          + '<div class="tb-next__ask">'
          + '<h3 class="tb-next__head">Choose what to add next</h3>'
          + '</div>'
          + '<div class="tb-next__cards">' + cards + '</div>');
      }

      showNext(still) {
        if (!this.flow || !this.nextEl) return;
        if (this.quiet) return;
        // Opening a product put an entry in the browser's history, so leaving
        // it takes that entry back off. Otherwise the two ways out of a
        // product — this button and the phone's own back arrow — would
        // disagree about where "back" is, and the history would fill up with
        // screens nobody can see.
        if (this.histIn) {
          this.histIn = false;
          try { window.history.back(); } catch (err) { /* carry on */ }
        }
        this.flushBag();
        this.grid.hidden = true;
        this.nextEl.hidden = false;
        // The row of little product pictures showed the same four things as
        // the tiles directly underneath it. Two rows of the same four, one of
        // them too small to read. On this screen the tiles ARE the navigation;
        // the row comes back on a product page, where it is the only way to
        // reach the other three.
        this.navOff = true;
        if (still) { this.paintFlow(); return; }
        this.paintFlow();
        // Land on the meter, not on the cards. From the meter down the whole
        // screen reads in order — how far along the pack is, then what is in
        // it, then what to add next — and scrolling to the cards cut the first
        // two off the top of the screen.
        const meter = this.querySelector('[data-tb-meter], .tb-meter');
        const top = meter && !meter.hidden ? meter : this.nextEl;
        // The bar at the foot goes up FIRST, so the screen is whole before the
        // page is moved to the top of it rather than finishing itself
        // afterwards.
        this.paintDock();
        this.goTop(top);
      }

      // scrollIntoView puts the element's top at the window's top, where the
      // shop's own sticky header is already sitting — so the thing scrolled to
      // lands UNDER it and reads as missing. The header is measured and
      // subtracted. The scroll waits a frame, because the cards being painted
      // above it change the distance.
      // Room is always left for the shop's header, so the header had better be
      // there. A theme that hides it on the way down leaves that room empty —
      // the same landing, looking like a gap one time and like the screen it
      // was designed as the next.
      //
      // Every one of these themes brings the header back the instant the page
      // moves UP, so the page is moved two pixels down and then back. Two
      // pixels, one frame apart: too small to see, large enough to be a scroll
      // in the direction that asks for the header.
      headPeek(y) {
        const head = document.querySelector('sticky-header, .header-wrapper, .section-header');
        if (!head) return;
        const how = getComputedStyle(head).position;
        if (how !== 'fixed' && how !== 'sticky') return;
        const tall = head.offsetHeight || 0;
        if (!tall) return;
        if (head.getBoundingClientRect().bottom > tall - 1) return;
        if (y <= 2) return;
        window.scrollTo({ top: y + 2, behavior: 'auto' });
        if (typeof requestAnimationFrame !== 'function') {
          window.scrollTo({ top: y, behavior: 'auto' });
          return;
        }
        requestAnimationFrame(() => requestAnimationFrame(() => {
          window.scrollTo({ top: y, behavior: 'auto' });
        }));
      }

      // Where a screen change should leave the page — worked out four
      // times, not once.
      //
      // Measuring once, on the frame the screen changes, is measuring a page
      // that has not finished happening: the shop's own header slides in and
      // out as the page moves and changes how much it covers, the meter pins
      // itself a frame later, the bar at the foot appears and changes how tall
      // the document is, and a picture arriving late moves everything under
      // it. Any one of those leaves the landing a little out, and the second
      // time round it is right because everything is already in place — which
      // is exactly the "hiccup that gets cleaner over time".
      //
      // So the sum is done again a fifth of a second later, again at half a
      // second and once more at nine tenths, from live positions each time,
      // and the page is nudged if it has drifted. Somebody who has started scrolling for themselves is left
      // alone: being hauled back is worse than landing a few pixels out.
      goTop(el) {
        if (!el) return;
        // Only a header that stays on screen is in the way. A header that
        // scrolls off with the rest of the page is not, and subtracting its
        // height anyway is how a landing ends up with a band of nothing at the
        // top of it.
        const headH = () => {
          const head = document.querySelector('sticky-header, .header-wrapper, .section-header');
          if (!head) return 0;
          const how = getComputedStyle(head).position;
          if (how !== 'fixed' && how !== 'sticky') return 0;
          return head.offsetHeight || 0;
        };

        const aim = () => {
          // A pinned element reports the position it is pinned AT, which says
          // nothing about where the page should be. Its marker stays in the
          // flow and is the honest answer.
          const spot = el.tbSpy || el;
          const loose = getComputedStyle(spot).position;
          const was = spot.style.position;
          if (loose === 'sticky' || loose === 'fixed') spot.style.position = 'static';
          const headNow = headH();
          const topOf = (node) => node.getBoundingClientRect().top + window.pageYOffset;
          const base = topOf(spot) - headNow - 8;
          if (loose === 'sticky' || loose === 'fixed') spot.style.position = was;
          // One place, every time.
          //
          // There used to be three: this one, and two lower down that gave up
          // the note and then the question so the bottom row of products could
          // clear the bar at the foot of the screen. Which of the three got
          // taken depended on heights that are still settling while the screen
          // is being painted — a picture arriving late, the bar being measured
          // before it is up — so the same tap landed in a different place on
          // the second go, and the page looked unsure of itself.
          //
          // The screen is built to fit from here down. This is where it lands.
          return Math.max(0, base);
        };

        let mine = -1;
        const go = () => {
          if (mine >= 0 && Math.abs(window.pageYOffset - mine) > 40) return;
          const y = aim();
          if (Math.abs(y - window.pageYOffset) < 2) { mine = window.pageYOffset; this.headPeek(mine); return; }
          window.scrollTo({ top: y, behavior: 'auto' });
          mine = y;
        };

        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() => requestAnimationFrame(go));
        } else go();
        setTimeout(go, 180);
        setTimeout(go, 460);
        // One more, late, for the header: a theme that hides its header on the
        // way down and shows it again on the way up is a different height a
        // moment after the page stops moving, and that moment is after the
        // other three.
        setTimeout(go, 900);
      }

      // The line in the pack for whatever this tile is currently showing, if
      // there is one. Everything that edits rather than adds hangs off this.
      packLine(tile) {
        const variant = this.currentVariant(tile);
        if (!variant) return null;
        return this.lines.get(String(variant.id)) || null;
      }

      // A subscription chosen on something already in the pack has to land on
      // the pack now. Making somebody press Add again to apply it is how a
      // choice gets made and then silently dropped.
      applySub(tile) {
        if (!this.flow) return;
        const line = this.packLine(tile);
        if (!line) return;
        const key = String(line.id);
        if (tile.subWant) this.subscribeLine(key, tile.subEvery, tile.subQty);
        else this.unsubscribeLine(key);
        this.render();
      }

      unsubscribeLine(key) {
        this.subOff.add(String(key));
        let any = false;
        this.lines.forEach((other) => {
          if (this.hasAnyPlan(other) && !this.subOff.has(String(other.id))) any = true;
        });
        if (!any) {
          const no = this.repeatBox
            && this.repeatBox.querySelector('[data-tb-repeat-mode][value="once"]');
          if (no) no.checked = true;
        }
      }

      add(tile) {
        const variant = this.currentVariant(tile);
        if (!variant || !variant.available) return;
        const key = String(variant.id);
        // Already in the pack: the button adds another one. It used to turn
        // into a way out of the screen, which left a product page with no Add
        // button on it at all — and a page with nothing to press reads as a
        // page that is finished with you. The number in the quantity box and
        // this button now move the same figure, so they cannot disagree.
        if (this.flow && this.lines.has(key)) {
          // And it leaves, the same as the first one did. Pressing Add
          // another and staying put makes the button look like it did
          // nothing — the quantity moved, but it moved below the fold — so
          // people press it again, and again. The way out of a product is
          // the same gesture whether it is the first of them or the fourth.
          this.step(variant.id, 1);
          this.lastAdded = tile;
          this.goAfter = key;
          this.goBackTo = tile;
          this.showNext();
          return;
        }
        const existing = this.lines.get(key);
        if (existing) {
          existing.qty += 1;
        } else {
          this.lines.set(key, {
            id: variant.id,
            qty: 1,
            price: variant.price,
            productId: tile.dataset.productId,
            counts: this.eligibleAll
              || (tile.dataset.counts !== 'false' && this.eligible.has(Number(tile.dataset.productId))),
            title: this.shortTitle(tile),
            detail: this.lineDetail(tile),
          });
          if (this.repeating() && this.subAll === false) {
          // A pack where some lines are switched off is a pack somebody is
          // subscribing SELECTIVELY — they came in through one item. Anything
          // added after that starts off, or picking "subscribe" on the tees
          // would quietly put the socks on a plan too. Where nothing is
          // switched off the whole pack is subscribed on purpose, and a new
          // item joining it is what was asked for.
            this.subOff.add(key);
          }
        }
        tile.classList.add('tb-bundle__tile--on');
        this.rememberSize(tile);
        this.savePack();
        tbTrack('add_to_pack', {
          variant_id: variant.id,
          product_title: tile.dataset.title || '',
          price: variant.price / 100,
          quantity: this.lines.get(key).qty,
          pack_lines: this.lines.size,
        });
        // The focus view asks how many BEFORE adding, the way a product page
        // does, so the rest of that quantity goes on now.
        const want = Number(tile.addQty || 1);
        if (want > 1) {
          this.step(key, want - 1);
          tile.addQty = 1;
          const n = tile.querySelector('[data-tb-aqn]');
          if (n) n.textContent = '1';
        }
        this.lastAdded = tile;
        // The choice was made on the product page, so it is already answered
        // by the time the line exists. Coming in through one item subscribes
        // that item and nothing else — the rest of the pack is untouched.
        if (tile.subWant) this.subscribeLine(key, tile.subEvery, tile.subQty);
        this.render();
        // The screen moves NOW. It does not wait for the cart.
        //
        // It used to, so that a refused add could keep somebody on the
        // product — and the price of that was a visible pause on the one
        // press that has to feel instant, every single time, to protect a
        // case that almost never happens: a sold-out size is already turned
        // away above, locally, before any of this. Paying a wait on every
        // press to be tidy about the rare one is the wrong way round.
        //
        // So the pack opens immediately and the write goes out behind it
        // (showNext sends whatever is pending at once). If the cart does
        // refuse, the note says why and they are put back on the product —
        // the correction costs a moment, and only in the case that earned it.
        this.goAfter = key;
        this.goBackTo = tile;
        this.showNext();
      }

      step(key, delta) {
        const line = this.lines.get(String(key));
        if (!line || !Number.isFinite(delta)) return;
        const next = line.qty + delta;
        if (!Number.isFinite(next) || next < 1) {
          this.lines.delete(String(key));
        } else {
          line.qty = next;
        }
        this.savePack();
        tbTrack(delta > 0 ? 'add_to_pack' : 'remove_from_pack', {
          variant_id: line.id,
          product_title: line.title,
          price: line.price / 100,
          quantity: Math.max(0, next),
          pack_lines: this.lines.size,
        });
        this.render();
      }

      counts() {
        let packs = 0;
        let subtotal = 0;
        let eligibleSubtotal = 0;
        this.lines.forEach((line) => {
          const amount = line.qty * line.price;
          subtotal += amount;
          if (line.counts) {
            packs += line.qty;
            eligibleSubtotal += amount;
          }
        });
        return { packs, subtotal, eligibleSubtotal };
      }

      // What the pack costs on the plan. Every line that HAS a plan switches to
      // its per-delivery price; a product with no plan stays at full price and
      // is simply bought once, so a mixed pack still works.
      //
      // The pack discount is only folded in when the theme setting says the
      // Shopify discount is set to cover subscriptions as well as one-time
      // orders. Left off, the page quotes the higher number and checkout can
      // only ever come in cheaper — the page is never the one making a promise
      // it cannot keep.
      // What the ladder is actually measured against once a subscription is on.
      //
      // Shopify takes the subscription off the lines FIRST, then decides which
      // rung the cart has reached from what is left. Three tee 3-packs are
      // $80.97 — the $80 rung — but subscribed they are $72.87, which is the
      // $50 rung. The page used to pick the rung from list prices and then
      // apply it to the discounted lines, so it promised a rung checkout was
      // never going to give.
      subEligible() {
        let eligible = 0;
        this.lines.forEach((line) => {
          if (!line.counts) return;
          const plan = this.repeats(line);
          const repeat = plan ? this.repeatQtyFor(line) : 0;
          const today = Math.max(line.qty, repeat);
          const once = today - repeat;
          eligible += once * line.price + repeat * (plan ? plan.price : 0);
        });
        return eligible;
      }

      subTotals(pct) {
        let subtotal = 0;
        let eligible = 0;
        let planned = 0;
        this.lines.forEach((line) => {
          const plan = this.repeats(line);
          // A subscription's first delivery IS today's order, so a line can be
          // part one-off and part repeating. Today's charge is both halves.
          const repeat = plan ? this.repeatQtyFor(line) : 0;
          const today = Math.max(line.qty, repeat);
          const once = today - repeat;
          const amount = once * line.price + repeat * (plan ? plan.price : 0);
          if (repeat > 0) planned += repeat;
          subtotal += amount;
          // Until Shopify is proved to give the pack discount on subscription
          // lines, only the part of the pack that is NOT repeating earns it.
          //
          // This used to zero the pack discount for the WHOLE pack the moment
          // one switch went on, which is why subscribing made the total jump:
          // one subscribed item cost the socks and the tanks their discount
          // too. Nothing in Shopify works that way — a one-time line is a
          // one-time line whatever the line above it is doing.
          if (!line.counts) return;
          if (this.stackDiscount) {
            eligible += amount;
          } else {
            eligible += once * line.price;
          }
        });
        const discount = Math.round((eligible * pct) / 100);
        return { subtotal: subtotal, discount: discount, after: subtotal - discount, planned: planned };
      }

      // The next rung down from this one, or null at the bottom.
      tierBelow(tier) {
        let below = null;
        this.tiers.forEach((t) => {
          if (t.at < tier.at) below = t;
        });
        return below;
      }

      tierFor(progress) {
        let hit = null;
        this.tiers.forEach((t) => {
          if (progress >= t.at) hit = t;
        });
        return hit;
      }

      nextTier(progress) {
        return this.tiers.find((t) => progress < t.at) || null;
      }

      render() {
        // Emptying the pack empties the subscription with it. Clearing a pack
        // is somebody starting over, and a builder that still said "Set up
        // subscription" over an empty pack was answering a question they had
        // already taken back. Only fires on the way from something to nothing,
        // so it can never fight a choice being made right now.
        if (this.lines.size) {
          this.hadLines = true;
        } else if (this.hadLines) {
          this.hadLines = false;
          this.clearSubPrefs();
        }

        const { packs, subtotal, eligibleSubtotal } = this.counts();
        const repeating = this.repeating();
        const ladderBase = repeating && !this.stackDiscount
          ? this.subEligible()
          : eligibleSubtotal;
        let counted = this.mode === 'value'
          ? ladderBase + this.inBagValue
          : packs + this.inBag;
        if (!Number.isFinite(counted)) counted = 0;
        this.counted = counted;
        let tier = this.tierFor(counted);

        // The rung the pack has EARNED is measured at list price, which is what
        // the meter shows and what the page promises. Shopify decides
        // separately, and it measures its own minimum after the subscription
        // has come off — so a cart that clears $80 here can arrive at $72.
        //
        // Where the Shopify minimum has been set lower to absorb that, the rung
        // survives and nothing below needs to change. Where it has not, the
        // rung is genuinely lost, and the page steps down to the one that will
        // actually be applied rather than quoting a saving checkout will
        // refuse. No guessing at a buffer: it compares the same two numbers
        // Shopify does.
        if (repeating && this.stackDiscount && this.mode === 'value' && tier) {
          const real = this.subEligible() + this.inBagValue;
          while (tier && real < (tier.min == null ? tier.at : tier.min)) {
            tier = this.tierBelow(tier);
          }
        }
        const next = this.nextTier(counted);
        const pct = tier ? tier.pct : 0;
        const discount = Math.round((eligibleSubtotal * pct) / 100);
        const after = subtotal - discount;

        // Everything downstream — the meter's free-shipping test, the summary,
        // the dock — spends the number the shopper is actually going to be
        // charged, which is a different number once the pack is on a plan.
        const rep = repeating;
        const sub = this.subTotals(pct);
        const eff = rep ? sub.after : after;
        this.paintRepeat(sub, after, subtotal);

        // meter
        const gotFree = Boolean(this.freeCents) && eff >= this.freeCents;
        const span = this.max > 0 ? this.max : 1;
        const width = Math.max(0, Math.min(100, this.fillPercent(counted)));

        // The fill is painted as the track's own background, not as a child
        // element. A background can't be collapsed by a layout quirk, so this
        // works regardless of what the surrounding theme does.
        const track = this.querySelector('.tb-meter__track');
        if (track) {
          // The fill colour is set here, inline, which means the stylesheet
          // can never win an argument with it — the reason the bar kept
          // filling black after the green rule was written. So the flow's
          // green is chosen at this line or nowhere.
          const tone = this.flow ? 'var(--tb-go, #2F9E63)' : 'var(--tb-ink, #14140F)';
          track.style.backgroundColor = 'var(--tb-sand, #E7E1D6)';
          track.style.backgroundImage = 'linear-gradient(' + tone + ', ' + tone + ')';
          track.style.backgroundRepeat = 'no-repeat';
          track.style.backgroundPosition = 'left center';
          track.style.backgroundSize = `${width}% 100%`;
          this.trackEl = track;
        }
        // The old child element, if the markup still has one, is left out of it.
        const legacyFill = this.querySelector('[data-tb-meter-fill]');
        if (legacyFill) legacyFill.style.display = 'none';

        // Whatever just changed, the cart hears about it. Compared against
        // the cart inside, so a render that changed nothing costs nothing.
        this.queueBag();
        this.paintWarn();

        this.writeDebug(counted, span);
        if (this.debug) {
          clearTimeout(this.debugTimer);
          this.debugTimer = setTimeout(() => this.writeDebug(counted, span), 700);
        }

        const stops = this.stops || [];
        this.querySelectorAll('[data-tb-stop]').forEach((el) => {
          // data-tb-stop reads back as dataset.tbStop, not dataset.stop. With
          // the wrong key every lookup came back undefined and the loop bailed
          // on the line below — which is why the checkpoint dots have never
          // once lit up as the bar passed them.
          const stop = stops[Number(el.dataset.tbStop)];
          if (!stop) return;
          el.classList.toggle('is-on', stop.kind === 'ship' ? gotFree : counted >= stop.at);
        });

        const nextStop = stops.find((stop) => (stop.kind === 'ship' ? !gotFree : counted < stop.at));
        const msg = this.querySelector('[data-tb-meter-msg]');
        if (msg && !this.lines.size && !this.inBag) {
          msg.innerHTML = this.startNote;
        } else if (msg && (this.lines.size || this.inBag)) {
          if (!nextStop) {
            // Short on purpose. This line shares a row with the badge, and
            // a row that cannot hold both becomes two rows — which makes the
            // meter taller on every screen for the rest of the visit.
            msg.innerHTML = 'Everything unlocked.';
          } else if (this.mode === 'value') {
            const gap = nextStop.kind === 'ship'
              ? this.freeToGo(counted, eff)
              : nextStop.at - counted;
            msg.innerHTML = `<strong>${this.money(Math.max(0, gap))}</strong> to <strong>${nextStop.label}</strong>`;
          } else {
            const need = Math.max(1, nextStop.at - counted);
            msg.innerHTML = `<strong>${need}</strong> more to <strong>${nextStop.label}</strong>`;
          }
        }

        const badge = this.querySelector('[data-tb-meter-badge]');
        if (badge) {
          const won = [];
          if (tier) won.push(`${tier.pct}% off`);
          if (gotFree) won.push('free shipping');
          badge.hidden = won.length === 0;
          // No "unlocked" on the end. A green badge beside a progress bar is
          // already saying that, and those eight letters are the difference
          // between this sitting on the same line as the total and taking a
          // line of its own — which makes the whole meter taller on every
          // screen, forever, to repeat a word.
          if (won.length) badge.textContent = won.join(' + ');
        }

        if (pct > this.lastPct || (gotFree && !this.lastFree)) this.celebrate(badge);
        if (pct > this.lastPct) {
          tbTrack('checkpoint_unlocked', {
            checkpoint: `${pct}% off`,
            percent_off: pct,
            pack_value: eligibleSubtotal / 100,
          });
        }
        if (gotFree && !this.lastFree) {
          tbTrack('checkpoint_unlocked', { checkpoint: 'Free shipping', pack_value: eff / 100 });
        }
        this.lastPct = pct;
        this.lastFree = gotFree;

        // lines
        if (this.list) {
          this.fresh(this.list, !this.lines.size
            ? '<li class="tb-bundle__empty">Nothing chosen yet.</li>'
            : Array.from(this.lines.values()).map((line) => this.lineRow(line)).join(''));
        }

        this.paintFlow();

        this.tiles.forEach((tile) => {
          let qty = 0;
          this.lines.forEach((line) => {
            if (line.productId === tile.dataset.productId) qty += line.qty;
          });
          this.paintTilePrice(tile);
          // The open product has to agree with the pack after every change:
          // the quantity box, the button, and which way the subscribe switch
          // is pointing all read off the line, not off a draft.
          if (this.flow && tile === this.focusTile) {
            this.syncSub(tile);
            this.paintBuy(tile);
            this.paintBack(tile);
          }
          const count = tile.querySelector('[data-tb-count]');
          if (count) {
            count.hidden = qty === 0;
            count.textContent = qty;
          }
          tile.classList.toggle('tb-bundle__tile--on', qty > 0);

          // The stepper shows the chosen variant, not the whole product: the
          // corner badge already carries the product total.
          const addButton = tile.querySelector('[data-tb-add]');
          if (tile.stepper && addButton) {
            const chosen = this.currentVariant(tile);
            const line = chosen ? this.lines.get(String(chosen.id)) : null;
            const mine = line ? line.qty : 0;
            // On the grid, the card swaps its Add button for a stepper once
            // the thing is in the pack. On a product page that is wrong: it
            // leaves the page with no button on it at all, and a page with
            // nothing to press reads as a page that is finished with you. The
            // product page keeps its button, which says "Add another" and
            // moves the same number the quantity box shows.
            if (this.flow) {
              tile.stepper.hidden = true;
              addButton.hidden = false;
            } else {
              tile.stepper.hidden = mine === 0;
              addButton.hidden = mine > 0;
            }
            const readout = tile.stepper.querySelector('[data-tb-tile-qty]');
            if (readout) readout.textContent = mine;
          }
        });

        // totals
        const totals = this.querySelector('[data-tb-totals]');
        if (totals) totals.hidden = !this.lines.size;
        const subEl = this.querySelector('[data-tb-sub]');
        if (subEl) subEl.textContent = this.money(subtotal);

        const saveRow = this.querySelector('[data-tb-save-row]');
        if (saveRow) {
          const shown = rep ? sub.discount : discount;
          saveRow.hidden = shown === 0;
          const label = this.querySelector('[data-tb-save-label]');
          if (label) label.textContent = `Pack discount (${pct}%)`;
          const save = this.querySelector('[data-tb-save]');
          if (save) save.textContent = `−${this.money(shown)}`;
        }

        const subRow = this.querySelector('[data-tb-subsave-row]');
        if (subRow) {
          const off = subtotal - sub.subtotal;
          subRow.hidden = !rep || off <= 0;
          const cell = this.querySelector('[data-tb-subsave]');
          if (cell) cell.textContent = `−${this.money(off)}`;

          // Name what the 10% actually comes off. Sitting bare as
          // "Subscription" in a column of pack-wide figures, it reads as 10%
          // off the order — which it is not. It is 10% off the items that
          // repeat, and the cheapest place to say so is right here, at the
          // number itself. Same item in two sizes is still one name.
          // Falls back to the row's own <dt> so this works against the markup
          // already on the page — no second file to re-paste for one attribute.
          const subLabel = subRow.querySelector('[data-tb-subsave-label]')
            || subRow.querySelector('dt');
          if (subLabel) {
            const names = [];
            this.lines.forEach((line) => {
              if (!this.repeats(line)) return;
              // "Subscription (TRAILBLAZER Tees, TRAILBLAZER Socks)" is a
              // paragraph. The brand is already all over the page; what this
              // needs to carry is which of the four things it is.
              const nm = String(line.title || '').replace(/^\s*TRAILBLAZER\s+/i, '').trim();
              if (nm && names.indexOf(nm) === -1) names.push(nm);
            });
            subLabel.textContent = names.length
              ? `Subscription (${names.join(', ')})`
              : 'Subscription';
          }
        }

        if (this.quick && !this.quick.hidden) this.paintQuick();

        if (this.dock) {
          const total = this.dock.querySelector('[data-tb-dock-total]');
          // Whose it is and what it comes to. No item count: "pack" was doing
          // two jobs at once — the whole order, and a 3-pack of tees — so "3
          // packs" could mean either and was wrong for one of them.
          if (total) total.innerHTML = '<i>Your pack</i> &middot; ' + this.money(eff);
          const note = this.dock.querySelector('[data-tb-dock-note]');
          if (note) note.remove();
          this.paintDock();
        }

        const shipRow = this.querySelector('[data-tb-ship-row]');
        if (shipRow) {
          shipRow.hidden = !this.freeCents || !this.lines.size;
          const ship = this.querySelector('[data-tb-ship]');
          if (ship) {
            if (eff >= this.freeCents) {
              // "Free" on its own left people asking "free what?" — the meter
              // says Free shipping, so the summary says it too.
              ship.textContent = 'Free shipping';
              ship.classList.add('tb-bundle__free');
            } else {
              ship.textContent = `${this.money(this.freeToGo(counted, eff))} away from free shipping`;
              ship.classList.remove('tb-bundle__free');
            }
          }
        }

        const totalEl = this.querySelector('[data-tb-total]');
        if (totalEl) totalEl.textContent = this.money(eff);

        if (this.addButton) {
          this.addButton.disabled = this.lines.size === 0;
          if (this.restartBar) {
            this.restartBar.hidden = this.lines.size === 0;
            if (!this.lines.size) this.askRestart(false);
          }
          this.addButton.classList.toggle('tb-bundle__ready', Boolean(tier));
        }
      }

      // ---- resupply: how often, asked once, applied per line -----------
      //
      // The builder is the landing page for paid traffic, so this is where
      // most people will ever meet the subscription. It is one choice at the
      // summary rather than a control on every tile, because four of them
      // would push the meter and the button off a phone screen.
      //
      // Per line, the repeat quantity is its OWN number and defaults to 1 —
      // never inherited from what they are buying today. A re-up is a top-up.
      wireRepeat() {
        this.repeatBox = this.querySelector('[data-tb-repeat]');
        if (!this.repeatBox) return;
        this.stackDiscount = this.dataset.stackDiscount === 'true';

        // A radio that is already checked fires no change event, so pressing
        // Subscribe while already subscribed did nothing at all — the exact
        // case where somebody wants everything switched back on. Click covers
        // it; change still covers keyboard.
        this.repeatBox.querySelectorAll('.tb-repeat__opt').forEach((el) => {
          el.addEventListener('click', () => {
            const input = el.querySelector('[data-tb-repeat-mode]');
            if (input) this.setRepeat(input.value === 'repeat');
          });
        });

        this.repeatBox.querySelectorAll('[data-tb-repeat-mode]').forEach((el) => {
          el.addEventListener('change', () => {
            // Choosing Subscribe on the card means subscribe, every time it is
            // pressed. It used to leave whatever items had been switched off
            // earlier still switched off, so the pack could say Subscribe with
            // half of it — or none of it — actually repeating.
            if (this.repeating()) { this.subOff.clear(); this.subAll = true; }
            tbTrack('resupply_mode', { on: this.repeating() });
            this.render();
          });
        });

      }

      // The card carries the numbers, so it has to be repainted like any other
      // total. It stays out of sight until there is a pack to put on repeat —
      // an empty summary quoting $0.00 a month is noise.
      paintRepeat(sub, onceAfter, listTotal) {
        const box = this.repeatBox;
        if (!box) return;

        box.hidden = this.lines.size === 0;
        if (!this.lines.size) return;

        // Held for the dock, which paints on its own schedule as the page
        // scrolls and has no access to these numbers otherwise.
        this.subOffPct = this.subRate();

        // The tag is the offer, not a verdict on the current pack — the same
        // standing fact as the words beside it. It states what subscribing
        // takes off the things that repeat, which is true whether or not
        // anything is switched on yet, so it is always on screen.
        //
        // Liquid prints it from a real plan price. This only sharpens it once
        // there is a pack to measure, and never blanks it.
        const tag = box.querySelector('[data-tb-rep-tag]');
        if (tag && this.repeating()) {
          const off = this.subRate();
          if (off > 0) tag.textContent = `Save ${off}%`;
        }
        void onceAfter;
        void listTotal;
        void sub;

        const note = box.querySelector('[data-tb-repeat-note]');
        if (note) {
          let able = 0;
          let on = 0;
          let units = 0;
          this.lines.forEach((line) => {
            if (!this.hasAnyPlan(line)) return;
            able += 1;
            if (this.repeats(line)) { on += 1; units += this.repeatQtyFor(line); }
          });
          // Four states, four sentences. The one on "Just this once" used to
          // describe the machinery — "10% off anything you keep coming, set
          // per item" — to somebody who had just said they did not want any
          // of it. It sells the offer in one line instead.
          if (!this.repeating()) {
            note.textContent = `Subscribe to any item and save ${this.subOffPct || 10}% on it. `
              + 'Skip or cancel any time.';
          } else if (able === 0) {
            note.textContent = 'None of these can be subscribed to yet.';
          } else if (on === 0) {
            note.textContent = 'Switch on the items you want sent again. '
              + 'You pick how many and how often for each one.';
          } else {
            const every = on === 1 ? 'item' : 'items';
            note.textContent = `${units} pack${units === 1 ? '' : 's'} coming back `
              + `from ${on} ${every}. Skip or cancel any time.`;
          }
        }
      }

      repeating() {
        const on = this.repeatBox?.querySelector('[data-tb-repeat-mode]:checked');
        return Boolean(on) && on.value === 'repeat';
      }

      // This product's own plan for the CADENCE on screen. Each product carries
      // its own plan ids even when the cadences are worded identically, so the
      // match is on "Every 3 months", not on an id borrowed from whichever
      // product happened to be first in the collection.
      //
      // Returns null when this product has no plan at that cadence, which is a
      // normal state — that item is simply bought once.
      // This variant's plan for the cadence on screen.
      planOn(variant, every) {
        if (!variant || !variant.plans || !every) return null;
        const map = this.cadences || {};
        const ids = Object.keys(variant.plans);
        const hit = ids.find((id) => map[id] === every);
        if (hit) return { id: hit, price: variant.plans[hit] };
        // Older markup put plan IDS in the picker rather than cadences. Match
        // those too, so a half-updated theme degrades to "works" rather than
        // to "nothing in this pack can repeat".
        if (variant.plans[every] != null) return { id: every, price: variant.plans[every] };
        // No map (or a cadence this product does not run) — one plan and one
        // cadence on offer is not ambiguous, so use it rather than telling the
        // shopper nothing can repeat.
        if (ids.length === 1 && Object.keys(map).length <= 1) {
          return { id: ids[0], price: variant.plans[ids[0]] };
        }
        // A cadence this product does not run. It still has a plan, so it can
        // still repeat — on the nearest thing it does offer.
        if (ids.length) return { id: ids[0], price: variant.plans[ids[0]] };
        return null;
      }

      // What the plan actually takes off, measured on the things that repeat.
      //
      // This used to divide the pack's total saving by the pack's whole list
      // value, which asks two different questions at once: one subscribed item
      // out of three reported "Save 6%" when the plan takes 10% off that item.
      // The pack is not on the offer — the subscribed lines are — so numerator
      // and denominator have to describe the same lines. With nothing switched
      // on there is nothing to measure and Liquid's printed rate, which comes
      // from a real plan price, is left alone.
      subRate() {
        let list = 0;
        let plan = 0;
        this.lines.forEach((line) => {
          const p = this.repeats(line);
          if (!p) return;
          const n = this.repeatQtyFor(line);
          list += n * line.price;
          plan += n * p.price;
        });
        if (list <= 0) return 0;
        return Math.round(((list - plan) / list) * 100);
      }

      repeats(line) {
        if (!this.repeating()) return null;
        if (this.subOff.has(String(line.id))) return null;
        return this.planFor(line);
      }

      // Starts at one. A re-up is a top-up: somebody buying three today is
      // stocking a drawer, not committing to three of the same thing forever,
      // and the number they were shown is the one they are agreeing to.
      repeatQtyFor(line) {
        const key = String(line.id);
        let n = this.repeatQty.has(key) ? this.repeatQty.get(key) : 1;
        if (n < 1) n = 1;
        if (n > 10) n = 10;
        return n;
      }

      // Each line keeps its own cadence. Socks every month and tees every three
      // is a real pack, and one picker at the top could not describe it.
      everyFor(line) {
        const key = String(line.id);
        return this.repeatEvery.has(key) ? this.repeatEvery.get(key) : this.defaultEvery;
      }

      planFor(line) {
        for (let i = 0; i < this.tiles.length; i += 1) {
          const variant = (this.tiles[i].variants || []).find((v) => String(v.id) === String(line.id));
          if (variant) return this.planOn(variant, this.everyFor(line));
        }
        return null;
      }

      repeatPrice(line) {
        const plan = this.planFor(line);
        return plan ? plan.price : null;
      }

      // The switch rides on the item's own line, on the right, because it is
      // a fact about that item — not a separate row asking a separate
      // question. Turning it on opens a second line underneath with that
      // item's own quantity and its own cadence, on the same rules as the
      // product page: the first delivery is today's order, so raising the
      // subscription raises today rather than being capped by it.
      lineRow(line) {
        const name = `<span class="tb-bundle__line-name">${line.title}${line.detail ? `<small>${line.detail}</small>` : ''}</span>`;
        const step = (dir, attr, variant, label) =>
          `<button type="button" ${attr}="${dir}" data-variant="${variant}" aria-label="${label}">${dir < 0 ? '&minus;' : '+'}</button>`;

        const qty = `<span class="tb-bundle__step">
            ${step(-1, 'data-tb-step', line.id, 'Remove one')}
            <b>${line.qty}</b>
            ${step(1, 'data-tb-step', line.id, 'Add one')}
          </span>`;
        const amount = `<span class="tb-bundle__line-amt">${this.money(line.qty * line.price)}</span>`;

        // The switch is on every line that COULD repeat, whichever way the
        // pack is set — because it is also the way in. Somebody on "Just this
        // once" who flips one item has said exactly what they want, and making
        // them find the master card first would be asking them to say it
        // twice.
        const key = String(line.id);
        const can = this.hasAnyPlan(line);
        const on = can && this.repeating() && !this.subOff.has(key);

        // The saving goes ON the switch. A toggle labelled only "Subscribe"
        // is asking somebody to commit to a repeat delivery for no stated
        // reason — the reason is 10% and it costs one word to say so.
        const swPlan = can ? this.planFor(line) : null;
        const swOff = swPlan && line.price > 0
          ? Math.round(((line.price - swPlan.price) / line.price) * 100)
          : 0;

        let sw = '';
        if (can) {
          sw = `<span class="tb-bundle__subsw">
            <span class="tb-bundle__subsw-lab">Subscribe${swOff > 0 ? `<em>Save ${swOff}%</em>` : ''}</span>
            <button type="button" class="tb-sw${on ? ' is-on' : ''}" role="switch"
              aria-checked="${on}" data-tb-line-sub="${line.id}"
              aria-label="Subscribe to ${line.title}"><i></i></button>
          </span>`;
        } else if (this.repeating()) {
          // Only worth saying while subscribing is on the table. A one-time
          // buyer does not need to be told which items have no plan.
          sw = '<span class="tb-bundle__subsw tb-bundle__subsw--none">No subscription</span>';
        }
        const main = `<div class="tb-bundle__line-main">${name}${qty}${amount}${sw}</div>`;

        if (!on) return `<li class="tb-bundle__line">${main}</li>`;

        const plan = this.planFor(line);
        const n = this.repeatQtyFor(line);
        const mine = this.everyFor(line);
        const options = this.everyNames
          .map((nm) => `<option value="${nm}"${nm === mine ? ' selected' : ''}>${nm}</option>`)
          .join('');

        const listPrice = this.money(n * line.price);
        const subPrice = plan ? this.money(n * plan.price) : '';
        const packs = `${n} pack${n === 1 ? '' : 's'}`;
        const said = (line.detail || '').replace(/^3-pack · ?/, '') || line.title;
        return `<li class="tb-bundle__line tb-bundle__line--on">${main}
          <div class="tb-bundle__subrow">
            <div class="tb-bundle__subrow-head">
              <span class="tb-bundle__subrow-lab">Subscription</span>
              <span class="tb-bundle__subprice">${
                plan ? `<s>${listPrice}</s> ${subPrice}` : '&mdash;'
              }</span>
            </div>
            <div class="tb-bundle__subrow-controls">
              <span class="tb-bundle__field">
                <span class="tb-bundle__field-lab">How many</span>
                <span class="tb-bundle__step tb-bundle__step--sub">
                  ${step(-1, 'data-tb-rep-step', key, 'One fewer on the subscription')}
                  <b>${n}</b>
                  ${step(1, 'data-tb-rep-step', key, 'One more on the subscription')}
                </span>
              </span>
              <span class="tb-bundle__field">
                <span class="tb-bundle__field-lab">Choose how often</span>
                <select class="tb-bundle__subevery" data-tb-line-every="${line.id}"
                  aria-label="How often ${line.title} comes back">${options}</select>
              </span>
            </div>
            <p class="tb-bundle__subnote">${packs} of ${said}, ${
              mine.toLowerCase()
            }. Skip or cancel any time.</p>
          </div>
        </li>`;
      }

      // Whether this product has a plan at ANY cadence. A line whose own
      // cadence is one this product does not run still belongs on screen with
      // its switch — it just needs a different frequency picked.
      hasAnyPlan(line) {
        for (let i = 0; i < this.tiles.length; i += 1) {
          const variant = (this.tiles[i].variants || []).find((v) => String(v.id) === String(line.id));
          if (variant) return Boolean(variant.plans && Object.keys(variant.plans).length);
        }
        return false;
      }

      // =====================================================================
      // There is one cart, and it is Shopify's.
      //
      // The pack panel used to be a second one: lines held here, written
      // across afterwards, the two compared and reconciled. Every way this
      // broke — a quantity that disagreed, a subscription that came back off,
      // a checkout holding something other than what the screen said — was
      // that comparison losing, and a comparison that loses quietly is the
      // worst kind: everything looks right until the money.
      //
      // Nothing is compared now. A tap writes to the cart, the cart is asked
      // what it now holds, and the panel is rebuilt from the answer. The
      // panel cannot show a quantity, a subscription or a total the cart does
      // not have, because it is never the one deciding — it follows.
      //
      // Written line by line, never by emptying and refilling: re-adding a
      // line that was already there fires a SECOND add-to-cart event, and the
      // ad platforms are reading those. A quantity change is a change.
      // =====================================================================

      // What the cart should hold, keyed so a one-off line and a repeating one
      // for the same product stay apart. Shopify keeps them as two lines, so
      // this has to as well.
      bagWants() {
        const want = new Map();
        this.lines.forEach((line) => {
          const plan = this.repeats(line);
          const repeat = plan ? this.repeatQtyFor(line) : 0;
          const today = Math.max(line.qty, repeat);
          const once = today - repeat;
          if (once > 0) want.set(line.id + ':once', { id: line.id, quantity: once });
          if (repeat > 0) {
            want.set(line.id + ':' + plan.id, {
              id: line.id, quantity: repeat, selling_plan: Number(plan.id),
            });
          }
        });
        return want;
      }

      bagKeyOf(item) {
        const plan = item.selling_plan_allocation
          && item.selling_plan_allocation.selling_plan
          && item.selling_plan_allocation.selling_plan.id;
        return item.variant_id + ':' + (plan || 'once');
      }

      // Is what is on screen something other than what the cart holds?
      // Measured against the CART — bagMap is what the cart itself said it
      // contains, the last time it was asked — and never against a remembered
      // note of what this page last sent. A remembered note goes stale the
      // instant the cart changes anywhere else, and from then on it blocks
      // every write for the rest of the visit while the two sides disagree in
      // silence. That was the bug.
      bagDiff() {
        const want = this.bagWants();
        const have = this.bagMap || new Map();
        if (want.size !== have.size) return true;
        let off = false;
        want.forEach((item, key) => {
          const row = have.get(key);
          if (!row || row.qty !== item.quantity) off = true;
        });
        return off;
      }

      // Called after every change, and cheap when nothing changed — render()
      // runs for plenty of reasons that are not a change to the pack.
      queueBag() {
        if (!this.bagReady) return;
        if (!this.bagDiff()) return;
        // One pending write covers every tap made before it starts, because
        // WHAT to send is worked out when it runs, not when it was asked for.
        // Six taps on a quantity is one change to the cart — which is also
        // how you avoid being throttled for hammering it.
        if (this.bagTimer) return;
        this.bagWait();
        this.bagTimer = setTimeout(() => { this.bagTimer = 0; this.syncBag(); }, 220);
      }

      // Said out loud, and gone the moment the next change lands. Silence
      // after a tap is the failure that loses the order: somebody presses Add
      // to pack, nothing appears, and the only conclusion available to them
      // is that the shop is broken.
      //
      // Pinned to the screen rather than placed in the pack panel, because
      // the panel is not always the thing being looked at and a notice nobody
      // sees is the same as no notice.
      paintWarn() {
        const say = this.bagWarn || '';
        let box = this.warnEl;
        if (!box) {
          if (!say) return;
          box = document.createElement('p');
          box.className = 'tb-warn';
          box.setAttribute('role', 'status');
          document.body.appendChild(box);
          this.warnEl = box;
        }
        if (box.textContent !== say && say) box.textContent = say;
        box.classList.toggle('is-on', Boolean(say));
        clearTimeout(this.warnTimer);
        if (say) {
          this.warnTimer = setTimeout(() => {
            this.bagWarn = '';
            if (this.warnEl) this.warnEl.classList.remove('is-on');
          }, 7000);
        }
      }

      askRestart(on) {
        if (!this.restartBar) return;
        const go = this.restartBar.querySelector('[data-tb-restart]');
        const ask = this.restartBar.querySelector('[data-tb-restart-ask]');
        if (go) go.hidden = Boolean(on);
        if (ask) ask.hidden = !on;
        this.restartBar.classList.toggle('is-asking', Boolean(on));
      }

      // Everything out, and back to the beginning: no lines, no subscription
      // choices, no discount, an empty meter and the first product on screen
      // the way it was on arrival. The cart is emptied of this pack with it —
      // there is one pack and it IS the cart, so a restart that left the cart
      // full would not be a restart.
      restartPack() {
        if (!this.lines.size || this.restarting) return;
        this.restarting = true;
        const had = this.lines.size;
        this.lines.clear();
        this.packOrder = [];
        this.subOff.clear();
        this.repeatQty.clear();
        this.repeatEvery.clear();
        this.clearSubPrefs();
        this.hadLines = false;
        this.lastAdded = null;
        this.bagWarn = '';
        this.bagStuck = 0;
        try { TB_STORE.drop(this.packKey); } catch (err) { /* nothing kept, nothing to drop */ }
        tbTrack('pack_restarted', { lines: had });
        this.render();
        // Sent outright, not "flushed if something happens to be pending".
        // flushBag only fires a write that was ALREADY queued, and whether one
        // was queued depends on timing nobody should have to think about — so
        // a restart could come down to whether a timer existed at that
        // instant. This asks for the write itself. A pack that still shows in
        // the bag icon after being cleared is the page disagreeing with itself
        // in the one place a shopper can always see.
        clearTimeout(this.bagTimer);
        this.bagTimer = 0;
        const loose = () => { this.restarting = false; };
        this.syncBag().then(loose, loose);
        this.navOff = false;
        this.histIn = false;
        this.focusOn(this.startHandle(), true);
        this.goTop(this.chipRail || this.grid);
      }

      // Checked after the fact, not waited on. They are already looking at
      // their pack; this only has something to do in the case where the cart
      // turned the item away.
      landAfter(carried) {
        const key = carried;
        if (!key) return;
        // Only the write that carried it gets to answer for it.
        if (this.goAfter !== key) return;
        this.goAfter = '';
        const back = this.goBackTo;
        this.goBackTo = null;
        let got = false;
        (this.bagMap || new Map()).forEach((row, had) => {
          if (String(had).indexOf(String(key) + ':') === 0) got = true;
        });
        if (got) return;
        // Refused. Put them back on the product with the reason, rather than
        // leaving them in front of a pack that does not contain the thing
        // they just pressed Add on.
        if (back && back.dataset && back.dataset.handle) this.focusOn(back.dataset.handle);
      }

      // Only when something is actually WRONG. A healthy write and read-back
      // is a few hundred milliseconds, and marking that is noise — a panel
      // that greys on every single tap reads as a page struggling, which is
      // the opposite of what this is for. Nothing is said until a change has
      // been in the air long enough that the silence would worry somebody.
      bagWait() {
        if (this.waitTimer) return;
        this.waitTimer = setTimeout(() => {
          this.waitTimer = 0;
          this.classList.add('tb-bag-busy');
        }, 1400);
      }

      bagDone() {
        clearTimeout(this.waitTimer);
        this.waitTimer = 0;
        this.classList.remove('tb-bag-busy');
      }

      // Write, then ask. Serialised, because two of these overlapping would
      // race the cart into a state neither of them wanted.
      //
      // Whether the write landed, was refused, was throttled, or the cart was
      // changed in another tab makes no difference to what happens next: the
      // cart is read back and the panel is redrawn from it. One answer to
      // every failure, which is why there is no longer a way for the two to
      // drift apart and stay that way.
      syncBag() {
        clearTimeout(this.bagTimer);
        this.bagTimer = 0;
        this.bagChain = (this.bagChain || Promise.resolve())
          .then(() => {
            // What is being sent, noted before it goes.
            this.bagSent = JSON.stringify(Array.from(this.bagWants().entries()));
            // And WHICH add this write is carrying, if any.
            //
            // The check for a refused add used to read whatever the page was
            // waiting on at the moment this link of the chain finished — not
            // at the moment the write was composed. Those are the same thing
            // right up until somebody taps while a write is already in the
            // air, which is exactly what happens after a restart: the restart
            // is still clearing the cart, they press Add on the tees, and the
            // restart's own turn then finishes, looks for the tees in a cart
            // it has just emptied on purpose, does not find them, and puts
            // them back on the product as though the shop had turned them
            // away. Press it again and it works, because the second time
            // nothing else is in flight. That is the "random rejection".
            this.landWant = this.goAfter || '';
            return this.pushBag();
          })
          .then(
            () => {
              this.bagNote = 'ok ' + new Date().toLocaleTimeString();
              this.bagFail = '';
              this.bagHard = false;
              this.bagWarn = '';
            },
            (err) => {
              this.bagNote = 'write failed: ' + (err && err.message ? err.message : err);
              const said = err && err.said ? err.said : '';
              // Noted, not announced. A write that failed is about to be sent
              // again, and most of the time the second one lands — telling
              // somebody their tap did not save and then quietly saving it is
              // worse than saying nothing for half a second.
              this.bagFail = said && said.length < 120
                ? said
                : 'That didn’t save. Give it a moment and tap again.';
              this.bagHard = Boolean(err && err.hard);
            }
          )
          // Before the read-back, not after it: the cart has the item at this
          // point and that is the whole question the screen is waiting on.
          // Guarded, because a tap that lands WHILE this is in the air must
          // not be undone by the answer to a question asked before it
          // happened. Press + twice in a second and the second press would
          // otherwise be wiped out by the cart confirming the first — the
          // number going backwards under somebody's thumb, which is worse
          // than any amount of lag. The test is made at the last possible
          // moment, inside the read, because that is when it is true.
          .then(() => {
            const had = this.freshCart;
            this.freshCart = null;
            return this.readBag(true, true, had);
          })
          .then(() => { this.bagSent = null; })
          .then(() => {
            this.bagDone();
            this.writeDebug(this.counted || 0, this.max || 1);
            // A removal the cart refused AND is still holding. Asked after
            // the read-back, because before it this was only a guess: a line
            // that was already gone refuses removal too, and that is a success
            // wearing the clothes of a failure.
            const missed = this.deadMiss;
            this.deadMiss = null;
            if (missed && missed.some((gone) => (this.bagMap || new Map()).has(gone))) {
              // In the shop's own words where it gave any. "Check your
              // connection" over a working connection is worse than saying
              // nothing: it sends somebody off to fix a thing that is not
              // broken, and it hides what is.
              this.bagWarn = this.bagWhy
                || 'That didn’t save. Give it a moment and tap again.';
              this.paintWarn();
            }
            this.bagWhy = '';
            const now = JSON.stringify(Array.from(this.bagWants().entries()));
            if (!this.bagDiff()) {
              this.bagStuck = 0;
              this.bagLast = now;
              // Agreeing with the cart is not the same as having got what
              // was asked for. When the write was refused, the agreement was
              // reached by the panel giving the item up — so if anything was
              // refused on the way here, it gets said.
              if (this.bagFail) { this.bagWarn = this.bagFail; this.paintWarn(); }
              this.bagFail = '';
              this.bagHard = false;
              this.landAfter(this.landWant);
              return;
            }
            // Still not what the cart holds: either a tap landed while that
            // was in the air, or the write did not take. Go again — but not
            // forever. Twice at the same unchanged thing is a disagreement
            // retrying will not settle, and a page that writes to the cart in
            // a loop is worse than one that stops and shows the truth.
            this.bagStuck = now === this.bagLast ? (this.bagStuck || 0) + 1 : 0;
            this.bagLast = now;
            if (this.bagStuck < 2 && !this.bagHard) {
              // Going again. Whatever was being waited on is still being
              // waited on, so nothing is said and nobody is sent anywhere.
              this.queueBag();
              return;
            }
            this.bagHard = false;
            this.bagNote = 'cart would not take this';
            // Given up. Now it is a real refusal: say why, in the shop's own
            // words, and put them back on the product it happened on.
            if (this.bagFail) { this.bagWarn = this.bagFail; this.bagFail = ''; this.paintWarn(); }
            this.landAfter(this.landWant);
          })
          // Last, and still inside the queue so it cannot collide with a real
          // change: the stamp the order tag reads.
          .then(() => {
            if (!this.wantStamp) return null;
            this.wantStamp = false;
            return this.bagPost('cart/update.js', {
              attributes: { 'Built with': TB_PACK_MARK },
            }).catch(() => null);
          });
        return this.bagChain;
      }

      // Shopify throttles the cart. Press + five times in a second and one of
      // the writes comes back 429 — and a write that is simply dropped leaves
      // the cart holding something other than what the page is showing, which
      // is the worst way for this to fail: everything looks right until
      // checkout. So a refusal waits and goes again, for as long as Shopify
      // says to wait.
      async bagPost(path, body, tries) {
        const go = tries || 0;
        let res;
        try {
          res = await fetch(`${window.Shopify.routes.root}${path}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
        } catch (err) {
          // The request never arrived — a tunnel dropping as a phone moves
          // between cells, a moment of no signal. That is the most ordinary
          // failure there is on a phone and it is not a reason to tell
          // somebody their tap did not save; it is a reason to send it again.
          if (go < 5) {
            await new Promise((done) => setTimeout(done, Math.min(400 * Math.pow(2, go), 4000)));
            return this.bagPost(path, body, go + 1);
          }
          throw err;
        }
        // Shopify limits how fast a cart can be written to, and a burst gets
        // 429 with a Retry-After for a second or two. Backing off three times
        // and then giving up was landing inside that window: the tap was
        // turned away, the shopper was put back on the product, and pressing
        // the same button a moment later worked — which is the "random
        // rejection". Five goes with a longer ceiling outlasts it.
        if ((res.status === 429 || res.status >= 500) && go < 5) {
          const said = Number(res.headers.get('Retry-After'));
          const hold = said > 0 ? said * 1000 : 400 * Math.pow(2, go);
          await new Promise((done) => setTimeout(done, Math.min(hold + Math.random() * 150, 4000)));
          return this.bagPost(path, body, go + 1);
        }
        if (!res.ok) {
          // Whatever it refused, it said why. Carried back in words rather
          // than as a number, because somebody has to be told — a tap that
          // does nothing at all is the one thing this must never do now that
          // the cart is the only thing holding the pack.
          let why = '';
          try {
            const body = await res.json();
            why = body && (body.description || body.message) ? (body.description || body.message) : '';
          } catch (e) { why = ''; }
          const stop = new Error(path + ' ' + res.status);
          stop.said = String(why || '').trim();
          stop.code = res.status;
          // A refusal with a reason — the last one sold while they were
          // deciding, a limit per order — is not going to come good on the
          // third attempt. Those are said at once. Everything else is treated
          // as the shop being busy and is sent again.
          stop.hard = res.status >= 400 && res.status < 500
            && res.status !== 429 && res.status !== 408;
          throw stop;
        }
        return res.json();
      }

      // Taking a line out, three ways, in order of politeness.
      //
      // A cart can refuse to remove a line by its own key — the key goes stale
      // when anything else touches the cart, an app can re-write the line, and
      // some carts simply answer "no valid id" to a key they handed out
      // themselves a moment ago. One refusal used to be the end of it, and the
      // page said the removal had failed over a pack that was still sitting
      // there. So: by key, then by position (read fresh, so it cannot be
      // stale), then by variant, which clears it however the cart is holding
      // it. Each attempt is CHECKED against the cart that comes back, because
      // an answer of "fine" that leaves the line in place is the worst of the
      // lot.
      // Several lines out in ONE write.
      //
      // A restart used to be one call per line, one after another, because
      // each had to be checked before the next went out — four items in the
      // pack meant four round trips to the cart and then a fifth to read it
      // back, which is the five or six seconds before the bag icon catches
      // up. The cart will take a whole list of quantities by position, so it
      // is read once, the pack's lines are set to nothing in place, and the
      // lot goes in a single write. Everything that is NOT this pack keeps
      // the quantity it already had, which is why this is done by position
      // rather than by variant: somebody who bought a tee the ordinary way
      // before building a pack does not lose it.
      async dropMany(rows) {
        const now = await this.cartRead();
        if (!now || !Array.isArray(now.items) || !now.items.length) return new Set();
        const going = new Set(rows.map((pair) => pair[0]));
        let touched = false;
        const quantities = now.items.map((item) => {
          const mine = (item.properties || {})._pack && going.has(this.bagKeyOf(item));
          if (mine) touched = true;
          return mine ? 0 : item.quantity;
        });
        if (!touched) return new Set();
        const cart = await this.bagPost('cart/update.js', { updates: quantities });
        this.freshCart = cart;
        const left = new Set();
        rows.forEach((pair) => { if (this.cartHas(cart, pair[0])) left.add(pair[0]); });
        return left;
      }

      async dropLine(row, want) {
        // The ladder below leaves the cart somewhere only the cart knows, so
        // whatever was being held as "the cart as of the last write" is no
        // longer safe to redraw from.
        this.freshCart = null;
        if (row && row.key) {
          try {
            const cart = await this.bagPost('cart/change.js', { id: String(row.key), quantity: 0 });
            if (!this.cartHas(cart, want)) return true;
          } catch (err) { this.bagWhy = this.whyOf(err); }
        }
        try {
          const now = await this.cartRead();
          const at = this.lineAt(now, want);
          if (at < 0) return true;
          const cart = await this.bagPost('cart/change.js', { line: at, quantity: 0 });
          if (!this.cartHas(cart, want)) return true;
        } catch (err) { this.bagWhy = this.whyOf(err); }
        try {
          const updates = {};
          updates[String(want).split(':')[0]] = 0;
          const cart = await this.bagPost('cart/update.js', { updates: updates });
          if (!this.cartHas(cart, want)) return true;
        } catch (err) { this.bagWhy = this.whyOf(err); }
        return false;
      }

      whyOf(err) {
        const said = err && err.said ? String(err.said).trim() : '';
        return said && said.length < 120 ? said : '';
      }

      async cartRead() {
        const res = await fetch(`${window.Shopify.routes.root}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        return res.json();
      }

      // Is this pack line still in there? Judged the same way the pack is
      // built, so the two can never disagree about what counts.
      cartHas(cart, want) {
        if (!cart || !Array.isArray(cart.items)) return true;
        return cart.items.some((item) => (item.properties || {})._pack
          && this.bagKeyOf(item) === want);
      }

      // Where it sits, counting from one, which is how the cart counts.
      lineAt(cart, want) {
        if (!cart || !Array.isArray(cart.items)) return -1;
        for (let i = 0; i < cart.items.length; i += 1) {
          const item = cart.items[i];
          if ((item.properties || {})._pack && this.bagKeyOf(item) === want) return i + 1;
        }
        return -1;
      }

      async pushBag() {
        this.bagBusy = true;
        try {
          await this.pushBagNow();
        } finally {
          this.bagBusy = false;
        }
      }

      async pushBagNow() {
        // Nothing carried over from the last write.
        this.freshCart = null;
        const want = this.bagWants();
        const have = this.bagMap || new Map();
        const adds = [];

        // Gone from the pack.
        //
        // A removal is the one write that cannot really fail. If the cart
        // refuses it because the line is not there — a stale key after the
        // cart was edited somewhere else, a line already taken out, a restart
        // hitting the same line twice — then the line is gone, which is what
        // was being asked for. Saying "that didn't save" to somebody who just
        // pressed Restart pack, over a pack that HAS been cleared, is the page
        // inventing a problem. The read-back straight after settles the truth
        // either way, so this stays quiet and lets it.
        const dead = [];
        have.forEach((row, key) => { if (!want.has(key)) dead.push([key, row]); });
        let stuck = dead;
        if (dead.length > 1) {
          try {
            const left = await this.dropMany(dead);
            stuck = dead.filter((pair) => left.has(pair[0]));
          } catch (err) {
            this.bagWhy = this.whyOf(err);
            stuck = dead;
          }
        }
        dead.forEach((pair) => have.delete(pair[0]));
        for (let i = 0; i < stuck.length; i += 1) {
          const key = stuck[i][0];
          const gone = await this.dropLine(stuck[i][1], key);
          if (!gone) {
            this.deadMiss = this.deadMiss || [];
            this.deadMiss.push(key);
          }
        }

        // Still there, different quantity. change.js, not add.js — this is an
        // edit, and it must not read as another add-to-cart.
        const keys = Array.from(want.keys());
        for (let i = 0; i < keys.length; i += 1) {
          const item = want.get(keys[i]);
          const row = have.get(keys[i]);
          if (!row) { adds.push(Object.assign({ properties: { _pack: TB_PACK_MARK } }, item)); continue; }
          if (row.qty === item.quantity) continue;
          this.freshCart = await this.bagPost('cart/change.js', { id: row.key, quantity: item.quantity });
          row.qty = item.quantity;
        }

        if (adds.length) {
          // add.js answers with the lines it made, not with the cart, so
          // whatever a previous step handed back is out of date now.
          this.freshCart = null;
          const data = await this.bagPost('cart/add.js', { items: adds });
          (data.items || []).forEach((item) => {
            have.set(this.bagKeyOf(item), { key: item.key, qty: item.quantity });
          });
        }

        this.bagMap = have;

        // The stamp the order tag is built on. Written once a session, after
        // there is something to tag — and NOT here, in front of the shopper.
        // It used to be awaited in the middle of the first add, which bought a
        // second round trip before the screen was allowed to move: a whole
        // extra wait, on the one press where waiting is most obvious, for a
        // cart attribute nobody is looking at. Noted now, sent at the end.
        if (have.size && !this.bagStamped) {
          this.bagStamped = true;
          this.wantStamp = true;
        }

        // The bag icon is refreshed once the cart has been read back, not
        // here as well: the theme's counter goes and asks the cart for itself,
        // so doing it in both places was a second round trip on every tap for
        // a number that was about to be set again anyway.
      }

      // The cart decides. This runs on arrival AND after every single write,
      // which is the whole of the new arrangement: whatever the panel is
      // showing is thrown away and built again out of the cart's own lines —
      // quantities, which items repeat, how often, and what today's half of a
      // subscription is. Nothing on screen outlives a disagreement with the
      // cart by more than one action.
      adoptBag(cart, quiet, keep) {
        const mine = (cart.items || []).filter((item) => (item.properties || {})._pack);
        this.bagMap = new Map();
        mine.forEach((item) => {
          this.bagMap.set(this.bagKeyOf(item), { key: item.key, qty: item.quantity });
        });

        // keep: the pack moved on while this answer was coming back, so the
        // answer is stale. The cart's line keys are still worth having — they
        // are what the next write is addressed to — but what is on screen is
        // newer than this and stays.
        if (keep) { this.bagReady = true; return; }

        if (!mine.length) {
          if (this.lines.size) {
            this.lines.clear();
            this.packOrder = [];
            this.subOff.clear();
            this.repeatQty.clear();
            TB_STORE.drop(this.packKey);
          }
          this.bagReady = true;
          return;
        }

        const had = this.lines.size;
        this.lines.clear();
        this.subOff.clear();
        this.repeatQty.clear();

        // One product can arrive as TWO cart lines — a repeating half and a
        // one-off half. So the plans are collected first and applied after
        // every line is known, or whichever half happened to come second
        // would be the one that decided.
        const plans = new Map();
        mine.forEach((item) => {
          const tile = this.tiles.find((t) => (t.variants || [])
            .some((v) => String(v.id) === String(item.variant_id)));
          if (!tile) return;
          const variant = (tile.variants || [])
            .find((v) => String(v.id) === String(item.variant_id));
          const key = String(variant.id);
          const line = this.lines.get(key);
          if (line) {
            line.qty += item.quantity;
          } else {
            this.lines.set(key, {
              id: variant.id,
              qty: item.quantity,
              price: variant.price,
              productId: tile.dataset.productId,
              counts: this.eligibleAll
                || (tile.dataset.counts !== 'false' && this.eligible.has(Number(tile.dataset.productId))),
              title: this.shortTitle(tile),
              detail: this.lineDetail(tile, variant.options),
            });
            // On arrival the pickers follow the pack, so the tile agrees
            // with it. Never afterwards: somebody who has just moved the size
            // picker to White Large in order to add a SECOND one is not
            // asking for it to be put back to the Black Large already in
            // their pack. That is where "it turned my black into a white"
            // came from.
            if (!quiet) {
              tile.querySelectorAll('[data-tb-option]').forEach((sel, index) => {
                if (variant.options[index] != null) sel.value = variant.options[index];
              });
            }
          }
          const plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
          if (plan) {
            plans.set(key, (this.cadences && this.cadences[plan.id]) || '');
            this.repeatQty.set(key, item.quantity);
          }
        });

        // The panel keeps the order the shopper built the pack in, whatever
        // order the cart hands the lines back in.
        //
        // Turning a subscription on is, underneath, the one-off line coming
        // out and a repeating line going in — a different line as far as the
        // cart is concerned. So the cart gives it back in a different place,
        // the panel is drawn from the cart, and the row the shopper just
        // touched jumps somewhere else on the list. Nothing moved as far as
        // they are concerned; they flicked a switch.
        this.lines = this.packOrdered(this.lines);

        let any = false;
        this.lines.forEach((line, key) => {
          if (plans.has(key)) {
            any = true;
            if (plans.get(key)) this.repeatEvery.set(key, plans.get(key));
          } else {
            this.subOff.add(key);
          }
        });
        const radio = this.repeatBox && this.repeatBox.querySelector(
          '[data-tb-repeat-mode][value="' + (any ? 'repeat' : 'once') + '"]'
        );
        if (radio) radio.checked = true;
        // Entered selectively unless literally everything in the pack repeats.
        this.subAll = any && this.subOff.size === 0;

        if (this.lines.size) this.savePack();
        if (!quiet && !had && this.lines.size) tbTrack('pack_restored', { lines: this.lines.size });

        this.bagReady = true;
      }


      // First seen, first listed. A line that genuinely leaves the pack drops
      // out of the running order, so if it is added again later it joins at
      // the bottom, which is where a new choice belongs.
      packOrdered(lines) {
        const seen = this.packOrder || [];
        const here = [];
        for (let i = 0; i < seen.length; i += 1) {
          if (lines.has(seen[i]) && here.indexOf(seen[i]) === -1) here.push(seen[i]);
        }
        lines.forEach((line, key) => { if (here.indexOf(key) === -1) here.push(key); });
        this.packOrder = here;
        const tidy = new Map();
        for (let i = 0; i < here.length; i += 1) tidy.set(here[i], lines.get(here[i]));
        return tidy;
      }

      async addAll() {
        if (!this.lines.size) return;
        const totals = this.counts();
        const earned = this.pctAt(this.mode === 'value'
          ? totals.eligibleSubtotal + this.inBagValue
          : totals.packs + this.inBag);
        let units = 0;
        this.lines.forEach((line) => { units += line.qty; });
        tbTrack('pack_added_to_cart', {
          pack_lines: this.lines.size,
          pack_units: units,
          value: totals.subtotal / 100,
          percent_off: earned,
        });

        // Nothing is added here any more. Every line went into the cart the
        // moment it was chosen, which is why the header count has been right
        // all along — so this is only making sure the last change landed
        // before the page is left behind.
        //
        // It also ends the bug this method used to carry: checkout APPENDED.
        // Build a pack, press Check out, change your mind, come back, rebuild,
        // press it again, and the bag held both packs. There is nothing to
        // append now; there is one pack, and it is the cart.
        const button = this.addButton;
        const label = button ? button.textContent : '';
        if (button) { button.disabled = true; button.textContent = 'One moment…'; }
        try {
          // Waited on, but not indefinitely. A slow or throttled cart must not
          // leave somebody staring at a dead button: four seconds is longer
          // than any of these writes needs, and past that the cart is far more
          // likely right than not.
          clearTimeout(this.bagTimer);
          this.bagTimer = 0;
          await Promise.race([
            this.syncBag(),
            new Promise((done) => setTimeout(done, 4000)),
          ]);
        } catch (err) {
          /* the bag holds what it holds; do not strand them here */
        }
        if (button) { button.disabled = false; button.textContent = label; }

        if (this.dataset.checkoutDirect === 'true') {
          window.location.href = `${window.Shopify.routes.root}checkout`;
          return;
        }

        // The drawer is worth opening rather than navigating to: it carries
        // the free shipping meter and the cross-sell rows, which on a builder
        // page are doing real work.
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function') {
          try {
            const data = await this.bagPost('cart/update.js', {
              attributes: { 'Built with': TB_PACK_MARK },
              sections: 'cart-drawer,cart-icon-bubble',
              sections_url: window.location.pathname,
            });
            if (data && data.sections) {
              drawer.renderContents(data);
              drawer.open?.();
              return;
            }
          } catch (err) {
            /* fall through to the cart page */
          }
        }
        window.location.href = `${window.Shopify.routes.root}cart`;
      }
    }
  );
}

// ---------------------------------------------------------------------------
// The pack line on a product page.
//
// It has to agree with the quantity picker sitting right above it. Someone who
// has already dialled the quantity to 2 does not want to be told to add a
// second pack — they added it — and the link must carry those 2 across, or the
// builder opens holding less than the product page was holding, which reads as
// the site losing their choice.
//
// Prices come from data attributes rendered by Liquid, so the arithmetic here
// is the same arithmetic the server did; only the quantity is live.
// ---------------------------------------------------------------------------
function tbPackCta() {
  var box = document.querySelector('[data-tb-packcta]');
  if (!box) return;
  var line = box.querySelector('[data-tb-packcta-line]');
  var go = box.querySelector('[data-tb-packcta-go]');
  if (!line || !go) return;

  var price = Number(box.dataset.price) || 0;
  var first = Number(box.dataset.first) || 0;
  var pct = Number(box.dataset.pct) || 0;
  var handle = box.dataset.handle || '';
  var url = box.dataset.url || '';
  if (!price || !first || !handle || !url) return;

  var money = function (cents) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: box.dataset.currency || 'USD',
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  // Dawn's stepper writes to this input and fires change; typing fires input.
  var field = document.querySelector('form[action*="/cart/add"] input[name="quantity"]')
    || document.querySelector('input[name="quantity"]');

  var paint = function () {
    var qty = 1;
    if (field) {
      qty = parseInt(field.value, 10);
      if (!Number.isFinite(qty) || qty < 1) qty = 1;
      if (qty > 10) qty = 10;
    }
    var total = price * qty;
    var text;
    if (total >= first) {
      text = qty > 1
        ? 'These ' + qty + ' already take <b>' + pct + '% off</b> the order.'
        : 'This one already takes <b>' + pct + '% off</b> the order.';
    } else if (qty === 1 && price * 2 >= first) {
      text = 'Two packs takes <b>' + pct + '% off</b> the whole order.';
    } else {
      text = '<b>' + money(first - total) + ' more</b> takes ' + pct + '% off the whole order.';
    }
    line.innerHTML = text + ' Mix any packs you like.';
    go.setAttribute('href', url + '?add=' + encodeURIComponent(handle) + ':' + qty);
  };

  if (field) {
    field.addEventListener('change', paint);
    field.addEventListener('input', paint);
  }
  paint();
}

if (!TB_BOOTED) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tbPackCta);
  } else {
    tbPackCta();
  }
}

// ---------------------------------------------------------------------------
// "How often" on a product page.
//
// The hard part is that one screen produces TWO cart lines: what they want
// today, and what they want to keep coming. A Shopify line either repeats or
// it does not, so "two packs today, one every six months" is 1 one-time line
// plus 1 subscription line. The maths is (today − repeating) and (repeating).
//
// Nothing here runs unless the block rendered, and the block only renders when
// the product actually has subscription plans attached.
// ---------------------------------------------------------------------------
// Shared with the cart, which has no tiles and no tags to read — only the
// title Shopify gives it.
function tbShortName(full) {
  const guesses = [
    [/boxer|brief/i, 'Boxer Briefs'],
    [/sock/i, 'Socks'],
    [/tank/i, 'Tanks'],
    [/tee|t-shirt|crew/i, 'Tees'],
  ];
  for (let i = 0; i < guesses.length; i += 1) {
    if (guesses[i][0].test(full)) return 'TRAILBLAZER ' + guesses[i][1];
  }
  return full;
}

// ---------------------------------------------------------------------------
// The Shopify bag, made to read like the pack.
//
// Almost nobody should end up here — the pack checks out on its own page. But
// the bag icon is in the header on every page, and somebody who taps it after
// building a pack should recognise what they are looking at rather than meet a
// different shop.
//
// It is a record, not a builder: the per-item subscription switches cannot
// exist here, because those choices are already made and committed. What it
// can do is use the same names, the same spacing and the same tag.
function tbCartNames(root) {
  (root || document).querySelectorAll('.cart-item__name').forEach(function (el) {
    if (el.dataset.tbShort === '1') return;
    var full = el.textContent.trim();
    var short = tbShortName(full);
    el.dataset.tbShort = '1';
    if (short === full) return;
    // The full name stays reachable — it is still the product's real title.
    if (!el.getAttribute('title')) el.setAttribute('title', full);
    el.textContent = short;
  });
}

function tbCartWatch() {
  tbCartNames(document);
  // Dawn re-renders the cart from the Section Rendering API on every quantity
  // change, which replaces these nodes wholesale. Watching is the only way to
  // keep up without hooking every one of its call sites.
  var spots = document.querySelectorAll('cart-drawer, .cart__items, cart-items, .cart__warnings');
  if (!spots.length || typeof MutationObserver !== 'function') return;
  var pending = null;
  var watcher = new MutationObserver(function () {
    clearTimeout(pending);
    pending = setTimeout(function () {
      tbCartNames(document);
    }, 60);
  });
  spots.forEach(function (spot) {
    watcher.observe(spot, { childList: true, subtree: true });
  });
}

if (!TB_BOOTED) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tbCartWatch);
  } else {
    tbCartWatch();
  }
}

function tbResupply() {
  var box = document.querySelector('[data-tb-resup]');
  if (!box) return;

  var form = document.getElementById(box.dataset.form);
  if (!form) return;

  var priceEl = box.querySelector('[data-tb-resup-price]');
  var qtyEl = box.querySelector('[data-tb-resup-qty]');
  var planEl = box.querySelector('[data-tb-resup-plan]');
  var stepper = box.querySelector('[data-tb-resup-stepper]');
  var modes = box.querySelectorAll('[data-tb-resup-mode]');

  var prices = {};
  try {
    prices = JSON.parse(box.querySelector('[data-tb-resup-data]').textContent);
  } catch (e) {
    return;
  }

  var repeatQty = 1;

  var money = function (cents) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  var currentVariant = function () {
    var field = form.querySelector('[name="id"]');
    return field ? String(field.value) : '';
  };

  // Dawn keeps the quantity input OUTSIDE the form and ties the two together
  // with a form="" attribute, so form.querySelector never finds it. It is in
  // form.elements, which is the list of controls the form will actually submit
  // — association, not nesting. Looking in the wrong place made today's
  // quantity read as 1 no matter what the pack cards said, so a 2-pack order
  // with a subscription lost the one-off half of it silently.
  var qtyField = function () {
    var el = form.elements ? form.elements.quantity : null;
    if (el && !el.tagName && el.length) el = el[0]; // more than one so named
    return el || document.querySelector('[name="quantity"][form="' + form.id + '"]');
  };

  var todayQty = function () {
    var field = qtyField();
    var n = field ? parseInt(field.value, 10) : 1;
    return Number.isFinite(n) && n > 0 ? n : 1;
  };

  // The select lists the group's plans; a variant only carries the ones it was
  // actually given an allocation for. Sending one it does not have gets the
  // whole add rejected, so fall back to a plan the variant really offers.
  var chosenPlan = function () {
    var row = prices[currentVariant()];
    var offered = row && row.plans ? Object.keys(row.plans) : [];
    var want = planEl ? String(planEl.value) : '';
    if (want && offered.indexOf(want) !== -1) return want;
    return offered[0] || want;
  };

  var isRepeat = function () {
    var on = box.querySelector('[data-tb-resup-mode]:checked');
    return !!on && on.value === 'repeat';
  };

  var setTodayQty = function (n) {
    var field = qtyField();
    if (!field || Number(field.value) === n) return;
    // Prefer the pack card, so the cards never show "One pack" while the
    // quantity underneath them says three. Ticking it sets the field itself.
    var tier = document.querySelector('.tb-tiers input[type="radio"][value="' + n + '"]');
    if (tier && !tier.checked) {
      tier.checked = true;
      tier.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    field.value = n;
    field.dispatchEvent(new Event('change', { bubbles: true }));
  };

  var paint = function () {
    // A subscription line's first delivery IS today's order, so the two
    // quantities cannot be independent. The dependency runs this way round:
    // raising the resupply raises what ships today, rather than the resupply
    // being blocked by a number the shopper has not reached yet.
    if (repeatQty < 1) repeatQty = 1;
    if (repeatQty > 10) repeatQty = 10;
    if (isRepeat() && todayQty() < repeatQty) setTodayQty(repeatQty);
    qtyEl.textContent = repeatQty;

    var row = prices[currentVariant()];
    var planId = chosenPlan();
    var wasEl = box.querySelector('[data-tb-resup-was]');
    // Liquid already printed a correct price for the first variant, so a miss
    // here leaves that standing rather than blanking it. Both numbers are for
    // the whole delivery, not one pack — the line underneath says "2 packs",
    // so a price for one pack beside it would simply be the wrong number.
    if (row && row.plans && row.plans[planId] != null) {
      priceEl.textContent = money(row.plans[planId] * repeatQty);
      if (wasEl) wasEl.textContent = money(row.base * repeatQty);
    }

    var what = box.querySelector('[data-tb-resup-what]');
    if (what && row && row.name) {
      var every = planEl && planEl.selectedOptions[0]
        ? planEl.selectedOptions[0].textContent.trim().toLowerCase()
        : 'every time';
      // When today's order is bigger than the repeat, say so in full. Two
      // lines go into the cart and the shopper should know that before the
      // cart tells them.
      var nowQty = Math.max(todayQty(), repeatQty);
      var lead = nowQty > repeatQty
        ? nowQty + (nowQty === 1 ? ' pack now, then ' : ' packs now, then ')
        : '';
      what.textContent = lead + repeatQty + (repeatQty === 1 ? ' pack of ' : ' packs of ')
        + row.name + ', ' + every + '. Skip or cancel any time.';
    }

    var repeating = isRepeat();
    box.classList.toggle('tb-resup--on', repeating);
    if (stepper) stepper.setAttribute('aria-disabled', repeating ? 'false' : 'true');
  };

  // Picking a frequency, or a number, IS choosing to subscribe — the same way
  // the stepper already worked. Greying the controls out until the radio is
  // ticked made the option look switched off at the very moment it is trying
  // to be noticed, and left the shopper a step to discover before the controls
  // would answer. Nothing is submitted as recurring unless this radio is on,
  // and it is visibly on once either control is touched.
  var choose = function () {
    var on = box.querySelector('[data-tb-resup-mode][value="repeat"]');
    if (on && !on.checked) on.checked = true;
  };

  // Outside the form, so a change there never bubbled to the form listener
  // below — the pack cards could move the quantity without this block noticing.
  var qf = qtyField();
  if (qf) qf.addEventListener('change', paint);

  modes.forEach(function (el) { el.addEventListener('change', paint); });
  if (planEl) planEl.addEventListener('change', function () { choose(); paint(); });
  form.addEventListener('change', paint);

  if (stepper) {
    stepper.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-tb-resup-step]');
      if (!btn) return;
      event.preventDefault();
      repeatQty += Number(btn.dataset.tbResupStep);
      choose();
      paint();
    });
  }

  // Captured on the document so this runs before the theme's own submit
  // handler and can take the add-to-cart over entirely when repeating.
  document.addEventListener('submit', function (event) {
    if (event.target !== form || !isRepeat()) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    var variant = currentVariant();
    if (!variant) return;
    var repeat = repeatQty;
    var today = Math.max(todayQty(), repeat);
    var once = today - repeat;

    var items = [];
    if (once > 0) items.push({ id: variant, quantity: once });
    var plan = chosenPlan();
    items.push({ id: variant, quantity: repeat, selling_plan: plan ? Number(plan) : undefined });

    var button = form.querySelector('[name="add"]');
    var label = button ? button.innerHTML : '';
    if (button) { button.setAttribute('aria-disabled', 'true'); button.classList.add('loading'); }

    tbTrack('resupply_added_to_cart', {
      variant_id: variant,
      today: today,
      repeating: repeat,
      selling_plan: plan || '',
    });

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({
        items: items,
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname,
      }),
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var drawer = document.querySelector('cart-drawer');
        if (drawer && data.sections && typeof drawer.renderContents === 'function') {
          drawer.renderContents(data);
          if (drawer.open) drawer.open();
        } else {
          window.location.href = window.Shopify.routes.root + 'cart';
        }
      })
      .catch(function () {
        window.location.href = window.Shopify.routes.root + 'cart';
      })
      .finally(function () {
        if (button) {
          button.removeAttribute('aria-disabled');
          button.classList.remove('loading');
          button.innerHTML = label;
        }
      });
  }, true);

  paint();
}

if (!TB_BOOTED) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tbResupply);
  } else {
    tbResupply();
  }
}

// ---------------------------------------------------------------------------
// Size guide.
//
// Delegated from the document so it works for the product page AND for the
// quick-view drawer, whose markup arrives after this script has already run.
//
// Uses a native <dialog>, which brings focus trapping, Escape to close and the
// top layer for free — inside the pack drawer that last part matters, because a
// hand-rolled overlay would be trapped under the drawer it was opened from.
// ---------------------------------------------------------------------------
function tbSizeGuide() {
  document.addEventListener('click', function (event) {
    var open = event.target.closest('[data-tb-sg-open]');
    if (open) {
      var dlg = document.getElementById(open.dataset.tbSgOpen);
      if (!dlg) return;
      event.preventDefault();
      tbTrack('size_guide_opened', { id: open.dataset.tbSgOpen });
      if (typeof dlg.showModal === 'function') {
        dlg.showModal();
      } else {
        dlg.setAttribute('open', '');
      }
      return;
    }

    var close = event.target.closest('[data-tb-sg-close]');
    if (close) {
      var owner = close.closest('dialog');
      if (owner) owner.close ? owner.close() : owner.removeAttribute('open');
      return;
    }

    // Clicking the backdrop. The panel fills the dialog, so a click that lands
    // on the dialog itself landed outside the panel.
    if (event.target.matches && event.target.matches('dialog.tb-sg')) {
      event.target.close ? event.target.close() : event.target.removeAttribute('open');
    }
  });
}

if (!TB_BOOTED) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tbSizeGuide);
  } else {
    tbSizeGuide();
  }
}

// ---------------------------------------------------------------------------
// The bag count, kept honest.
//
// Shopify renders the header's count bubble when the page is built. That is
// fine until the bag changes without a page build — which is exactly what the
// builder does, and exactly what Safari's back-forward cache reproduces when
// somebody backs out of checkout: the restored page is the one from before,
// showing an empty bag over a bag that is not empty.
//
// A shopper with no badge has no reason to believe anything is in there, and
// no way to find the one screen where it can be fixed. So the count is
// re-read on load and again whenever a cached page is restored.
function tbPaintBag(count) {
  var host = document.getElementById('cart-icon-bubble')
    || document.querySelector('.header__icon--cart');
  if (!host) return;
  var bubble = host.querySelector('.cart-count-bubble');
  if (count > 0) {
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      host.appendChild(bubble);
    }
    bubble.innerHTML = '<span aria-hidden="true">' + (count < 100 ? count : '') + '</span>'
      + '<span class="visually-hidden">' + count + ' items</span>';
  } else if (bubble) {
    bubble.remove();
  }
}

function tbSyncBag() {
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
    .then(function (res) { return res.json(); })
    .then(function (cart) { tbPaintBag(cart.item_count || 0); })
    .catch(function () { /* leave whatever the page rendered */ });
}

if (!TB_BOOTED) {
  // persisted = restored from the back-forward cache, so nothing re-rendered.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) tbSyncBag();
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tbSyncBag);
  } else {
    tbSyncBag();
  }
}

// ---------------------------------------------------------------------------
// "Continue shopping", on a cart built out of Build Your Pack, means back to
// Build Your Pack.
//
// Dawn points that link at /collections/all, which is the right default for a
// shop where the cart was filled from a product page. It is the wrong one here:
// somebody halfway through a pack who presses it is dropped into a product
// grid with no meter, no pack, and no sign of the thing they were building. The
// screen they want is the one that shows what they have and what is missing.
//
// Done here rather than in the cart template so there is no cart file to paste
// and keep in step with Dawn. The destination is the page the builder itself
// last ran on, remembered when the pack was saved, so it is right whatever the
// page is called — with the usual handle as the fallback.
var TB_BYP_FALLBACK = '/pages/build-your-pack';

function tbPackHome() {
  var saved = TB_STORE.read('tb-pack-v1');
  if (saved && typeof saved.url === 'string' && saved.url.indexOf('/') === 0) return saved.url;
  return TB_BYP_FALLBACK;
}

function tbFixCartLinks() {
  // Only where a pack is actually in play. A cart filled from a product page
  // keeps Dawn's own link, because for that shopper it is the better one.
  var saved = TB_STORE.read('tb-pack-v1');
  if (!saved || !Number(saved.units)) return;
  var home = tbPackHome();

  // On the cart PAGE the whole document is cart, so there is no scope to get
  // wrong. Elsewhere — a drawer on a product page, say — only the cart's own
  // corner of the page is touched. Naming Dawn's ids and nothing else was too
  // narrow: a theme that wraps them, renames them, or renders the drawer
  // somewhere else left the link exactly as it was, which is indistinguishable
  // from the fix not existing.
  var onCart = window.location.pathname.indexOf('/cart') !== -1;
  var scopes = onCart
    ? [document]
    : document.querySelectorAll('#main-cart-items, #main-cart-footer, cart-drawer, cart-notification, .cart__warnings, .cart__empty-text');
  for (var i = 0; i < scopes.length; i += 1) {
    var links = scopes[i].querySelectorAll('a[href]');
    for (var j = 0; j < links.length; j += 1) {
      var link = links[j];
      var href = link.getAttribute('href') || '';
      var says = (link.textContent || '').trim().toLowerCase();
      if (href.indexOf('/collections/all') === -1 && says.indexOf('continue shopping') === -1) continue;
      if (href === home) continue;
      link.setAttribute('href', home);
      link.setAttribute('data-tb-sent-home', '');
    }
  }
}

if (!TB_BOOTED) {
  // Run it at every point the page could be ready, because a deferred script,
  // a section rendered late and a drawer built on demand are three different
  // moments and only one of them is DOMContentLoaded.
  tbFixCartLinks();
  document.addEventListener('DOMContentLoaded', tbFixCartLinks);
  window.addEventListener('load', tbFixCartLinks);
  window.addEventListener('pageshow', tbFixCartLinks);
  setTimeout(tbFixCartLinks, 400);
  setTimeout(tbFixCartLinks, 1500);
  // Dawn re-renders the cart sections on every quantity change, which puts the
  // original link straight back. Cheap to simply do it again.
  // Watching the whole document is only reasonable where the cart lives. On
  // every other page of the shop it would be a listener running for nothing.
  if (typeof MutationObserver === 'function'
    && (window.location.pathname.indexOf('/cart') !== -1
      || document.querySelector('cart-drawer, #main-cart-items'))) {
    var tbCartWatch = new MutationObserver(function () {
      clearTimeout(window.tbCartLinkTimer);
      window.tbCartLinkTimer = setTimeout(tbFixCartLinks, 60);
    });
    tbCartWatch.observe(document.documentElement, { childList: true, subtree: true });
  }
}

/* =====================================================================
   Where the order came from, kept from the first visit to the checkout
   =====================================================================

   Shopify's own report answers "what was the last thing they touched",
   and for a shop whose traffic arrives from an ad and comes back three
   days later through a search for the brand, that report credits the
   search. The money was spent on the ad. So two things are kept, and
   kept apart: the FIRST touch, written once and never written again,
   and the LAST touch, rewritten every time somebody arrives from
   somewhere new.

   Both ride to the order as cart attributes, which come out in the Note
   Attributes column of an order export and show on the order in the
   admin. They are written while the shopper is still on the shop —
   never during checkout, where it is too late — because the cart holds
   its attributes from the moment they are set, through every add and
   removal, and hands them to the order at the end. That covers Build
   Your Pack, ordinary Add to cart, and Shop Pay or Apple Pay pressed on
   the cart.

   The one path it cannot cover is Shop Pay or Apple Pay pressed on a
   PRODUCT page: those build a checkout straight from the product form
   and never touch the cart, so there is nothing to carry. Those orders
   arrive with no attribution at all, which is itself the signal — an
   order with no _ft_source came in that way, and a Flow rule can tag it
   so it is never mistaken for genuine direct traffic. A compact copy is
   also pinned to the product form on the way past, on the chance that
   path carries line item properties; it costs nothing if it does not.

   Every name starts with an underscore. That is the convention a theme
   and a checkout use to mean "machinery, not for the customer", so none
   of this can end up printed on somebody's receipt — while the admin
   and the export still have all of it.
   ===================================================================== */
(function () {
  if (window.tbTrackOn) return;
  window.tbTrackOn = true;

  var FT = 'tb_ft';
  var LT = 'tb_lt';
  var YEAR = 60 * 60 * 24 * 365;

  // Two places, because either one on its own can be taken away. Safari
  // clears script-written storage after a week for somebody who arrived from
  // another site, some people browse with storage off, and a cookie is cleared
  // by anybody tidying up. Written to both, read from whichever still has it.
  function keep(name, value) {
    var text = JSON.stringify(value);
    try { window.localStorage.setItem(name, text); } catch (err) { /* storage off */ }
    try {
      document.cookie = name + '=' + encodeURIComponent(text)
        + ';path=/;max-age=' + YEAR + ';samesite=lax'
        + (window.location.protocol === 'https:' ? ';secure' : '');
    } catch (err) { /* cookies off */ }
  }

  function held(name) {
    var text = '';
    try { text = window.localStorage.getItem(name) || ''; } catch (err) { text = ''; }
    if (!text) {
      var hit = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      if (hit) { try { text = decodeURIComponent(hit[1]); } catch (err) { text = ''; } }
    }
    if (!text) return null;
    try {
      var got = JSON.parse(text);
      return got && typeof got === 'object' ? got : null;
    } catch (err) { return null; }
  }

  // Long enough to be useful, short enough that twenty of these cannot bloat
  // the cart. Control characters out, because these came off a URL somebody
  // else wrote.
  function tidy(value, cap) {
    if (value == null) return '';
    var text = String(value).replace(/[\u0000-\u001F\u007F]/g, ' ').trim();
    text = text.replace(/\s+/g, ' ');
    var stop = cap || 180;
    return text.length > stop ? text.slice(0, stop) : text;
  }

  function param(url, name) {
    try { return tidy(url.searchParams.get(name)); } catch (err) { return ''; }
  }

  function here() {
    try { return new URL(window.location.href); } catch (err) { return null; }
  }

  // Our own pages are not a referral. Neither is the checkout coming back, nor
  // the payment sheets handing the shopper back to us — treating those as a
  // new source is how a last touch ends up saying "shop.app".
  var OURS = /(^|\.)shopify\.com$|(^|\.)shop\.app$|(^|\.)shopifycdn\.com$|(^|\.)shopifypreview\.com$/i;
  function outside(host) {
    if (!host) return false;
    if (host === window.location.hostname) return false;
    // A subdomain of the same shop is still the same shop.
    var mine = window.location.hostname.split('.').slice(-2).join('.');
    var theirs = host.split('.').slice(-2).join('.');
    if (mine && mine === theirs) return false;
    return !OURS.test(host);
  }

  function referrer() {
    var raw = document.referrer || '';
    if (!raw) return { host: '', full: '' };
    try {
      var at = new URL(raw);
      if (!outside(at.hostname)) return { host: '', full: '' };
      return { host: tidy(at.hostname, 80), full: tidy(at.hostname + at.pathname, 180) };
    } catch (err) { return { host: '', full: '' }; }
  }

  // An untagged visit still says where it came from, and "google / referral"
  // in a report is a row somebody has to translate every time they read it.
  // Only the handful that actually send this shop traffic — everything else
  // is left as a referral, which is honest.
  var SEARCH = /^(google|bing|duckduckgo|yahoo|ecosia|search\.brave)\./i;
  var SOCIAL = /^(tiktok|facebook|instagram|pinterest|youtube|twitter|x|reddit|snapchat|threads|linkedin)\./i;
  var MAIL = /^(mail\.google|outlook|mail\.yahoo)\./i;
  function kind(host) {
    var at = host + '.';
    if (SEARCH.test(at)) return 'organic';
    if (SOCIAL.test(at)) return 'social';
    if (MAIL.test(at)) return 'email';
    return 'referral';
  }

  // What this particular arrival is carrying.
  function touch() {
    var url = here();
    if (!url) return null;
    var from = referrer();
    var got = {
      src: param(url, 'utm_source'),
      med: param(url, 'utm_medium'),
      cmp: param(url, 'utm_campaign'),
      cnt: param(url, 'utm_content'),
      trm: param(url, 'utm_term'),
      fbc: param(url, 'fbclid'),
      ttc: param(url, 'ttclid'),
      dt: param(url, 'dt_id'),
      ref: from.full,
      lp: tidy(url.pathname + (url.search || ''), 180),
      t: new Date().toISOString().slice(0, 19) + 'Z'
    };
    // Did THIS arrival carry anything, or is it just the next page of a visit
    // that is already being counted?
    got.tagged = Boolean(got.src || got.med || got.cmp || got.cnt || got.trm
      || got.fbc || got.ttc || got.dt);
    got.walked = Boolean(from.host);
    // A source nobody tagged. Better than "direct" for a shop whose ads do not
    // always carry utms: the network is in the referrer either way.
    if (!got.src) {
      if (got.ttc) { got.src = 'tiktok'; got.med = got.med || 'paid'; }
      else if (got.fbc) { got.src = 'meta'; got.med = got.med || 'paid'; }
      else if (got.dt) { got.src = 'creator'; got.med = got.med || 'collabs'; }
      else if (from.host) {
        got.src = from.host.replace(/^(www|m|l|lm|out|away)\./, '');
        got.med = got.med || kind(got.src);
      } else { got.src = 'direct'; got.med = got.med || 'none'; }
    }
    return got;
  }

  // A new arrival worth recording, as opposed to the next page of the same
  // visit. Anything tagged on the link counts; so does walking in from another
  // site. Clicking through the shop's own pages is neither, which is what
  // stops the last touch being overwritten with "direct" by the second page
  // somebody looks at.
  function fresh(got) {
    return Boolean(got && (got.tagged || got.walked));
  }

  // The two flags above answer one question each, at the moment the page
  // loads. They are no use a week later, so they are not what gets kept.
  function bare(got) {
    var out = {};
    var keys = ['src', 'med', 'cmp', 'cnt', 'trm', 'fbc', 'ttc', 'dt', 'ref', 'lp', 't'];
    for (var i = 0; i < keys.length; i += 1) {
      if (got[keys[i]]) out[keys[i]] = got[keys[i]];
    }
    return out;
  }

  var now = touch();
  var first = held(FT);
  var last = held(LT);

  if (now) {
    // Written once. Everything after this is a later visit, and a later visit
    // is not how they found the shop.
    if (!first) { first = bare(now); keep(FT, first); }
    // The most recent arrival from somewhere, kept separately so it can never
    // climb over the first.
    if (!last || fresh(now)) { last = bare(now); keep(LT, last); }
  }

  // ---- what goes on the order -------------------------------------------

  // Every field, every time — an empty one written as empty on purpose.
  //
  // Writing only the fields that have something in them looks tidier and is
  // wrong: the cart keeps what it was told last. Somebody who arrives on a
  // TikTok ad for the October tees and comes back a week later through Google
  // would have had a last touch reading "google / referral / october-tees",
  // because the campaign from the first visit was never cleared. A blank sent
  // as a blank removes it.
  function spread(into, prefix, got, full) {
    got = got || {};
    var pairs = [
      ['source', got.src], ['medium', got.med], ['campaign', got.cmp],
      ['content', got.cnt], ['term', got.trm],
      ['fbclid', got.fbc], ['ttclid', got.ttc], ['dt_id', got.dt],
      ['referrer', got.ref], ['date', got.t]
    ];
    if (full) pairs.push(['landing', got.lp]);
    for (var i = 0; i < pairs.length; i += 1) {
      into['_' + prefix + '_' + pairs[i][0]] = pairs[i][1] || '';
    }
  }

  function wanted() {
    var out = {};
    spread(out, 'ft', first, true);
    spread(out, 'lt', last, false);
    // Present on every order that came through the cart. An order WITHOUT it
    // was placed on a product page with Shop Pay or Apple Pay, which never
    // touches the cart — so a blank here is an answer, not a gap.
    out._attribution = (first || last) ? 'cart' : '';
    return out;
  }

  function same(mine, theirs) {
    theirs = theirs || {};
    for (var key in mine) {
      if (Object.prototype.hasOwnProperty.call(mine, key)
        && String(theirs[key] || '') !== String(mine[key])) return false;
    }
    return true;
  }

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Shopify slows a cart down that is written to in a burst, and the pack
  // builder on Build Your Pack is already writing to it. This waits its turn
  // rather than adding to the pile.
  function busy() {
    var builder = document.querySelector('tb-bundle-builder');
    return Boolean(builder && (builder.bagBusy || builder.bagTimer));
  }

  function send(body, keepalive) {
    return fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      keepalive: Boolean(keepalive)
    });
  }

  var put = wanted();
  var anything = false;
  for (var probe in put) {
    if (Object.prototype.hasOwnProperty.call(put, probe) && put[probe]) anything = true;
  }
  var done = false;

  function write(tries) {
    if (done) return;
    var go = tries || 0;
    if (busy() && go < 12) { setTimeout(function () { write(go + 1); }, 500); return; }
    fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        // Already saying the same thing. A write for nothing is a write that
        // can be throttled, and the thing being throttled might be an Add.
        if (same(put, cart && cart.attributes)) { done = true; return null; }
        return send({ attributes: put }).then(function (res) {
          if (res.ok) { done = true; return null; }
          if ((res.status === 429 || res.status >= 500) && go < 4) {
            setTimeout(function () { write(go + 1); }, 600 * Math.pow(2, go));
          }
          return null;
        });
      })
      .catch(function () {
        if (go < 4) setTimeout(function () { write(go + 1); }, 600 * Math.pow(2, go));
      });
  }

  // Early, so it is already on the cart long before anybody reaches for a
  // checkout button — a cart holds its attributes while it is still empty, so
  // there is no need to wait for a first item.
  if (anything) {
    if (document.readyState === 'complete') setTimeout(write, 300);
    else window.addEventListener('load', function () { setTimeout(write, 300); });
  }

  // Insurance, not the plan: one more go the moment somebody commits to
  // checking out, sent in a way that survives the page going away. If the
  // first write landed this does nothing at all.
  function onWayOut(event) {
    if (done || !anything) return;
    var el = event.target && event.target.closest
      ? event.target.closest('[name="checkout"],[href*="/checkout"],.cart__checkout-button,#checkout,[data-tb-bundle-add]')
      : null;
    if (!el) return;
    send({ attributes: put }, true).catch(function () { /* best effort */ });
  }
  document.addEventListener('click', onWayOut, true);
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.getAttribute) return;
    var action = form.getAttribute('action') || '';
    if (action.indexOf('/cart') === -1 && action.indexOf('/checkout') === -1) return;
    if (done || !anything) return;
    send({ attributes: put }, true).catch(function () { /* best effort */ });
  }, true);

  // The product-page express buttons never touch the cart, so the only thing
  // that can ride along with them is the product form itself. One compact
  // field, added only to a form that actually has one of those buttons, so an
  // ordinary Add to cart line stays clean. If that path does not carry line
  // item properties, nothing is lost by having tried.
  function pin() {
    if (!first) return;
    var line = [first.src, first.med, first.cmp, (first.t || '').slice(0, 10)]
      .filter(Boolean).join(' / ');
    if (!line) return;
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    for (var i = 0; i < forms.length; i += 1) {
      var form = forms[i];
      if (form.tbPinned) continue;
      if (!form.querySelector('shopify-accelerated-checkout, .shopify-payment-button')) continue;
      form.tbPinned = true;
      var field = document.createElement('input');
      field.type = 'hidden';
      field.name = 'properties[_ft]';
      field.value = tidy(line, 180);
      form.appendChild(field);
    }
  }
  if (document.readyState !== 'loading') pin();
  else document.addEventListener('DOMContentLoaded', pin);
  window.addEventListener('load', pin);

  // Readable from the console on the live shop, for checking it without
  // placing an order: tbWhereFrom()
  window.tbWhereFrom = function () {
    return { first: first, last: last, attributes: put };
  };
}());

/* =====================================================================
   Two events for Meta, fired on purpose
   =====================================================================

   Meta can be told to go and find people who look like the ones who
   bought. That is the strongest signal there is and the smallest pool
   to learn from. It can also be told to find people who will load a
   page, which is an enormous pool that proves nothing. Neither is the
   thing actually worth optimising towards, which is the moment a
   stranger stops looking and starts shopping.

   So: BYP_Interaction, once per session, the first time somebody does
   something on this page that a passer-by does not do — changes a size,
   opens the size guide, switches to tanks, reads the reviews, adds
   something. And BYP_CheckoutClick, once per session, when they press
   the button that takes them to checkout, which is a different and
   earlier fact than Meta's own InitiateCheckout.

   Both are sent with trackCustom. Nothing here touches PageView,
   AddToCart, InitiateCheckout or Purchase — the pixel and the Shopify
   channel keep doing exactly what they already do.

   Once per session, not once per action: the question being asked is
   "did this person become curious", and somebody who opens the size
   guide, changes size, switches tab and reads the reviews is one
   curious person, not four.
   ===================================================================== */
(function () {
  if (window.tbPixelOn) return;

  // Only where the builder is. The events are about this experience, and
  // firing them from a page that has no builder on it would be a lie.
  function wire() {
    if (window.tbPixelOn) return;
    if (!document.querySelector('tb-bundle-builder')) return;
    window.tbPixelOn = true;

    var SENT = 'tb_fb_';
    var waiting = [];

    function already(name) {
      try { return window.sessionStorage.getItem(SENT + name) === '1'; } catch (err) { return false; }
    }
    function remember(name) {
      try { window.sessionStorage.setItem(SENT + name, '1'); } catch (err) { /* storage off */ }
    }

    // The pixel may not have finished loading when somebody touches
    // something — fbq queues for itself once it exists, but it can be a
    // moment before it exists at all. Held and sent, rather than dropped.
    var tries = 0;
    function flush() {
      if (typeof window.fbq !== 'function') {
        tries += 1;
        if (tries < 40) setTimeout(flush, 250);
        return;
      }
      while (waiting.length) {
        var next = waiting.shift();
        try { window.fbq('trackCustom', next[0], next[1]); } catch (err) { /* never break the page */ }
      }
    }

    // Once. In memory AND in the tab's own storage, so a reload inside the
    // same visit does not count the same person twice.
    var fired = {};
    function once(name, data) {
      if (fired[name] || already(name)) return false;
      fired[name] = true;
      remember(name);
      waiting.push([name, data || {}]);
      flush();
      return true;
    }

    // ---- what counts as becoming curious ----------------------------
    //
    // Named one at a time rather than "any click inside the builder",
    // because the point is to exclude the things a passer-by also does.
    // Nothing here can be reached by scrolling, by waiting, or by the page
    // loading itself.
    var DOING = [
      ['.tb-chip', 'category'],                 // switched to tanks, socks, briefs
      ['[data-tb-nextadd]', 'category'],        // "add more tees" from the decision screen
      ['[data-tb-pill]', 'option'],             // size or colour
      ['[data-tb-swap]', 'option'],             // picked one of the in-stock alternatives
      ['.tb-focus__qtybox button', 'quantity'],
      ['[data-tb-step]', 'quantity'],
      ['[data-tb-tile-step]', 'quantity'],
      ['[data-tb-rep-step]', 'quantity'],
      ['[data-tb-add]', 'add'],
      ['[data-tb-quick-add]', 'add'],
      ['[data-tb-sg-open]', 'size_guide'],
      ['[data-tb-line-sub]', 'subscription'],   // the switch on a line in the pack
      ['.tb-focus__subopt', 'subscription'],    // one time / subscribe, on a product
      ['[data-tb-dock-sub]', 'subscription']
    ];

    function why(el) {
      for (var i = 0; i < DOING.length; i += 1) {
        if (el.closest(DOING[i][0])) return DOING[i][1];
      }
      return '';
    }

    document.addEventListener('click', function (event) {
      var el = event.target;
      if (!el || !el.closest) return;
      // The button that goes to checkout is its own event, and also counts
      // as having been curious — somebody who got that far plainly was,
      // and this is the one route to it that does not pass an Add.
      if (el.closest('[data-tb-bundle-add], [data-tb-nextcheckout]')) {
        once('BYP_Interaction', { trigger: 'checkout' });
        once('BYP_CheckoutClick', {});
        return;
      }
      var cause = why(el);
      if (cause) once('BYP_Interaction', { trigger: cause });
    }, true);

    // Size and colour as dropdowns rather than pills, the subscription
    // choice, and how often it repeats.
    document.addEventListener('change', function (event) {
      var el = event.target;
      if (!el || !el.closest) return;
      if (el.closest('[data-tb-option]')) { once('BYP_Interaction', { trigger: 'option' }); return; }
      if (el.closest('[data-tb-repeat-mode], [data-tb-line-every], [data-tb-focus-every], .tb-focus__subopt')) {
        once('BYP_Interaction', { trigger: 'subscription' });
      }
    }, true);

    // Description, reviews and the FAQ are <details>. Their toggle event
    // fires on the way open AND on the way shut, and shutting something
    // you already opened is not a new sign of interest — so only open
    // counts. toggle does not bubble, which is why this listens on the way
    // DOWN (capture) rather than waiting for it to come back up.
    document.addEventListener('toggle', function (event) {
      var el = event.target;
      if (!el || !el.open || !el.matches) return;
      if (el.matches('.tb-faq__row')) { once('BYP_Interaction', { trigger: 'faq' }); return; }
      if (!el.matches('.tb-fold')) return;
      var head = el.querySelector('summary');
      var said = head ? head.textContent.toLowerCase() : '';
      once('BYP_Interaction', { trigger: said.indexOf('review') !== -1 ? 'reviews' : 'description' });
    }, true);

    /* ------------------------------------------------------------------
       Volume, depth, and the moment shopping starts.

       Everything above this line is left exactly as it was. BYP_Interaction
       still answers "did this visitor ever engage" and fires once; the five
       events below answer different questions and are wired on their own
       listeners so they cannot change the answer to that one.
    ------------------------------------------------------------------ */

    // Unlike once(), this fires every time. That is the whole point of
    // BYP_Engagement — eight actions should read as eight, not as one.
    var engCount = 0;
    function every(name, data) {
      waiting.push([name, data || {}]);
      flush();
    }

    // One human action can reach the page as two events. Clicking a label
    // that wraps a radio dispatches the click AND the change it causes, so
    // counting both would report twice the engagement that happened.
    //
    // Both arrive inside the same task; two real clicks never do, however
    // fast the finger. So the guard is "same cause, still in this task",
    // cleared on a timeout rather than a microtask because microtasks drain
    // between the two dispatches and would clear it too early. No duration
    // is guessed at, so rapid genuine taps still count one each.
    var inTask = false;
    var taskCause = '';
    function twice(cause) {
      if (inTask && taskCause === cause) return true;
      inTask = true;
      taskCause = cause;
      setTimeout(function () { inTask = false; taskCause = ''; }, 0);
      return false;
    }

    // Shopping, as opposed to reading about it. Opening the FAQ or the
    // reviews is interest; choosing a size is intent, and those are worth
    // telling apart. Switching category is browsing, so it is not here.
    var CONFIGURE = { option: 1, quantity: 1, add: 1 };

    function act(cause) {
      if (!cause || twice(cause)) return;
      engCount += 1;
      every('BYP_Engagement', { trigger: cause });
      if (CONFIGURE[cause]) once('BYP_ProductConfigure', { trigger: cause });
    }

    document.addEventListener('click', function (event) {
      var el = event.target;
      if (!el || !el.closest) return;
      // Checkout has its own event and is not on the list of actions to
      // count as engagement, so it is passed over here deliberately.
      if (el.closest('[data-tb-bundle-add], [data-tb-nextcheckout]')) return;
      act(why(el));
    }, true);

    document.addEventListener('change', function (event) {
      var el = event.target;
      if (!el || !el.closest) return;
      if (el.closest('[data-tb-option]')) { act('option'); return; }
      if (el.closest('[data-tb-repeat-mode], [data-tb-line-every], [data-tb-focus-every], .tb-focus__subopt')) {
        act('subscription');
      }
    }, true);

    document.addEventListener('toggle', function (event) {
      var el = event.target;
      if (!el || !el.open || !el.matches) return;
      if (el.matches('.tb-faq__row')) { act('faq'); return; }
      if (!el.matches('.tb-fold')) return;
      var head = el.querySelector('summary');
      var said = head ? head.textContent.toLowerCase() : '';
      act(said.indexOf('review') !== -1 ? 'reviews' : 'description');
    }, true);

    // ---- how far down the page they got ------------------------------
    //
    // Depth is measured to the BOTTOM of what they can see, so 100% means
    // the end of the page has been reached rather than scrolled past.
    var MARKS = [[25, 'BYP_Scroll25'], [50, 'BYP_Scroll50'], [75, 'BYP_Scroll75']];

    function howFar() {
      var doc = document.documentElement || {};
      var body = document.body || {};
      var tall = Math.max(
        doc.scrollHeight || 0, body.scrollHeight || 0,
        doc.offsetHeight || 0, body.offsetHeight || 0
      );
      if (tall <= 0) return 0;
      var down = window.pageYOffset || doc.scrollTop || 0;
      var tube = window.innerHeight || doc.clientHeight || 0;
      return Math.min(100, Math.round(((down + tube) / tall) * 100));
    }

    // Only the marks still outstanding. Asked once, here, rather than on
    // every frame of every scroll: the second page of a visit has these
    // already in storage, and reading storage sixty times a second to be
    // told the same thing is exactly how a page starts to feel heavy.
    var left = [];
    for (var m = 0; m < MARKS.length; m += 1) {
      if (!already(MARKS[m][1])) left.push(MARKS[m]);
    }

    function depthCheck() {
      // Nothing may fire from the page merely existing. On a page too short
      // to scroll, the position never leaves zero and none of these count —
      // which is right: nobody read their way down anything.
      var down = window.pageYOffset || (document.documentElement || {}).scrollTop || 0;
      if (down <= 0) return;
      var got = howFar();
      // In order, so a flick that lands at 80% records passing 25 and 50 too.
      while (left.length && got >= left[0][0]) {
        var mark = left.shift();
        once(mark[1], { depth: mark[0] });
      }
      if (!left.length) stopWatching();
    }

    // Passive, and at most one measurement a frame: this must not be the
    // reason a scroll feels heavy. Once all three are recorded there is
    // nothing left to learn, so it stops listening altogether.
    var pending = false;
    var watching = false;
    function onScroll() {
      if (pending) return;
      pending = true;
      var run = function () { pending = false; depthCheck(); };
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run);
      else setTimeout(run, 100);
    }
    function stopWatching() {
      if (!watching) return;
      watching = false;
      try { window.removeEventListener('scroll', onScroll, { passive: true }); } catch (err) {}
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('scroll', onScroll);
    }
    if (left.length) {
      watching = true;
      try {
        window.addEventListener('scroll', onScroll, { passive: true });
      } catch (err) {
        window.addEventListener('scroll', onScroll, true);
      }
    }

    // For checking it by hand on the live shop: tbPixelState()
    window.tbPixelState = function () {
      var depths = {};
      for (var i = 0; i < MARKS.length; i += 1) {
        depths[MARKS[i][1]] = Boolean(fired[MARKS[i][1]] || already(MARKS[i][1]));
      }
      return {
        pixelReady: typeof window.fbq === 'function',
        interactionSent: Boolean(fired.BYP_Interaction || already('BYP_Interaction')),
        checkoutClickSent: Boolean(fired.BYP_CheckoutClick || already('BYP_CheckoutClick')),
        configureSent: Boolean(fired.BYP_ProductConfigure || already('BYP_ProductConfigure')),
        engagementCount: engCount,
        scrollDepth: howFar(),
        scrollWatching: watching,
        scrollSent: depths,
        queued: waiting.length
      };
    };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', wire);
  else wire();
  window.addEventListener('load', wire);
}());

/* ---------------------------------------------------------------------------
   Event check — an independent readout of what actually reached Meta.

   Meta's Test Events panel is their display of their own data. When it
   stops showing something, nothing in this file can put it back. So this
   does not try. It reads the record of network requests the browser keeps
   anyway, and reports every pixel beacon that left the page, with its
   parameters, as it happens.

   It never touches fbq — does not wrap it, does not queue through it,
   does not call it. That matters twice over: it cannot break the shop's
   own tracking, and because it watches the wire rather than the pixel,
   what it shows is what actually left the machine, whoever sent it. The
   shop's five standard events and the two BYP ones are read the same way,
   by the same means, with no special handling for either.

   Off unless asked for. ?tbcheck=1 turns it on and it stays on for the
   visit; ?tbcheck=0 turns it off. Nobody shopping ever sees it.
--------------------------------------------------------------------------- */
(function () {
  var FLAG = 'tbcheck';

  function asked() {
    var q = '';
    try { q = window.location.search || ''; } catch (err) { return false; }
    if (q.indexOf(FLAG + '=1') !== -1) {
      // Keep it on through checkout and back. Session cookie, so it dies
      // with the browser and never follows anyone home.
      try { document.cookie = FLAG + '=1;path=/;SameSite=Lax'; } catch (err) {}
      return true;
    }
    if (q.indexOf(FLAG + '=0') !== -1) {
      try { document.cookie = FLAG + '=;path=/;Max-Age=0;SameSite=Lax'; } catch (err) {}
      return false;
    }
    try { return (document.cookie || '').indexOf(FLAG + '=1') !== -1; } catch (err) { return false; }
  }

  if (!asked()) return;
  if (window.tbCheckOn) return;
  window.tbCheckOn = true;

  var seen = [];
  var pixels = {};
  var started = Date.now();

  function dec(s) {
    try { return decodeURIComponent(String(s).replace(/\+/g, ' ')); } catch (err) { return String(s); }
  }

  // A pixel beacon is .../tr?id=…&ev=…&cd[…]=… — everything worth knowing
  // about the event is in the query string.
  function unpack(url) {
    var at = url.indexOf('?');
    if (at === -1) return null;
    var out = { ev: '', id: '', eid: '', cd: {} };
    var bits = url.slice(at + 1).split('&');
    for (var i = 0; i < bits.length; i += 1) {
      var eq = bits[i].indexOf('=');
      if (eq === -1) continue;
      var key = dec(bits[i].slice(0, eq));
      var val = dec(bits[i].slice(eq + 1));
      if (key === 'ev') out.ev = val;
      else if (key === 'id') out.id = val;
      else if (key === 'eid') out.eid = val;
      else if (key.slice(0, 3) === 'cd[' && key.slice(-1) === ']') out.cd[key.slice(3, -1)] = val;
    }
    return out.ev ? out : null;
  }

  var STANDARD = {
    PageView: 1, ViewContent: 1, AddToCart: 1, InitiateCheckout: 1, Purchase: 1,
    AddPaymentInfo: 1, Lead: 1, CompleteRegistration: 1, Search: 1, AddToWishlist: 1,
    Subscribe: 1, Contact: 1, CustomizeProduct: 1, FindLocation: 1, Schedule: 1,
    StartTrial: 1, SubmitApplication: 1, Donate: 1
  };

  // The parameters worth showing. Everything else is still kept on the
  // record — this is only what fits on a line.
  var SHOW = ['value', 'currency', 'content_ids', 'content_name', 'content_type', 'contents', 'num_items', 'trigger', 'order_id'];

  function note(url, when) {
    var got = unpack(url);
    if (!got) return;
    if (got.id) pixels[got.id] = (pixels[got.id] || 0) + 1;
    seen.push({
      ev: got.ev,
      id: got.id,
      eid: got.eid,
      cd: got.cd,
      kind: STANDARD[got.ev] ? 'standard' : 'custom',
      from: got.eid && got.eid.slice(0, 3) === 'sh-' ? 'shop' : 'browser',
      at: Math.max(0, Math.round(when))
    });
    paint();
  }

  // ---- the panel ---------------------------------------------------------
  var box = null;
  var list = null;
  var cap = null;
  var shut = false;

  function dress(el, css) { el.setAttribute('style', css); return el; }

  function build() {
    if (box) return;

    // On a phone the bottom right corner is the Build Your Pack bar — the
    // checkout button being tested. Sitting on top of it would make the
    // readout the reason the test fails, so on a narrow screen this goes to
    // the top instead and starts rolled up, out of the way of the header.
    var tight = false;
    try { tight = window.innerWidth < 700; } catch (err) {}
    if (tight) shut = true;

    box = dress(document.createElement('div'),
      'position:fixed;z-index:2147483000;right:12px;' +
      (tight ? 'top:76px;' : 'bottom:12px;') +
      'width:330px;max-width:calc(100vw - 24px);' +
      'max-height:' + (tight ? '50vh' : '70vh') + ';display:flex;flex-direction:column;' +
      'background:#14110E;color:#F5F1EA;' +
      'font:12px/1.45 ui-monospace,SFMono-Regular,Menlo,monospace;border-radius:10px;' +
      'box-shadow:0 10px 40px rgba(0,0,0,.45);overflow:hidden');

    var top = dress(document.createElement('div'),
      'display:flex;align-items:center;gap:8px;padding:10px 12px;background:#221D18;cursor:pointer;flex:0 0 auto');
    cap = dress(document.createElement('div'), 'flex:1 1 auto;font-weight:600;letter-spacing:.02em');
    cap.textContent = 'Event check';

    var copy = dress(document.createElement('button'),
      'all:unset;cursor:pointer;padding:3px 8px;border-radius:5px;background:#3C332A;color:#F5F1EA;font:inherit');
    copy.textContent = 'copy';
    copy.addEventListener('click', function (e) { e.stopPropagation(); lift(); });

    var hide = dress(document.createElement('button'),
      'all:unset;cursor:pointer;padding:3px 8px;border-radius:5px;background:#3C332A;color:#F5F1EA;font:inherit');
    hide.textContent = shut ? '+' : '–';

    top.appendChild(cap); top.appendChild(copy); top.appendChild(hide);
    top.addEventListener('click', function () {
      shut = !shut;
      hide.textContent = shut ? '+' : '–';
      list.style.display = shut ? 'none' : 'block';
    });

    list = dress(document.createElement('div'),
      'flex:1 1 auto;overflow:auto;padding:4px 0 8px' + (shut ? ';display:none' : ''));
    box.appendChild(top); box.appendChild(list);
    (document.body || document.documentElement).appendChild(box);
  }

  // fbq keeps its own tally. Comparing it against the beacons counted here
  // is the point: if the pixel says it sent more than left the wire, this
  // readout is incomplete and says so rather than quietly under-reporting.
  function tally() {
    try {
      if (typeof window.fbq !== 'function' || !window.fbq.getState) return -1;
      var state = window.fbq.getState();
      var all = (state && state.pixels) || [];
      var n = 0;
      for (var i = 0; i < all.length; i += 1) n += all[i].eventCount || 0;
      return n;
    } catch (err) { return -1; }
  }

  function row(item) {
    var line = dress(document.createElement('div'),
      'padding:6px 12px;border-top:1px solid #2A2420');
    var head = dress(document.createElement('div'), 'display:flex;gap:6px;align-items:baseline');

    var name = dress(document.createElement('span'),
      'font-weight:700;color:' + (item.kind === 'standard' ? '#9BD9A4' : '#F0C98A'));
    name.textContent = item.ev;

    var tag = dress(document.createElement('span'), 'color:#8A8178;font-size:11px');
    tag.textContent = (item.kind === 'standard' ? 'standard' : 'custom') +
      ' · ' + item.from + ' · ' + (item.at / 1000).toFixed(1) + 's';

    head.appendChild(name); head.appendChild(tag);
    line.appendChild(head);

    var bits = [];
    for (var i = 0; i < SHOW.length; i += 1) {
      var k = SHOW[i];
      if (item.cd[k] === undefined || item.cd[k] === '') continue;
      var v = item.cd[k];
      if (v.length > 42) v = v.slice(0, 40) + '…';
      bits.push(k + '=' + v);
    }
    if (bits.length) {
      var p = dress(document.createElement('div'), 'color:#C9C1B6;word-break:break-word;margin-top:2px');
      p.textContent = bits.join('  ');
      line.appendChild(p);
    }

    var idline = dress(document.createElement('div'), 'color:#8A8178;font-size:11px;margin-top:2px');
    idline.textContent = 'pixel ' + (item.id || '?') + (item.eid ? '  ·  event id ' + item.eid : '  ·  no event id');
    line.appendChild(idline);
    return line;
  }

  var drawn = 0;

  function paint() {
    build();
    for (var i = drawn; i < seen.length; i += 1) list.appendChild(row(seen[i]));
    if (seen.length > drawn && !shut) list.scrollTop = list.scrollHeight;
    drawn = seen.length;

    var ids = Object.keys(pixels);
    var said = tally();
    var head = seen.length + ' event' + (seen.length === 1 ? '' : 's');
    if (ids.length > 1) head += ' · ' + ids.length + ' PIXELS';
    if (said > seen.length) head += ' · pixel says ' + said;
    cap.textContent = 'Event check — ' + head;
  }

  // ---- plain text, for pasting somewhere useful --------------------------
  function script() {
    var out = [];
    out.push('EVENT CHECK — ' + new Date().toISOString());
    try { out.push('page: ' + window.location.href); } catch (err) {}
    var ids = Object.keys(pixels);
    out.push('pixels seen: ' + (ids.length ? ids.join(', ') : 'none'));
    var said = tally();
    out.push('beacons captured here: ' + seen.length + (said >= 0 ? '   fbq eventCount: ' + said : '   fbq eventCount: unavailable'));
    if (said > seen.length) out.push('NOTE: pixel reports more events than were captured — some used a transport this readout cannot see.');
    out.push('');
    for (var i = 0; i < seen.length; i += 1) {
      var it = seen[i];
      out.push((i + 1) + '. ' + it.ev + '  [' + it.kind + ', ' + it.from + ', ' + (it.at / 1000).toFixed(1) + 's]');
      out.push('   pixel ' + (it.id || '?') + '   event id ' + (it.eid || '(none)'));
      var keys = Object.keys(it.cd);
      for (var k = 0; k < keys.length; k += 1) out.push('   ' + keys[k] + ' = ' + it.cd[keys[k]]);
    }
    out.push('');
    out.push('Note: this reads browser beacons only. Events sent from the shop\'s');
    out.push('servers (Conversions API) do not pass through the browser and are not');
    out.push('listed. Checkout and thank-you pages are not part of the shop theme, so');
    out.push('Purchase is not captured here either.');
    return out.join('\n');
  }

  function lift() {
    var text = script();
    var pad = dress(document.createElement('textarea'),
      'position:fixed;left:-9999px;top:0;opacity:0');
    pad.value = text;
    (document.body || document.documentElement).appendChild(pad);
    pad.select();
    var won = false;
    try { won = document.execCommand('copy'); } catch (err) {}
    if (pad.parentNode) pad.parentNode.removeChild(pad);
    if (!won && window.navigator && window.navigator.clipboard) {
      try { window.navigator.clipboard.writeText(text); won = true; } catch (err) {}
    }
    cap.textContent = won ? 'Event check — copied' : 'Event check — see console';
    if (!won) { try { console.log(text); } catch (err) {} }
    setTimeout(paint, 1400);
  }

  // ---- watching the wire -------------------------------------------------
  function mine(url) {
    return typeof url === 'string' &&
      url.indexOf('/tr') !== -1 &&
      /facebook\.com|facebook\.net/.test(url);
  }

  function watch() {
    if (typeof window.PerformanceObserver !== 'function') return false;
    try {
      var eye = new window.PerformanceObserver(function (run) {
        var rows = run.getEntries();
        for (var i = 0; i < rows.length; i += 1) {
          if (mine(rows[i].name)) note(rows[i].name, rows[i].startTime);
        }
      });
      // buffered picks up the beacons that went out before this ran —
      // PageView fires early, and missing it would be the whole point lost.
      eye.observe({ type: 'resource', buffered: true });
      return true;
    } catch (err) { return false; }
  }

  if (!watch()) {
    // Old browser. Sweep the same log by hand instead.
    var done = 0;
    setInterval(function () {
      var rows = [];
      try { rows = window.performance.getEntriesByType('resource'); } catch (err) { return; }
      for (var i = done; i < rows.length; i += 1) {
        if (mine(rows[i].name)) note(rows[i].name, rows[i].startTime);
      }
      done = rows.length;
    }, 500);
  }

  paint();

  window.tbCheck = function () {
    return { events: seen.slice(), pixels: Object.keys(pixels), fbqCount: tally(), text: script() };
  };
}());
